import React, { useState, useEffect, useCallback, useRef } from 'react';
import './App.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LOGO_URL = 'https://customer-assets-agu9un31.emergentagent.net/job_offroad-platform/artifacts/fa1ox7xq_cropped-cropped-logo_size-03-2-512x490-1.png';
const APP_ICON_4X4 = '/apps/assets/tectur-icon-rugged.png';
const APP_ICON_LAUNCHER = '/apps/assets/tectur-logo.png';
const YT_VIDEOS_FALLBACK = [
  { id: '_DdRd3lgGNg', title: 'Rota da Aldeia Presépio de Piódão' },
  { id: 'bPDm8d0sUSs', title: 'Rotas dos Castelos e Muralhas do Mondego' },
];

const IMG = {
  heroJipe: 'https://static.prod-images.emergentagent.com/jobs/6282196d-35bc-4d45-89d3-ba709a617567/images/964c9089afde038a99e86ef44d58dbe8a475bac8008c3005ff160d2f18fdd411.jpeg',
  heroMobile: 'https://static.prod-images.emergentagent.com/jobs/6282196d-35bc-4d45-89d3-ba709a617567/images/4f9d1a6a0bb00dc2ab90dea6470661e12f7d979e21cf25f354bd9dc1b148084d.jpeg',
  convoy: 'https://static.prod-images.emergentagent.com/jobs/6282196d-35bc-4d45-89d3-ba709a617567/images/e777a79c25bbafb34f995acc1ea26e01bc46fe42c87b218fa51ea14dccb5df32.jpeg',
  miradouro: 'https://static.prod-images.emergentagent.com/jobs/6282196d-35bc-4d45-89d3-ba709a617567/images/e8f54332d8dd2769862fb3634f7ddcfc1ddf796ae4e3acbfb515216387ac1f29.jpeg',
  tablet: 'https://static.prod-images.emergentagent.com/jobs/6282196d-35bc-4d45-89d3-ba709a617567/images/0a243a3456a4ea95b65d2614354ff0665aafc187abc7e072c633db20db4188e9.jpeg',
  gpsMount: 'https://static.prod-images.emergentagent.com/jobs/6282196d-35bc-4d45-89d3-ba709a617567/images/cb9fdc2bcd2aa0a0ebbd3a652eeb87e6a3a3b8a2309ab60f0ca206bb5216fff3.jpeg',
  teamBuilding: 'https://static.prod-images.emergentagent.com/jobs/6282196d-35bc-4d45-89d3-ba709a617567/images/ad3383e28b0d87c6b17a5a27a6ed84accd50c10dc0c34e2032fa35a4bb47da35.jpeg',
  aldeia: 'https://static.prod-images.emergentagent.com/jobs/6282196d-35bc-4d45-89d3-ba709a617567/images/ad9d386863d6e4acd43e206ca6a87fef6558f6a5d99a4b3d5e88620f7a41838d.jpeg',
  aerial: 'https://static.prod-images.emergentagent.com/jobs/6282196d-35bc-4d45-89d3-ba709a617567/images/6756da1aeb57a9c4888c8ceac8fafbf82a9fef7d908021149eb0aee490ddc5a1.jpeg',
};

// Converte um "detail" do FastAPI (string OU array de erros Pydantic) numa mensagem legível.
function _errMsg(detail, status) {
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map(e => {
      const loc = Array.isArray(e?.loc) ? e.loc.filter(x => x !== 'body').join('.') : '';
      const msg = e?.msg || 'erro';
      return loc ? `${loc}: ${msg}` : msg;
    }).join(' · ');
  }
  if (detail && typeof detail === 'object') return JSON.stringify(detail);
  return `Erro ${status}`;
}

// ---- fetch helpers ----
async function apiGet(path) {
  const res = await fetch(`${API}${path}`, { credentials: 'include' });
  if (!res.ok) throw new Error(_errMsg((await res.json().catch(() => ({}))).detail, res.status));
  return res.json();
}
async function apiPost(path, body) {
  const res = await fetch(`${API}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(_errMsg((await res.json().catch(() => ({}))).detail, res.status));
  return res.json();
}
async function apiPut(path, body) {
  const res = await fetch(`${API}${path}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(_errMsg((await res.json().catch(() => ({}))).detail, res.status));
  return res.json();
}
async function apiDelete(path) {
  const res = await fetch(`${API}${path}`, { method: 'DELETE', credentials: 'include' });
  if (!res.ok) throw new Error(_errMsg((await res.json().catch(() => ({}))).detail, res.status));
  return res.json();
}

// Resolve a URL de imagem — se for relativa (upload local), prefixa o backend
function resolveImg(u) {
  if (!u) return u;
  if (u.startsWith('http://') || u.startsWith('https://') || u.startsWith('data:')) return u;
  return `${process.env.REACT_APP_BACKEND_URL}${u}`;
}

// Map backend trip → shape esperado pelos cards existentes (não altera CSS)
function tripToCard(t) {
  const fallbackImgs = [IMG.miradouro, IMG.aerial, IMG.convoy];
  return {
    id: t.id,
    titulo: t.title,
    type: t.type || 'guided',
    duracao: t.duration || '',
    data: t.date_label || '',
    vagas: t.vagas_disponiveis,
    total: t.vagas_total,
    dificuldade: t.difficulty || 'Médio',
    preco: t.price_from || 0,
    img: resolveImg(t.image) || fallbackImgs[0],
    detail_image: resolveImg(t.detail_image) || '',
    desc: t.short_desc || t.long_desc || '',
    region: t.region || '',
    distance_km: t.distance_km || null,
    long_desc: t.long_desc || '',
    highlights: t.highlights || [],
    included: t.included || [],
    not_included: t.not_included || [],
    vehicle_requirements: t.vehicle_requirements || [],
    gallery: (t.gallery || []).map(resolveImg),
    videos: t.videos || [],
    _raw: t,
  };
}

const NAV = [
  { id: 'home', label: 'Início' },
  { id: 'passeios', label: 'Passeios' },
  { id: 'passeios-realizados', label: 'Passeios Realizados' },
  { id: 'apps', label: 'Apps' },
  { id: 'equipamento', label: 'Equipamento' },
  { id: 'teambuilding', label: 'Team Building' },
  { id: 'municipios', label: 'Municípios' },
  { id: 'contactos', label: 'Contactos' },
];

function Header({ current, onNav, portalSession }) {
  const [open, setOpen] = useState(false);
  return (
    <header className="header">
      <div className="rnaat-strip">Turismo Off-Road licenciado · RNAAT nº <strong>143/2022</strong></div>
      <div className="header-inner">
        <div className="logo" data-testid="header-logo" onClick={() => onNav('home')}><img src={LOGO_URL} alt="Tectur" /></div>
        <nav className={`nav ${open ? 'nav-open' : ''}`}>
          {NAV.map(n => (
            <a key={n.id} data-testid={`nav-${n.id}`} className={current === n.id ? 'active' : ''} onClick={() => { onNav(n.id); setOpen(false); }}>{n.label}</a>
          ))}
          <a data-testid="nav-entrar" className={current === 'entrar' || current === 'minha-area' ? 'active' : ''} onClick={() => { onNav(portalSession ? 'minha-area' : 'entrar'); setOpen(false); }} style={{ color: '#ff6b1a' }}>
            {portalSession ? '👤 Minha área' : 'Entrar'}
          </a>
          <a className="nav-cta" data-testid="nav-login" onClick={() => { onNav('login'); setOpen(false); }}>Gestão</a>
        </nav>
        <button className="hamburger" onClick={() => setOpen(!open)}>☰</button>
      </div>
    </header>
  );
}

function Footer({ onNav }) {
  const [cfg, setCfg] = useState(null);
  useEffect(() => {
    apiGet('/config/public').then(setCfg).catch(() => setCfg(null));
  }, []);
  const nif = cfg?.nif && cfg.nif !== '(por preencher)' ? cfg.nif : null;
  const companyName = cfg?.company_name || 'Tectur';
  return (
    <footer className="footer">
      <div className="container">
        <div className="footer-grid">
          <div>
            <div className="logo"><img src={LOGO_URL} alt="Tectur" /></div>
            <p className="muted small">Equipa a tua aventura off-road.</p>
            <p className="muted small" style={{ marginTop: 12, lineHeight: 1.6 }}>
              Turismo Off-Road licenciado pelo <strong>Turismo de Portugal</strong><br />
              RNAAT nº <strong>143/2022</strong>{nif ? <><br />NIF <strong>{nif}</strong></> : null}
            </p>
            <div className="footer-cert-strip" data-testid="footer-certs">
              <div className="footer-cert" title="Certificação Turismo de Natureza — Turismo de Portugal">
                <img src={`${API}/uploads/turismo-natureza.png`} alt="Turismo de Natureza" />
                <span>Turismo de<br/>Natureza</span>
              </div>
              <a
                href="https://rnt.turismodeportugal.pt/RNT/RNAAT.aspx?nr=143/2022"
                target="_blank"
                rel="noreferrer"
                className="footer-cert footer-cert-tp"
                title="Licenciado pelo Turismo de Portugal, I.P. — RNAAT nº 143/2022"
                data-testid="footer-cert-tp"
              >
                <div className="tp-chip">
                  <img src={`${API}/uploads/turismo-de-portugal.png`} alt="Turismo de Portugal" />
                </div>
                <span>Licenciado<br/>RNAAT 143/2022</span>
              </a>
              <a
                href="https://turismodocentro.pt/"
                target="_blank"
                rel="noreferrer"
                className="footer-cert footer-cert-tp"
                title="Turismo Centro de Portugal — Entidade Regional"
                data-testid="footer-cert-centro"
              >
                <div className="tp-chip">
                  <img src={`${API}/uploads/turismo-centro-de-portugal.png`} alt="Turismo Centro de Portugal" style={{ maxWidth: 150 }} />
                </div>
                <span>Região<br/>Centro Portugal</span>
              </a>
            </div>
            <a onClick={() => onNav && onNav('sobre')} className="link small" style={{ display: 'inline-block', marginTop: 14, cursor: 'pointer' }} data-testid="footer-sobre">
              Sobre a Tectur & certificações →
            </a>
          </div>
          <div>
            <h4>Explora</h4>
            <a onClick={() => onNav && onNav('passeios')} style={{ cursor: 'pointer' }} role="button" tabIndex="0">Passeios</a>
            <a onClick={() => onNav && onNav('passeios-realizados')} style={{ cursor: 'pointer' }} role="button" tabIndex="0" data-testid="footer-passeios-realizados">Passeios Realizados</a>
            <a onClick={() => onNav && onNav('equipamento')} style={{ cursor: 'pointer' }} role="button" tabIndex="0">Equipamento</a>
            <a href="https://loja.tectur.pt" target="_blank" rel="noreferrer">Loja</a>
          </div>
          <div>
            <h4>B2B</h4>
            <a onClick={() => onNav && onNav('teambuilding')} style={{ cursor: 'pointer' }}>Team Building</a>
            <a onClick={() => onNav && onNav('municipios')} style={{ cursor: 'pointer' }}>Municípios</a>
          </div>
          <div>
            <h4>Contactos</h4>
            <a href="mailto:tectur@tectur.pt">tectur@tectur.pt</a>
            <a href="https://loja.tectur.pt" target="_blank" rel="noreferrer">loja.tectur.pt</a>
          </div>
          <div>
            <h4>Legal</h4>
            <a onClick={() => onNav && onNav('privacidade')} style={{ cursor: 'pointer' }} data-testid="footer-priv">Política de Privacidade</a>
            <a onClick={() => onNav && onNav('cookies')} style={{ cursor: 'pointer' }} data-testid="footer-cookies">Política de Cookies</a>
            <a onClick={() => onNav && onNav('termos')} style={{ cursor: 'pointer' }} data-testid="footer-terms">Termos e Condições</a>
            <a href="https://www.livroreclamacoes.pt/Inicio/" target="_blank" rel="noreferrer" data-testid="footer-livro">
              <img src="https://www.livroreclamacoes.pt/Content/Images/logo_lr.png" alt="Livro de Reclamações" style={{ height: 42, marginTop: 6 }} onError={(e) => { e.target.style.display = 'none'; e.target.insertAdjacentText('afterend', 'Livro de Reclamações →'); }} />
            </a>
          </div>
        </div>
        <div className="footer-bottom">© 2026 {companyName}. Todos os direitos reservados. · RNAAT nº 143/2022{nif ? ` · NIF ${nif}` : ''}</div>
      </div>
    </footer>
  );
}

// ---- Newsletter popup (mostra 1 vez por visitante) ----
function NewsletterPopup() {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('');
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const dismissed = localStorage.getItem('tectur_news_popup');
    if (dismissed) return;
    const t = setTimeout(() => setOpen(true), 15000); // 15s após entrar no site
    return () => clearTimeout(t);
  }, []);
  const close = () => {
    setOpen(false);
    try { localStorage.setItem('tectur_news_popup', String(Date.now())); } catch (e) { /* ignore */ }
  };
  const submit = async (e) => {
    e.preventDefault(); setStatus('');
    try {
      const r = await apiPost('/newsletter/subscribe', { email, source: 'popup' });
      if (r && r.pending_confirmation) {
        setStatus('✓ Enviámos um email para ' + email + '. Clica no link de confirmação para completar a subscrição.');
      } else {
        setStatus('Obrigado! Já fazes parte da comunidade Tectur.');
      }
      setEmail('');
      setTimeout(close, 4500);
    } catch (err) { setStatus(err.message || 'Erro'); }
  };
  if (!open) return null;
  return (
    <div className="popup-overlay" data-testid="newsletter-popup" onClick={close}>
      <div className="popup-card" onClick={e => e.stopPropagation()}>
        <button className="popup-close" onClick={close} data-testid="popup-close" aria-label="Fechar">×</button>
        <div className="popup-badge">NEWSLETTER TECTUR</div>
        <h3 className="popup-title">NÃO PERCAS NENHUMA ROTA</h3>
        <p className="popup-lead">
          Subscreve e recebe primeiro as datas das próximas rotas, vagas remanescentes e conteúdos off-road exclusivos.
        </p>
        <form onSubmit={submit} className="popup-form" data-testid="popup-form">
          <input type="email" required value={email} onChange={e => setEmail(e.target.value)} placeholder="o.teu@email.com" data-testid="popup-email" />
          <button type="submit" className="btn" data-testid="popup-submit">Subscrever</button>
        </form>
        <p className="popup-guarantee">
          <strong>Sem spam.</strong> Só te enviamos novidades das rotas Tectur.<br />
          Cancela quando quiseres — respeitamos o RGPD.
        </p>
        {status && <p className="small muted" style={{ marginTop: 8 }} data-testid="popup-status">{status}</p>}
      </div>
    </div>
  );
}

// ---- Cookie consent banner ----
function CookieBanner({ onNav }) {
  const [show, setShow] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!localStorage.getItem('tectur_cookies_v1')) setShow(true);
  }, []);
  const accept = (mode) => {
    try { localStorage.setItem('tectur_cookies_v1', mode); } catch (e) { /* ignore */ }
    setShow(false);
  };
  if (!show) return null;
  return (
    <div className="cookie-banner" data-testid="cookie-banner">
      <div className="cookie-inner">
        <p className="cookie-text">
          Este site usa <strong>cookies</strong> técnicos essenciais para funcionamento (sessão, preferências) e cookies de análise anónimos para melhorar a experiência. Não usamos cookies de publicidade nem partilhamos dados com terceiros. Consulta a nossa <a onClick={() => { accept('accepted'); onNav && onNav('cookies'); }} style={{ color: '#ff6b1a', cursor: 'pointer', textDecoration: 'underline' }}>Política de Cookies</a>.
        </p>
        <div className="cookie-actions">
          <button className="btn btn-outline" onClick={() => accept('essential')} data-testid="cookie-essential">Só essenciais</button>
          <button className="btn" onClick={() => accept('accepted')} data-testid="cookie-accept">Aceitar todos</button>
        </div>
      </div>
    </div>
  );
}

// ---- Newsletter form (real API) ----
function NewsletterForm() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('');
  const submit = async (e) => {
    e.preventDefault();
    setStatus('');
    try {
      const r = await apiPost('/newsletter/subscribe', { email });
      if (r && r.pending_confirmation) {
        setStatus('✓ Enviámos um email para confirmares a subscrição. Verifica a caixa de entrada.');
      } else {
        setStatus('Subscrição registada. Obrigado!');
      }
      setEmail('');
    } catch (err) {
      setStatus(err.message || 'Erro ao subscrever.');
    }
  };
  return (
    <form className="newsletter-form" onSubmit={submit} data-testid="newsletter-form">
      <input type="email" placeholder="o.teu@email.com" required value={email} onChange={e => setEmail(e.target.value)} data-testid="newsletter-email" />
      <button className="btn" type="submit" data-testid="newsletter-submit">Subscrever</button>
      {status && <p className="small muted" style={{ marginTop: 8 }} data-testid="newsletter-status">{status}</p>}
    </form>
  );
}

function PasseiosCarousel({ passeios, onNav, testid = 'passeios-grid', slots = 3, intervalMs = 12000 }) {
  const [page, setPage] = useState(0);
  const total = passeios.length;
  const numPages = Math.max(1, Math.ceil(total / slots));

  useEffect(() => {
    if (numPages <= 1) return;
    const t = setInterval(() => setPage(p => (p + 1) % numPages), intervalMs);
    return () => clearInterval(t);
  }, [numPages, intervalMs]);

  const start = (page * slots) % Math.max(1, total);
  const showing = [];
  for (let i = 0; i < Math.min(slots, total); i++) {
    showing.push(passeios[(start + i) % total]);
  }

  return (
    <>
      <div className="grid grid-3" data-testid={testid}>
        {showing.map(p => (
          <div key={`${page}-${p.id}`} className="card card-anim" data-testid={`${testid}-card-${p.id}`} onClick={() => onNav('inscricao', p)}>
            <div className="card-img" style={{ backgroundImage: `url(${p.img})` }}>
              <span className={`badge badge-${(p.dificuldade || 'medio').toLowerCase()}`}>{p.dificuldade}</span>
              {p.type === 'navigation' && <span className="badge badge-nav">Navigation</span>}
            </div>
            <div className="card-body">
              <h3>{p.titulo}</h3>
              <div className="meta">
                <span>📅 {p.data}</span>
                <span>👥 {p.vagas}/{p.total} vagas</span>
              </div>
              <p className="muted">{p.desc}</p>
              <div className="card-footer">
                <span className="price">{p.preco}€<small>/pessoa</small></span>
                {p._raw?.inscriptions_closed
                  ? <span className="badge" style={{ position: 'static', background: '#333', color: '#fff' }}>🔒 Encerradas</span>
                  : <button className="btn btn-sm">Inscrever</button>}
              </div>
            </div>
          </div>
        ))}
      </div>
      {numPages > 1 && (
        <div className="videos-dots" data-testid={`${testid}-dots`}>
          {Array.from({ length: numPages }).map((_, i) => (
            <button key={i} className={`videos-dot ${i === page ? 'active' : ''}`} onClick={() => setPage(i)} aria-label={`Ir para grupo ${i + 1}`} />
          ))}
        </div>
      )}
    </>
  );
}

function Home({ onNav, passeios, loading, videos, realizados = [] }) {
  // Fallback defaults + cache do último valor válido em localStorage (evita mostrar
  // zeros quando /api/stats/public bate rate-limit 429 ou timeout).
  const STATS_FALLBACK = { trips_done: 10, total_km: 1200, since_year: 2022 };
  const [stats, setStats] = useState(() => {
    try {
      const cached = JSON.parse(localStorage.getItem('tectur_hero_stats') || 'null');
      return cached && typeof cached.trips_done === 'number' ? cached : STATS_FALLBACK;
    } catch (_) { return STATS_FALLBACK; }
  });
  useEffect(() => {
    apiGet('/stats/public').then((s) => {
      if (s && typeof s.trips_done === 'number') {
        setStats(s);
        try { localStorage.setItem('tectur_hero_stats', JSON.stringify(s)); } catch (_) {}
      }
    }).catch(() => { /* mantém stats existentes (cache ou fallback) */ });
  }, []);
  return (
    <>
      <section className="hero" style={{ backgroundImage: `linear-gradient(rgba(0,0,0,0.5), rgba(0,0,0,0.85)), url(${IMG.heroJipe})` }}>
        <div className="container hero-inner">
          <span className="pretitle">Rotas · Apps · Equipamento</span>
          <h1>EQUIPA A TUA<br /><span className="accent">AVENTURA OFF-ROAD</span></h1>
          <p className="lead">Passeios TT turísticos, tecnologia própria de navegação e equipamento profissional para expedições exigentes em Portugal.</p>
          <div className="hero-cta">
            <button className="btn" data-testid="hero-ver-passeios" onClick={() => onNav('passeios')}>Ver Passeios</button>
            <button className="btn btn-outline" data-testid="hero-ver-apps" onClick={() => onNav('apps')}>As Nossas Apps</button>
          </div>
          <div className="hero-stats" data-testid="hero-stats">
            <div className="hero-stat"><strong>{stats.trips_done || STATS_FALLBACK.trips_done}</strong><span>Passeios realizados</span></div>
            <div className="hero-stat"><strong>{(stats.total_km || STATS_FALLBACK.total_km).toLocaleString('pt-PT')}</strong><span>km percorridos</span></div>
            <div className="hero-stat"><strong>Desde {stats.since_year || STATS_FALLBACK.since_year}</strong><span>no terreno</span></div>
          </div>
        </div>
      </section>
      <section className="concept">
        <div className="container concept-lead">
          <span className="pretitle">O QUE É A TECTUR</span>
          <h2>Rotas testadas no terreno.<br /><span className="accent">Nunca tracks aleatórios.</span></h2>
          <p>Somos uma empresa portuguesa de turismo off-road que combina três coisas: <strong>passeios TT guiados</strong> por Portugal, <strong>apps de navegação</strong> feitas por quem conduz no terreno, e <strong>equipamento rugged</strong> pronto para o trilho. Cada rota que publicamos foi conduzida e validada pela nossa equipa — sabes exactamente o que vais encontrar antes de arrancar.</p>
        </div>
      </section>
      <section className="section">
        <div className="container">
          <div className="section-header">
            <h2>Próximos Passeios TT</h2>
            <div className="underline"></div>
            <p className="muted">Escolhe o teu próximo passeio guiado pela Tectur.</p>
          </div>
          {loading && <p className="muted">A carregar passeios…</p>}
          {!loading && passeios.length === 0 && <p className="muted">Sem passeios agendados de momento. Subscreve a newsletter para seres o primeiro a saber.</p>}
          {!loading && passeios.length > 0 && (
            <PasseiosCarousel passeios={passeios} onNav={onNav} testid="home-passeios-grid" />
          )}
        </div>
      </section>
      {realizados.length > 0 && (
        <section className="section" data-testid="home-realizados-section">
          <div className="container">
            <div className="section-header">
              <h2>Passeios Realizados</h2>
              <div className="underline"></div>
              <p className="muted">A nossa história off-road — passeios recentes, fotos, testemunhos.</p>
            </div>
            <div className="realizados-grid" data-testid="home-realizados-grid">
              {realizados.map(t => (
                <article
                  key={t.id}
                  className="realizado-card"
                  data-testid={`home-realizado-${t.id}`}
                  onClick={() => onNav('passeio-realizado', { slug: t.slug || t.id, trip: t })}
                >
                  <div className="realizado-image" style={{ backgroundImage: `url(${resolveImg((t.completed_gallery && t.completed_gallery[0]) || t.image || t.detail_image || IMG.serra)})` }}>
                    {t.date_label && <span className="realizado-date">{t.date_label}</span>}
                  </div>
                  <div className="realizado-body">
                    <h3>{t.title}</h3>
                    {t.region && <p className="muted small">{t.region}</p>}
                    <p className="small" style={{ marginTop: 8 }}>{t.short_desc || (t.long_desc ? t.long_desc.slice(0, 120) + '…' : '')}</p>
                    <div className="realizado-meta">
                      {t.vagas_total ? <span>🚙 Lotação: {t.vagas_total} viaturas</span> : null}
                      {t.distance_km ? <span>📏 {t.distance_km} km</span> : null}
                      {t.difficulty ? <span>⚠ {t.difficulty}</span> : null}
                    </div>
                    <button className="btn btn-outline btn-sm" data-testid={`home-realizado-open-${t.id}`}>Ver arquivo →</button>
                  </div>
                </article>
              ))}
            </div>
            <div style={{ textAlign: 'center', marginTop: 24 }}>
              <button className="btn btn-outline" onClick={() => onNav('passeios-realizados')} data-testid="home-realizados-verall">
                Ver todos os passeios realizados →
              </button>
            </div>
          </div>
        </section>
      )}
      <section className="section section-alt">
        <div className="container">
          <div className="section-header">
            <h2>As Nossas Apps</h2>
            <div className="underline"></div>
            <p className="muted">Duas apps Android desenvolvidas em Portugal para condutores off-road.</p>
          </div>
          <div className="grid grid-2">
            <a className="app-card" href="https://tectur.pt/apps/tectur-4x4.html" target="_blank" rel="noreferrer">
              <div className="app-card-media" style={{ backgroundImage: `url(${IMG.convoy})` }}>
                <img className="app-card-icon" src={APP_ICON_4X4} alt="Tectur 4×4" />
              </div>
              <div className="app-card-body">
                <span className="badge">Navegação Off-Road</span>
                <h3>Tectur 4×4 Navigation</h3>
                <p className="muted">Mapas offline, walkie-talkie, tracking, SOS, Navigation Challenge, Android Auto.</p>
                <span className="link">Saber mais →</span>
              </div>
            </a>
            <a className="app-card" href="https://tectur.pt/apps/tectur-car-launcher.html" target="_blank" rel="noreferrer">
              <div className="app-card-media" style={{ backgroundImage: `url(${IMG.tablet})` }}>
                <img className="app-card-icon" src={APP_ICON_LAUNCHER} alt="Tectur Car Launcher" />
              </div>
              <div className="app-card-body">
                <span className="badge">Cockpit Android</span>
                <h3>Tectur Car Launcher</h3>
                <p className="muted">Velocímetro GPS, IPMA, risco de fogo, Spotify, modo noturno — pensado para tablet off-road.</p>
                <span className="link">Saber mais →</span>
              </div>
            </a>
          </div>
        </div>
      </section>
      <section className="section">
        <div className="container">
          <div className="section-header">
            <h2>Tectur em Movimento</h2>
            <div className="underline"></div>
            <p className="muted">Últimos vídeos do <a className="link" href="https://www.youtube.com/@tectur1468" target="_blank" rel="noreferrer">canal Tectur no YouTube</a> — atualizado automaticamente.</p>
          </div>
          <VideosCarousel videos={videos} />
        </div>
      </section>
      <section className="section banner" style={{ backgroundImage: `linear-gradient(rgba(0,0,0,0.7), rgba(0,0,0,0.9)), url(${IMG.teamBuilding})` }}>
        <div className="container banner-inner">
          <span className="pretitle">Empresas</span>
          <h2>Team Building com Propósito</h2>
          <p>Navigation Challenge — caça ao tesouro gamificada com a app Tectur 4×4. Descoberta, colaboração, natureza.</p>
          <button className="btn" data-testid="home-teambuilding-cta" onClick={() => onNav('teambuilding')}>Saber Mais</button>
        </div>
      </section>
      <section className="section banner" style={{ backgroundImage: `linear-gradient(rgba(0,0,0,0.7), rgba(0,0,0,0.9)), url(${IMG.aldeia})` }}>
        <div className="container banner-inner">
          <span className="pretitle">Setor Público</span>
          <h2>Parceria com Municípios</h2>
          <p>Criamos rotas turísticas oficiais para o teu concelho e integramos na app Tectur 4×4. Turismo qualificado.</p>
          <button className="btn" data-testid="home-municipios-cta" onClick={() => onNav('municipios')}>Saber Mais</button>
        </div>
      </section>
      <section className="section">
        <div className="container">
          <div className="section-header">
            <h2>Equipamento Tectur</h2>
            <div className="underline"></div>
            <p className="muted">Tablets rugged Android convertidos para navegação, suportes RAM e acessórios.</p>
          </div>
          <div className="grid grid-2">
            <a className="card shop-card" href="https://loja.tectur.pt/?product_cat=tabnav" target="_blank" rel="noreferrer" data-testid="home-cat-nav">
              <div className="card-img" style={{ backgroundImage: `url(${IMG.tablet})`, height: 280 }}></div>
              <div className="card-body">
                <h3>GPS &amp; Navegação Tectur</h3>
                <p className="muted">Tablets rugged Nav2 · Nav3 Mini · Nav 4 Rugged 4G · Nav5 Mini 2 — preparados pela Tectur para navegação off-road profissional. Combina com a app Tectur 4×4 Navigation.</p>
                <span className="link">Ver toda a gama Nav →</span>
              </div>
            </a>
            <a className="card shop-card" href="https://loja.tectur.pt/?product_cat=ram" target="_blank" rel="noreferrer" data-testid="home-cat-ram">
              <div className="card-img" style={{ backgroundImage: `url(${IMG.gpsMount})`, height: 280 }}></div>
              <div className="card-body">
                <h3>Suportes RAM Mounts</h3>
                <p className="muted">Suportes profissionais RAM — ventosas, braços, encaixes e bases para tablet 7-10″ e telemóvel.</p>
                <span className="link">Ver toda a gama RAM →</span>
              </div>
            </a>
          </div>
        </div>
      </section>
      <section className="section newsletter">
        <div className="container">
          <div className="section-header">
            <h2>Não Percas Nada</h2>
            <div className="underline"></div>
            <p className="muted">Recebe rotas novas e inscrições em primeira mão.</p>
          </div>
          <NewsletterForm />
        </div>
      </section>
    </>
  );
}

function Passeios({ onNav, passeios, loading }) {
  const [filter, setFilter] = useState('all');
  const filtered = filter === 'all' ? passeios : passeios.filter(p => (p.type || 'guided') === filter);
  const countGuided = passeios.filter(p => (p.type || 'guided') === 'guided').length;
  const countNav = passeios.filter(p => p.type === 'navigation').length;
  return (
    <section className="section">
      <div className="container">
        <div className="section-header">
          <h2>Passeios TT Turísticos</h2>
          <div className="underline"></div>
          <p className="muted">Dois tipos de experiência — escolhe o teu.</p>
        </div>

        <div className="admin-tabs passeios-tabs" data-testid="passeios-filter">
          <button className={`admin-tab ${filter === 'all' ? 'active' : ''}`} onClick={() => setFilter('all')} data-testid="filter-all">Todos ({passeios.length})</button>
          <button className={`admin-tab ${filter === 'guided' ? 'active' : ''}`} onClick={() => setFilter('guided')} data-testid="filter-guided">Passeios Guiados ({countGuided})</button>
          <button className={`admin-tab ${filter === 'navigation' ? 'active' : ''}`} onClick={() => setFilter('navigation')} data-testid="filter-navigation">Navigation Challenge ({countNav})</button>
        </div>

        {filter !== 'all' && (
          <div className="filter-hint">
            {filter === 'guided'
              ? <p className="muted small">Passeios guiados pela Tectur, com guia no terreno, briefing técnico e viatura de apoio. Ideal para primeira experiência ou expedições exigentes.</p>
              : <p className="muted small">Rally paper em 4×4. Cada jipe é uma equipa, respondem a perguntas em cada ponto para desbloquear o próximo. App Tectur 4×4 obrigatória.</p>
            }
          </div>
        )}

        {loading && <p className="muted">A carregar passeios…</p>}
        {!loading && filtered.length === 0 && <p className="muted">Sem passeios deste tipo agendados de momento.</p>}
        {!loading && filtered.length > 0 && (
          <PasseiosCarousel passeios={filtered} onNav={onNav} testid="passeios-grid" />
        )}
      </div>
    </section>
  );
}

// ---- Passeios Realizados (arquivo público) ----
function PasseiosRealizados({ onNav }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    apiGet('/passeios-realizados')
      .then(r => setItems(r.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  const openDetail = (t) => onNav('passeio-realizado', { slug: t.slug || t.id, trip: t });

  return (
    <section className="section" data-testid="passeios-realizados-page">
      <div className="container">
        <div className="section-header">
          <h2>Passeios Realizados</h2>
          <div className="underline"></div>
          <p className="muted">A nossa história off-road — cada expedição, cada trilho, cada memória. Explora o arquivo e vê o que já fizemos.</p>
        </div>

        {loading && <p className="muted">A carregar arquivo…</p>}
        {!loading && items.length === 0 && (
          <div className="muted" style={{ padding: '48px 0', textAlign: 'center' }}>
            <p>Ainda não há passeios realizados no arquivo.</p>
            <p className="small">Assim que terminarmos os primeiros passeios, aparecerão aqui as fotos, os testemunhos e as memórias.</p>
          </div>
        )}
        {!loading && items.length > 0 && (
          <div className="realizados-grid" data-testid="realizados-grid">
            {items.map(t => (
              <article key={t.id} className="realizado-card" data-testid={`realizado-${t.id}`} onClick={() => openDetail(t)}>
                <div className="realizado-image" style={{ backgroundImage: `url(${resolveImg((t.completed_gallery && t.completed_gallery[0]) || t.image || t.detail_image || IMG.serra)})` }}>
                  {t.date_label && <span className="realizado-date">{t.date_label}</span>}
                </div>
                <div className="realizado-body">
                  <h3>{t.title}</h3>
                  {t.region && <p className="muted small">{t.region}</p>}
                  <p className="small" style={{ marginTop: 8 }}>{t.short_desc || t.long_desc?.slice(0, 120) + '…' || ''}</p>
                  <div className="realizado-meta">
                    {t.vagas_total ? <span>🚙 Lotação: {t.vagas_total} viaturas</span> : null}
                    {t.distance_km ? <span>📏 {t.distance_km} km</span> : null}
                    {t.difficulty ? <span>⚠ {t.difficulty}</span> : null}
                  </div>
                  <button className="btn btn-outline btn-sm" data-testid={`realizado-open-${t.id}`}>Ver arquivo →</button>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function PasseioRealizadoDetail({ slug, onNav }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    apiGet(`/passeios-realizados/${encodeURIComponent(slug)}`)
      .then(r => setData(r))
      .catch(e => setError(e.message || 'Não foi possível carregar.'))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <section className="section"><div className="container"><p className="muted">A carregar…</p></div></section>;
  if (error || !data) {
    return (
      <section className="section">
        <div className="container">
          <p className="muted">{error || 'Passeio não encontrado no arquivo.'}</p>
          <button className="btn btn-outline" onClick={() => onNav('passeios-realizados')}>← Voltar ao arquivo</button>
        </div>
      </section>
    );
  }
  const t = data.trip;
  const testimonials = data.testimonials || [];
  const gallery = (t.completed_gallery && t.completed_gallery.length) ? t.completed_gallery : (t.gallery || []);
  const videos = (t.completed_videos && t.completed_videos.length) ? t.completed_videos : (t.videos || []);

  return (
    <section className="section" data-testid="realizado-detail-page">
      <div className="container">
        <a onClick={() => onNav('passeios-realizados')} className="muted small" style={{ cursor: 'pointer' }} data-testid="realizado-back">← Passeios Realizados</a>

        <div className="realizado-hero" style={{ marginTop: 12 }}>
          <div>
            <h1>{t.title}</h1>
            {t.region && <p className="lead">{t.region}</p>}
            <div className="realizado-hero-meta">
              {t.date_label && <span>📅 {t.date_label}</span>}
              {t.vagas_total ? <span>🚙 Lotação máxima: {t.vagas_total} viaturas</span> : null}
              {t.distance_km ? <span>📏 {t.distance_km} km</span> : null}
              {t.difficulty ? <span>⚠ {t.difficulty}</span> : null}
            </div>
          </div>
        </div>

        {t.completed_recap && (
          <div className="realizado-recap">
            <h3>Como correu</h3>
            <p>{t.completed_recap}</p>
          </div>
        )}

        {(t.short_desc || t.long_desc) && (
          <div className="detail-block" data-testid="realizado-desc">
            <h3>Sobre este passeio</h3>
            {t.short_desc && <p className="lead" style={{ marginTop: 0 }}>{t.short_desc}</p>}
            {t.long_desc && <p className="muted" style={{ whiteSpace: 'pre-line' }}>{t.long_desc}</p>}
          </div>
        )}

        {t.highlights && t.highlights.length > 0 && (
          <div className="detail-block" data-testid="realizado-highlights">
            <h3>Destaques</h3>
            <ul className="check-list">
              {t.highlights.map((h, i) => <li key={i}><span className="dot-accent" />{h}</li>)}
            </ul>
          </div>
        )}

        {((t.included && t.included.length > 0) || (t.not_included && t.not_included.length > 0)) && (
          <div className="grid grid-2" style={{ gap: 20 }}>
            {t.included && t.included.length > 0 && (
              <div className="detail-block" data-testid="realizado-included">
                <h4>Incluído</h4>
                <ul className="check-list check-list-yes">
                  {t.included.map((h, i) => <li key={i}><span className="check-y">✓</span>{h}</li>)}
                </ul>
              </div>
            )}
            {t.not_included && t.not_included.length > 0 && (
              <div className="detail-block" data-testid="realizado-not-included">
                <h4>Não incluído</h4>
                <ul className="check-list check-list-no">
                  {t.not_included.map((h, i) => <li key={i}><span className="check-n">×</span>{h}</li>)}
                </ul>
              </div>
            )}
          </div>
        )}

        {gallery.length > 0 && (
          <div className="realizado-gallery-block">
            <h3>Álbum do dia</h3>
            <div className="realizado-gallery" data-count={gallery.length > 10 ? 'many' : String(gallery.length)}>
              {gallery.map((src, i) => (
                <div key={i} className="realizado-thumb" onClick={() => setLightbox(src)} data-testid={`realizado-photo-${i}`}>
                  <img src={resolveImg(src)} alt={`Foto ${i + 1}`} loading="lazy" />
                </div>
              ))}
            </div>
          </div>
        )}

        {videos.length > 0 && (
          <div className="realizado-videos-block">
            <h3>Vídeos</h3>
            <div className="realizado-videos">
              {videos.map((v, i) => (
                <div key={i} className="realizado-video">
                  <iframe
                    src={`https://www.youtube.com/embed/${v.id || v}?vq=${getPreferredQuality('url')}&hd=1&modestbranding=1&rel=0`}
                    title={v.title || `Vídeo ${i + 1}`}
                    width="1920"
                    height="1080"
                    frameBorder="0"
                    allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {testimonials.length > 0 && (
          <div className="realizado-testimonials-block">
            <h3>O que disseram os participantes</h3>
            <div className="realizado-testimonials">
              {testimonials.map(tt => (
                <blockquote key={tt.id} className="realizado-testimonial" data-testid={`realizado-testimonial-${tt.id}`}>
                  {tt.rating ? <div className="stars">{'★'.repeat(tt.rating)}{'☆'.repeat(5 - tt.rating)}</div> : null}
                  <p>“{tt.text}”</p>
                  <footer>
                    <strong>{tt.author_name}</strong>
                    {tt.position ? <span className="muted"> · {tt.position}</span> : null}
                    {tt.company ? <span className="muted"> · {tt.company}</span> : null}
                  </footer>
                </blockquote>
              ))}
            </div>
          </div>
        )}

        <div className="realizado-cta">
          <h3>Queres um passeio como este?</h3>
          <p className="muted">Fala connosco — organizamos para o teu grupo, empresa ou município.</p>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', justifyContent: 'center', marginTop: 12 }}>
            <button className="btn" onClick={() => onNav('teambuilding')} data-testid="realizado-cta-tb">Team Building</button>
            <button className="btn btn-outline" onClick={() => onNav('municipios')} data-testid="realizado-cta-mun">Municípios</button>
            <button className="btn btn-outline" onClick={() => onNav('contactos')} data-testid="realizado-cta-contact">Contactar</button>
          </div>
        </div>

        {lightbox && (
          <div className="lightbox" onClick={() => setLightbox(null)} data-testid="realizado-lightbox">
            <img src={resolveImg(lightbox)} alt="foto" />
            <button className="lightbox-close" onClick={() => setLightbox(null)}>×</button>
          </div>
        )}
      </div>
    </section>
  );
}


function TripVideos({ videos }) {
  const [playing, setPlaying] = useState(null);
  if (!videos || videos.length === 0) return null;
  return (
    <div className="detail-block" data-testid="trip-videos">
      <h4>Vídeos do passeio</h4>
      <div className={`trip-videos-grid ${videos.length === 1 ? 'single' : ''}`}>
        {videos.map(v => (
          <div key={v.id} className="video-wrap">
            {playing === v.id ? (
              <iframe
                src={`https://www.youtube.com/embed/${v.id}?autoplay=1&rel=0&modestbranding=1&playsinline=1&vq=${getPreferredQuality('url')}&hd=1`}
                title={v.title || 'Vídeo Tectur'}
                width="1920"
                height="1080"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                referrerPolicy="strict-origin-when-cross-origin"
                allowFullScreen
              />
            ) : (
              <button className="video-poster" onClick={() => setPlaying(v.id)} style={{ backgroundImage: `url(https://i.ytimg.com/vi/${v.id}/hqdefault.jpg)` }} aria-label={`Reproduzir ${v.title || 'vídeo'}`}>
                <span className="video-poster-play">▶</span>
                {v.title && <span className="video-poster-title">{v.title}</span>}
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function TermsModal({ open, onAccept, onClose, initialAceites }) {
  const [text, setText] = useState('');
  const [aceites, setAceites] = useState(initialAceites || { termo: false, rgpd: false, tc: false });
  useEffect(() => {
    if (!text) apiGet('/config/responsibility-term').then(r => setText(r.text || '')).catch(() => {});
  }, [text]);
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = ''; };
  }, [open, onClose]);
  if (!open) return null;
  const allChecked = aceites.termo && aceites.rgpd && aceites.tc;
  return (
    <div className="modal-backdrop" data-testid="terms-modal" onClick={onClose}>
      <div className="modal-box" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Termo de Responsabilidade</h3>
          <button className="modal-close" onClick={onClose} aria-label="Fechar" data-testid="terms-close">×</button>
        </div>
        <div className="modal-body">
          <pre className="terms-text">{text}</pre>
          <div className="modal-accept">
            <p className="label" style={{ marginTop: 24 }}>Para continuar, aceita as condições:</p>
            <label className="check-row"><input type="checkbox" data-testid="accept-termo" checked={aceites.termo} onChange={e => setAceites({ ...aceites, termo: e.target.checked })} /><span>Li e aceito o Termo de Responsabilidade</span></label>
            <label className="check-row"><input type="checkbox" data-testid="accept-rgpd" checked={aceites.rgpd} onChange={e => setAceites({ ...aceites, rgpd: e.target.checked })} /><span>Autorizo o tratamento dos dados (RGPD)</span></label>
            <label className="check-row"><input type="checkbox" data-testid="accept-tc" checked={aceites.tc} onChange={e => setAceites({ ...aceites, tc: e.target.checked })} /><span>Aceito os termos e política de privacidade</span></label>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onClose} data-testid="terms-decline">Cancelar</button>
          <button className="btn" onClick={() => onAccept(aceites)} disabled={!allChecked} data-testid="terms-accept">Aceito e continuo</button>
        </div>
      </div>
    </div>
  );
}

function TripGallery({ images }) {
  const [lightboxIdx, setLightboxIdx] = useState(null);
  const list = (images || []).filter(Boolean);
  if (list.length < 2) return null;

  const primary = list[0];
  const secondary = list.slice(1, 5); // up to 4 more

  return (
    <>
      <div className="gallery-grid" data-testid="trip-gallery">
        <button className="gallery-tile gallery-tile-primary" onClick={() => setLightboxIdx(0)} style={{ backgroundImage: `url(${primary})` }} aria-label="Foto 1">
          <span className="gallery-tile-overlay">
            <span className="gallery-zoom">⤢</span>
          </span>
        </button>
        <div className="gallery-secondary">
          {secondary.map((src, i) => (
            <button key={i + 1} className="gallery-tile" onClick={() => setLightboxIdx(i + 1)} style={{ backgroundImage: `url(${src})` }} aria-label={`Foto ${i + 2}`}>
              {i === secondary.length - 1 && list.length > 5 && (
                <span className="gallery-more">+{list.length - 5}</span>
              )}
              <span className="gallery-tile-overlay">
                <span className="gallery-zoom">⤢</span>
              </span>
            </button>
          ))}
        </div>
      </div>
      {lightboxIdx !== null && (
        <div className="lightbox" data-testid="lightbox" onClick={() => setLightboxIdx(null)}>
          <button className="lightbox-close" onClick={() => setLightboxIdx(null)} aria-label="Fechar">×</button>
          <button className="lightbox-nav lightbox-prev" onClick={(e) => { e.stopPropagation(); setLightboxIdx((lightboxIdx - 1 + list.length) % list.length); }} aria-label="Anterior">‹</button>
          <img className="lightbox-img" src={list[lightboxIdx]} alt={`Foto ${lightboxIdx + 1} de ${list.length}`} onClick={(e) => e.stopPropagation()} />
          <button className="lightbox-nav lightbox-next" onClick={(e) => { e.stopPropagation(); setLightboxIdx((lightboxIdx + 1) % list.length); }} aria-label="Seguinte">›</button>
          <span className="lightbox-counter">{lightboxIdx + 1} / {list.length}</span>
        </div>
      )}
    </>
  );
}

function Inscricao({ passeio }) {
  const [step, setStep] = useState(1);
  const p = passeio;
  const raw = p?._raw || {};

  // Load tiers from backend if available, else derive from price_from with same labels as before
  const backendTiers = raw.tiers && raw.tiers.length ? raw.tiers : null;
  const defaultTiers = [
    { id: 'adulto', label: 'Adulto individual', price: p?.preco || 0 },
    { id: 'casal', label: 'Casal', price: (p?.preco || 0) * 1.8 },
    { id: 'adulto_extra', label: 'Adulto extra (na mesma viatura)', price: (p?.preco || 0) * 0.85 },
    { id: 'crianca_4_17', label: 'Criança 4-17 anos', price: (p?.preco || 0) * 0.5 },
    { id: 'crianca_0_3', label: 'Criança 0-3 anos', price: 0 },
  ];
  const tierDefs = (backendTiers || defaultTiers).map(t => ({ id: t.id, label: t.label, price: t.price }));

  const [qty, setQty] = useState(() => {
    const o = {};
    tierDefs.forEach(t => { o[t.id] = 0; });
    if ('adulto' in o) o.adulto = 1;
    return o;
  });
  const [driver, setDriver] = useState({ name: '', phone: '', email: '', cc: '', nif: '', address: '' });
  const [teamName, setTeamName] = useState('');
  const [companions, setCompanions] = useState([]);
  const isNavigation = p?.type === 'navigation' || raw.type === 'navigation';
  const [aceites, setAceites] = useState({ termo: false, rgpd: false, tc: false });
  const [showTerms, setShowTerms] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const numPessoas = tierDefs.reduce((s, t) => s + (t.id === 'crianca_0_3' ? 0 : (t.id === 'casal' ? qty[t.id] * 2 : qty[t.id])), 0);
  const total = tierDefs.reduce((s, t) => s + qty[t.id] * t.price, 0);

  const changeQty = (key, delta) => setQty(prev => ({ ...prev, [key]: Math.max(0, (prev[key] || 0) + delta) }));

  useEffect(() => {
    const needed = Math.max(0, numPessoas - 1);
    setCompanions(prev => {
      const next = prev.slice(0, needed);
      while (next.length < needed) next.push({ name: '', cc: '', age: '' });
      return next;
    });
  }, [numPessoas]);

  const updateCompanion = (i, field, value) => {
    setCompanions(prev => prev.map((c, idx) => idx === i ? { ...c, [field]: value } : c));
  };

  const submit = async () => {
    setError('');
    setSubmitting(true);
    try {
      const payload = {
        trip_id: p.id,
        team_name: isNavigation ? teamName.trim() : null,
        tiers: tierDefs.map(t => ({ id: t.id, label: t.label, price: t.price, qty: qty[t.id] || 0 })),
        extras: [],
        driver_name: driver.name,
        driver_email: driver.email,
        driver_phone: driver.phone,
        driver_cc: driver.cc,
        driver_nif: driver.nif,
        driver_address: driver.address,
        companions: companions.map(c => ({ name: c.name, cc: c.cc || null, age: c.age ? parseInt(c.age) : null })),
        accepted_terms: aceites.termo,
        accepted_rgpd: aceites.rgpd,
        accepted_privacy: aceites.tc,
      };
      const res = await apiPost('/inscriptions', payload);
      setResult(res);
    } catch (err) {
      setError(err.message || 'Erro ao submeter inscrição.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!p) return <section className="section"><div className="container"><p className="muted">Escolhe um passeio primeiro.</p></div></section>;

  const gallery = (p.gallery && p.gallery.length ? p.gallery : []).filter(Boolean);
  const stepLabels = ['Participantes', 'Condutor', 'Acompanhantes', 'Confirmação'];

  return (
    <section className="section">
      <div className="container">
        <div className="hero-mini" style={{ backgroundImage: `linear-gradient(rgba(0,0,0,0.5), rgba(0,0,0,0.8)), url(${p.img})` }}>
          <h1>{p.titulo.toUpperCase()}</h1>
          {isNavigation && <span className="pretitle" style={{ color: 'var(--accent)', marginTop: 8, display: 'block' }}>NAVIGATION CHALLENGE · RALLY PAPER 4×4</span>}
          {p.region && <p className="lead" style={{ marginBottom: 12 }}>{p.region}</p>}
          <div className="meta-row">
            <span>📅 {p.data}</span>
            <span>⏱ {p.duracao}</span>
            {p.distance_km ? <span>📏 {p.distance_km} km</span> : null}
            <span>⚠ {p.dificuldade}</span>
            <span>👥 {p.vagas}/{p.total} vagas</span>
          </div>
          {p.id && (
            <a
              className="btn btn-outline btn-sm add-cal-btn"
              href={`${API}/trips/${p.id}/calendar.ics`}
              data-testid="add-calendar-btn"
              style={{ marginTop: 14, display: 'inline-flex', alignItems: 'center', gap: 6 }}
            >
              📅 Adicionar ao Calendário (Google · Apple · Outlook)
            </a>
          )}
        </div>

        <TripGallery images={gallery} />

        <div className="steps-indicator steps-indicator-top" data-testid="steps-indicator">
          {stepLabels.map((s, i) => (
            <div key={i} className={`step-dot ${step === i + 1 ? 'active' : ''} ${step > i + 1 ? 'done' : ''}`}>
              <span>{i + 1}</span><small>{s}</small>
            </div>
          ))}
        </div>

        <div className="inscricao-layout">
          <div className="inscricao-content" data-testid="trip-detail-content">
            {p.long_desc && (
              <>
                <h3>Sobre este passeio</h3>
                <p className="muted">{p.long_desc}</p>
              </>
            )}

            {p.highlights && p.highlights.length > 0 && (
              <div className="detail-block">
                <h4>Destaques</h4>
                <ul className="check-list">
                  {p.highlights.map((h, i) => <li key={i}><span className="dot-accent" />{h}</li>)}
                </ul>
              </div>
            )}

            {p.included && p.included.length > 0 && (
              <div className="detail-block">
                <h4>Incluído no preço</h4>
                <ul className="check-list check-list-yes">
                  {p.included.map((h, i) => <li key={i}><span className="check-y">✓</span>{h}</li>)}
                </ul>
              </div>
            )}

            {p.not_included && p.not_included.length > 0 && (
              <div className="detail-block">
                <h4>Não incluído</h4>
                <ul className="check-list check-list-no">
                  {p.not_included.map((h, i) => <li key={i}><span className="check-n">×</span>{h}</li>)}
                </ul>
              </div>
            )}

            {p.vehicle_requirements && p.vehicle_requirements.length > 0 && (
              <div className="detail-block">
                <h4>Requisitos da viatura</h4>
                <ul className="check-list">
                  {p.vehicle_requirements.map((h, i) => <li key={i}><span className="dot-accent" />{h}</li>)}
                </ul>
              </div>
            )}

            <TripVideos videos={p.videos} />

            <div className="detail-block private-info">
              <h4>Ponto de encontro</h4>
              <p className="muted small">🔒 O ponto de encontro e as coordenadas GPS exatas são enviados por email a inscritos com pagamento confirmado, 48h antes do passeio, juntamente com os ficheiros GPX/KML ou irá aparecer na app Tectur 4×4.</p>
            </div>
          </div>

          <aside className="inscricao-form" data-testid="inscricao-form">
            {raw.inscriptions_closed ? (
              <div data-testid="inscricao-closed" style={{ padding: '16px 4px' }}>
                <p className="label" style={{ color: '#ff6b1a' }}>🔒 Inscrições encerradas</p>
                <div className="terms-box" style={{ borderColor: 'rgba(255,107,26,.4)' }}>
                  <h4 style={{ margin: '0 0 10px' }}>Inscrições fechadas para este passeio</h4>
                  <p className="muted">As inscrições para <strong>{p.titulo}</strong> foram encerradas. Já não estamos a aceitar novos inscritos.</p>
                  <p className="small muted" style={{ marginTop: 12 }}>Fica atento aos próximos passeios em <strong>tectur.pt/passeios</strong> ou subscreve a nossa newsletter.</p>
                </div>
              </div>
            ) : result ? (
              <div data-testid="inscricao-success">
                <p className="label">Inscrição registada!</p>
                <div className="terms-box">
                  <h4>Referência: {result.reference}</h4>
                  <p>Total a pagar: <strong>{result.total}€</strong></p>
                  <p>Recebeste um email em <strong>{result.driver_email}</strong> com o NIB para transferência bancária.</p>
                  <p className="small muted">Após confirmação do pagamento, receberás um segundo email com os ficheiros GPX/KML e o ponto de encontro exato.</p>
                </div>
              </div>
            ) : (
              <>
                <h3>Inscreve-te</h3>
                {step === 1 && (
                  <div>
                    {isNavigation && (
                      <>
                        <p className="label">Nome da tua equipa</p>
                        <div className="form-row">
                          <input required data-testid="team-name" placeholder="Ex: Os Aventureiros" value={teamName} onChange={e => setTeamName(e.target.value)} />
                          <small className="muted" style={{ fontSize: 12, marginTop: 6, display: 'block' }}>Este nome aparecerá no ranking do desafio.</small>
                        </div>
                      </>
                    )}
                    <p className="label">Escolhe os participantes</p>
                    {tierDefs.map(t => (
                      <div key={t.id} className="tier">
                        <div>
                          <div className="tier-name">{t.label}</div>
                          <div className="tier-price">{t.price === 0 ? 'GRÁTIS' : t.price + '€'}</div>
                        </div>
                        <div className="qty">
                          <button data-testid={`qty-${t.id}-minus`} onClick={() => changeQty(t.id, -1)}>−</button>
                          <span data-testid={`qty-${t.id}-value`}>{qty[t.id] || 0}</span>
                          <button data-testid={`qty-${t.id}-plus`} onClick={() => changeQty(t.id, 1)}>+</button>
                        </div>
                      </div>
                    ))}
                    <div className="total-box"><small>SUBTOTAL</small><strong>{total.toFixed(0)}€</strong></div>
                    <button className="btn btn-block" data-testid="step1-continue" onClick={() => setStep(2)} disabled={numPessoas === 0 || (isNavigation && !teamName.trim())}>Continuar</button>
                  </div>
                )}
                {step === 2 && (
                  <div>
                    <p className="label">Dados do condutor</p>
                    <div className="form-row"><label>Nome completo</label><input required data-testid="driver-name" value={driver.name} onChange={e => setDriver({ ...driver, name: e.target.value })} /></div>
                    <div className="form-row"><label>Contacto telefónico</label><input type="tel" required data-testid="driver-phone" value={driver.phone} onChange={e => setDriver({ ...driver, phone: e.target.value })} /></div>
                    <div className="form-row"><label>Email</label><input type="email" required data-testid="driver-email" value={driver.email} onChange={e => setDriver({ ...driver, email: e.target.value })} /></div>
                    <div className="form-row"><label>Cartão de cidadão (para seguro)</label><input required data-testid="driver-cc" value={driver.cc} onChange={e => setDriver({ ...driver, cc: e.target.value })} /></div>
                    <div className="form-row"><label>NIF (para faturação)</label><input required data-testid="driver-nif" value={driver.nif} onChange={e => setDriver({ ...driver, nif: e.target.value })} /></div>
                    <div className="form-row"><label>Morada de faturação</label><input required data-testid="driver-address" value={driver.address} onChange={e => setDriver({ ...driver, address: e.target.value })} /></div>
                    <div className="form-nav">
                      <button className="btn btn-outline" onClick={() => setStep(1)}>Voltar</button>
                      <button className="btn" data-testid="step2-continue" onClick={() => setStep(3)}
                        disabled={!driver.name || !driver.phone || !driver.email || !driver.cc || !driver.nif || !driver.address}>Continuar</button>
                    </div>
                  </div>
                )}
                {step === 3 && (
                  <div>
                    <p className="label">Acompanhantes ({companions.length})</p>
                    {companions.length === 0 && <p className="small muted">Sem acompanhantes — segue para a confirmação.</p>}
                    {companions.map((c, i) => (
                      <div key={i} className="acomp-box">
                        <strong>Acompanhante {i + 1}</strong>
                        <div className="form-row"><label>Nome</label><input required data-testid={`comp-${i}-name`} value={c.name} onChange={e => updateCompanion(i, 'name', e.target.value)} /></div>
                        <div className="form-row"><label>Cartão de cidadão</label><input data-testid={`comp-${i}-cc`} value={c.cc} onChange={e => updateCompanion(i, 'cc', e.target.value)} /></div>
                        <div className="form-row"><label>Idade</label><input type="number" data-testid={`comp-${i}-age`} value={c.age} onChange={e => updateCompanion(i, 'age', e.target.value)} /></div>
                      </div>
                    ))}
                    <div className="form-nav">
                      <button className="btn btn-outline" onClick={() => setStep(2)}>Voltar</button>
                      <button className="btn" data-testid="step3-continue" onClick={() => {
                        if (aceites.termo && aceites.rgpd && aceites.tc) setStep(4);
                        else setShowTerms(true);
                      }}>Continuar</button>
                    </div>
                  </div>
                )}
                {step === 4 && (
                  <div>
                    <p className="label">Confirmação</p>
                    <p className="small muted" style={{ marginBottom: 16 }}>✓ Termos aceites. Podes agora confirmar a tua inscrição.</p>
                    <div className="total-box"><small>TOTAL A PAGAR</small><strong>{total.toFixed(0)}€</strong></div>
                    {error && <p className="small" style={{ color: '#ff6b1a', marginTop: 8 }} data-testid="inscricao-error">{error}</p>}
                    <div className="form-nav">
                      <button className="btn btn-outline" onClick={() => setStep(3)}>Voltar</button>
                      <button className="btn" data-testid="inscricao-confirm" disabled={submitting} onClick={submit}>{submitting ? 'A enviar…' : 'Confirmar Inscrição'}</button>
                    </div>
                    <p className="small muted" style={{ marginTop: 8 }}>Pagamento por transferência bancária. Receberás email com NIB e referência única.</p>
                  </div>
                )}
              </>
            )}
          </aside>
        </div>
        <TermsModal
          open={showTerms}
          initialAceites={aceites}
          onClose={() => setShowTerms(false)}
          onAccept={(a) => { setAceites(a); setShowTerms(false); setStep(4); }}
        />
      </div>
    </section>
  );
}

function Apps() {
  return (
    <section className="section">
      <div className="container">
        <div className="section-header">
          <h2>As Nossas Apps</h2>
          <div className="underline"></div>
          <p className="muted">Feitas em Portugal para condutores 4x4 e expedições off-road.</p>
        </div>
        <div className="grid grid-2">
          <a className="app-card app-card-big" href="https://tectur.pt/apps/tectur-4x4.html" target="_blank" rel="noreferrer">
            <div className="app-card-media" style={{ backgroundImage: `url(${IMG.convoy})`, height: 280 }}>
              <img className="app-card-icon" src={APP_ICON_4X4} alt="Tectur 4×4" />
            </div>
            <div className="app-card-body">
              <span className="badge">Navegação Off-Road</span>
              <h3>Tectur 4×4 Navigation</h3>
              <p className="muted">Descarrega rotas curadas, navega offline com mapas Mapsforge, junta o teu grupo com walkie-talkie e tracking em tempo real. Envia SOS num toque, desafia-te nos Navigation Challenges e leva a navegação para o ecrã do carro com Android Auto.</p>
              <ul className="feat-list">
                <li>Mapas offline</li><li>Grupos privados</li><li>Navigation Challenge</li><li>SOS</li><li>Walkie-talkie</li><li>Android Auto</li>
              </ul>
              <span className="link">Saber mais →</span>
            </div>
          </a>
          <a className="app-card app-card-big" href="https://tectur.pt/apps/tectur-car-launcher.html" target="_blank" rel="noreferrer">
            <div className="app-card-media" style={{ backgroundImage: `url(${IMG.tablet})`, height: 280 }}>
              <img className="app-card-icon" src={APP_ICON_LAUNCHER} alt="Tectur Car Launcher" />
            </div>
            <div className="app-card-body">
              <span className="badge">Cockpit Android</span>
              <h3>Tectur Car Launcher</h3>
              <p className="muted">Substitui o ecrã HOME do teu tablet ou telemóvel no carro. Velocidade GPS, meteorologia IPMA, risco de fogo, música Bluetooth e tudo à mão enquanto conduzes off-road.</p>
              <ul className="feat-list">
                <li>Substitui launcher</li><li>Velocímetro GPS</li><li>IPMA · Risco fogo</li><li>Spotify · YT Music</li><li>Modo nocturno</li>
              </ul>
              <span className="link">Saber mais →</span>
            </div>
          </a>
        </div>
      </div>
    </section>
  );
}

function Equipamento() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const slots = 6;

  useEffect(() => {
    apiGet('/equipment/shop')
      .then(r => setProducts(r.items || []))
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  const numPages = Math.max(1, Math.ceil(products.length / slots));
  useEffect(() => {
    if (numPages <= 1) return;
    const t = setInterval(() => setPage(p => (p + 1) % numPages), 15000);
    return () => clearInterval(t);
  }, [numPages]);

  const start = (page * slots) % Math.max(1, products.length);
  const showing = [];
  for (let i = 0; i < Math.min(slots, products.length); i++) {
    showing.push(products[(start + i) % products.length]);
  }

  return (
    <section className="section">
      <div className="container">
        <div className="hero-mini" style={{ backgroundImage: `linear-gradient(rgba(0,0,0,0.5), rgba(0,0,0,0.8)), url(${IMG.tablet})` }}>
          <h1>EQUIPAMENTO TECTUR</h1>
          <p className="lead">Tablets rugged Android convertidos, suportes RAM e acessórios profissionais — direto de <a className="link" href="https://loja.tectur.pt" target="_blank" rel="noreferrer">loja.tectur.pt</a>.</p>
        </div>

        {loading && <p className="muted">A carregar equipamento da loja…</p>}
        {error && <p className="small" style={{ color: '#ff6b1a' }} data-testid="shop-error">{error}</p>}
        {!loading && products.length === 0 && !error && <p className="muted">Sem produtos disponíveis no momento.</p>}

        {!loading && products.length > 0 && (
          <>
            <div className="grid grid-3" data-testid="shop-grid">
              {showing.map(p => (
                <a key={`${page}-${p.slug}`} className="card card-anim shop-card" href={p.url} target="_blank" rel="noreferrer" data-testid={`shop-card-${p.slug}`}>
                  <div className="card-img shop-card-img" style={{ backgroundImage: p.image ? `url(${p.image})` : 'none' }}>
                    {p.price_text && <span className="shop-price">{p.price_text}</span>}
                  </div>
                  <div className="card-body">
                    <h3>{p.name}</h3>
                    {p.description && <p className="muted small">{p.description}</p>}
                    <div className="card-footer">
                      <span className="link">Ver na loja →</span>
                    </div>
                  </div>
                </a>
              ))}
            </div>
            {numPages > 1 && (
              <div className="videos-dots" data-testid="shop-dots">
                {Array.from({ length: numPages }).map((_, i) => (
                  <button key={i} className={`videos-dot ${i === page ? 'active' : ''}`} onClick={() => setPage(i)} aria-label={`Página ${i + 1}`} />
                ))}
              </div>
            )}
            <p className="small muted center" style={{ marginTop: 16 }}>{products.length} produtos · a rodar a cada 15 s · atualização automática da loja</p>
          </>
        )}

        <div className="cta-banner" style={{ marginTop: 48 }}>
          <p>Todos os produtos são geridos e vendidos em <strong>loja.tectur.pt</strong></p>
          <a className="btn" href="https://loja.tectur.pt" target="_blank" rel="noreferrer">Visitar Loja Tectur</a>
        </div>
      </div>
    </section>
  );
}

function CompanyTestimonials() {
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ company: '', author_name: '', position: '', text: '' });
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    apiGet('/testimonials?source=team_building')
      .then(r => setItems(r.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoaded(true));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setStatus(''); setError('');
    try {
      await apiPost('/testimonials', { ...form, source: 'team_building' });
      setStatus('Obrigado! O teu testemunho foi enviado e será publicado após validação pela Tectur.');
      setForm({ company: '', author_name: '', position: '', text: '' });
      setTimeout(() => { setShowForm(false); setStatus(''); }, 5000);
    } catch (err) {
      setError(err.message || 'Erro ao enviar testemunho.');
    }
  };

  return (
    <>
      <div className="section-header">
        <h2>O Que Dizem as Empresas Tectur</h2>
        <div className="underline"></div>
        <p className="muted">Feedback real de quem já participou · publicado após moderação.</p>
      </div>

      {loaded && items.length === 0 && (
        <div className="filter-hint" style={{ textAlign: 'center' }}>
          <p className="muted">Ainda sem testemunhos publicados. <strong>Sê a primeira empresa a partilhar a tua experiência.</strong></p>
        </div>
      )}

      {items.length > 0 && (
        <div className="grid grid-3" data-testid="tb-testimonials">
          {items.map(t => (
            <div key={t.id} className="testimonial">
              <span className="quote-mark">&ldquo;</span>
              <p>{t.text}</p>
              <div className="testimonial-author">
                <strong>{t.author_name}</strong>
                <span className="muted small">{t.position ? `${t.position} · ` : ''}{t.company}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      <div style={{ textAlign: 'center', marginTop: 32 }}>
        {!showForm ? (
          <button className="btn btn-outline" onClick={() => setShowForm(true)} data-testid="tb-add-testimonial">Já participaste? Deixa o teu testemunho</button>
        ) : (
          <form className="form-panel" onSubmit={submit} data-testid="tb-testimonial-form" style={{ maxWidth: 720, margin: '0 auto', textAlign: 'left' }}>
            <h3>Partilha a tua experiência</h3>
            <p className="small muted" style={{ marginBottom: 20 }}>Todos os testemunhos são validados pela Tectur antes de aparecerem publicamente. Não partilhes dados sensíveis.</p>
            <div className="grid grid-2">
              <div className="form-row"><label>Empresa</label><input required data-testid="testimonial-company" value={form.company} onChange={e => setForm({ ...form, company: e.target.value })} /></div>
              <div className="form-row"><label>O teu nome</label><input required data-testid="testimonial-name" value={form.author_name} onChange={e => setForm({ ...form, author_name: e.target.value })} /></div>
              <div className="form-row" style={{ gridColumn: '1 / -1' }}><label>Cargo (opcional)</label><input data-testid="testimonial-position" value={form.position} onChange={e => setForm({ ...form, position: e.target.value })} placeholder="Ex: Diretora RH, CEO…" /></div>
            </div>
            <div className="form-row"><label>Testemunho (mínimo 20 caracteres)</label><textarea rows="5" required minLength="20" maxLength="1000" data-testid="testimonial-text" value={form.text} onChange={e => setForm({ ...form, text: e.target.value })} placeholder="Conta-nos como foi a experiência da tua equipa…"></textarea></div>
            <div className="form-nav">
              <button type="button" className="btn btn-outline" onClick={() => setShowForm(false)}>Cancelar</button>
              <button className="btn" type="submit" data-testid="testimonial-submit">Enviar</button>
            </div>
            {status && <p className="small" style={{ color: 'var(--accent)', marginTop: 12 }} data-testid="testimonial-status">{status}</p>}
            {error && <p className="small" style={{ color: '#ff6b1a', marginTop: 12 }} data-testid="testimonial-error">{error}</p>}
          </form>
        )}
      </div>
    </>
  );
}

function TeamBuilding() {
  const [form, setForm] = useState({ company: '', contact_name: '', email: '', phone: '', participants: '', date_preference: '', objectives: '' });
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  const submit = async (e) => {
    e.preventDefault();
    setStatus(''); setError('');
    try {
      await apiPost('/leads/team-building', form);
      setStatus('Pedido enviado. Contactamos em 24h.');
      setForm({ company: '', contact_name: '', email: '', phone: '', participants: '', date_preference: '', objectives: '' });
    } catch (err) {
      setError(err.message || 'Erro ao enviar pedido.');
    }
  };
  return (
    <section className="section">
      <div className="hero" style={{ backgroundImage: `linear-gradient(rgba(0,0,0,0.55), rgba(0,0,0,0.9)), url(${IMG.teamBuilding})`, minHeight: 560 }}>
        <div className="container hero-inner">
          <span className="pretitle">Empresas · Navigation Challenge</span>
          <h1>TEAM BUILDING<br /><span className="accent">COM PROPÓSITO</span></h1>
          <p className="lead">Rally paper em 4×4 desenhado para empresas. Comunicação, decisão partilhada e coesão de equipa no terreno — não numa sala de reuniões.</p>
          <div className="hero-cta">
            <button className="btn" data-testid="tb-cta-orcamento" onClick={() => document.getElementById('tb-form').scrollIntoView({ behavior: 'smooth' })}>Pedir Orçamento</button>
            <button className="btn btn-outline" onClick={() => document.getElementById('tb-como').scrollIntoView({ behavior: 'smooth' })}>Como Funciona</button>
          </div>
        </div>
      </div>

      <section className="concept">
        <div className="container concept-lead">
          <span className="pretitle">O que é o Navigation Challenge</span>
          <h2>Rally paper. Em 4×4.<br /><span className="accent">Adaptado à tua empresa.</span></h2>
          <p>Aplicámos o conceito de <strong>rally paper</strong> a um <strong>passeio todo-terreno turístico</strong>. Um jogo <strong>didático</strong> em que se junta o off-road à descoberta da zona. A Tectur envia o percurso e a tua equipa tem de encontrar locais de paragem ao longo da rota. Em cada ponto, respondem a uma pergunta sobre o sítio — e só depois desbloqueiam o próximo local. <strong>Vence a equipa que combinar melhor navegação, tempo e respostas certas.</strong></p>
          <p>Cada 4×4 é uma equipa dentro da empresa. Coordenam-se, dividem tarefas, comunicam sob pressão real — <strong>fora do escritório, com a paisagem como sala de reuniões</strong>.</p>
          <p><strong>A Tectur trata de tudo:</strong> definição do percurso, briefing técnico de segurança, guias no terreno, viatura de apoio, logística de refeições e alojamento (quando aplicável), gestão de tempos e ranking final. A equipa da empresa <strong>só se preocupa em participar</strong> — do momento em que chegam ao ponto de partida até ao encerramento, estão acompanhados por profissionais Tectur credenciados.</p>
          <p className="small muted" style={{ marginTop: 16 }}>Não trabalhas numa empresa e queres experimentar em grupo privado ou família? Vê a nossa secção de <a className="link" onClick={() => document.querySelector('[data-testid="nav-passeios"]').click()} style={{ cursor: 'pointer' }}>Passeios</a> — os Navigation Challenge também estão abertos ao público.</p>
        </div>
      </section>

      <div id="tb-como" className="container section">
        <div className="section-header">
          <h2>Como Funciona</h2>
          <div className="underline"></div>
          <p className="muted">4 passos, 0 competição, 100% descoberta.</p>
        </div>
        <div className="grid grid-4">
          {[
            { n: 1, t: 'Recebes a rota', d: 'A Tectur envia o track do percurso diretamente para a app Tectur 4×4 da tua equipa.' },
            { n: 2, t: 'Encontras o local', d: 'Segues o percurso na app até chegares a cada ponto de paragem.' },
            { n: 3, t: 'Respondes à pergunta', d: 'Perguntas sobre a zona no smartphone — desbloqueiam o próximo ponto.' },
            { n: 4, t: 'Vencem em conjunto', d: 'A equipa que combina melhor navegação, respostas certas e coesão vence o desafio.' },
          ].map(s => (
            <div key={s.n} className="step">
              <div className="step-num">{s.n}</div>
              <h3>{s.t}</h3>
              <p className="muted small">{s.d}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="container section">
        <div className="filter-hint" style={{ maxWidth: 880, margin: '0 auto', textAlign: 'center' }}>
          <p><strong style={{ color: 'var(--accent)' }}>Serviço chave-na-mão.</strong> A Tectur acompanha o evento do início ao fim com guias no terreno, viatura de apoio, briefing técnico, gestão de tempos e coordenação logística (refeições, alojamento, transferes se necessário). A tua empresa só se preocupa em desfrutar.</p>
        </div>
      </div>

      <div className="container section">
        <div className="section-header">
          <h2>O Que Precisa a Tua Empresa Para Participar</h2>
          <div className="underline"></div>
        </div>
        <div className="grid grid-4">
          {[
            { icon: '🚙', t: 'Uma viatura 4×4', d: 'Cada 4×4 é uma equipa. Se não tens, avisa-nos que combinamos.' },
            { icon: '📱', t: 'App Tectur 4×4 (obrigatória)', d: 'Cada equipa instala a app Tectur 4×4 no smartphone — é ela que gere a rota, perguntas e pontuação.' },
            { icon: '🌐', t: 'Dados móveis', d: 'A app precisa de rede móvel para validar respostas e desbloquear os pontos seguintes.' },
            { icon: '🏆', t: 'Espírito de equipa', d: 'Vem preparado para colaborar, decidir em grupo e vencer com os teus colegas.' },
          ].map(s => (
            <div key={s.t} className="step">
              <div className="step-icon">{s.icon}</div>
              <h3>{s.t}</h3>
              <p className="muted small">{s.d}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="container section">
        <div className="section-header">
          <h2>Programas Tectur</h2>
          <div className="underline"></div>
          <p className="muted">Dois formatos para escolher — do desafio de dia único à expedição de fim-de-semana.</p>
        </div>
        <div className="grid grid-2" data-testid="tb-programs">
          <div className="card card-featured">
            <div className="card-img" style={{ backgroundImage: `url(${IMG.miradouro})`, height: 220 }}>
              <span className="badge">Mais popular</span>
            </div>
            <div className="card-body">
              <h3>Dia Completo · Full Challenge</h3>
              <div className="meta"><span>⏱ 8 horas</span><span>🚙 10-30 viaturas</span></div>
              <ul className="feat-list">
                <li>Rota completa com 8-12 pontos de paragem</li>
                <li>Perguntas estratégicas e culturais sobre a zona</li>
                <li>Almoço tradicional em restaurante regional</li>
                <li>App Tectur 4×4 Full — navegação e desafios integrados</li>
                <li>Prémios simbólicos no final</li>
              </ul>
            </div>
          </div>
          <div className="card">
            <div className="card-img" style={{ backgroundImage: `url(${IMG.aldeia})`, height: 220 }}>
              <span className="badge">Weekend</span>
            </div>
            <div className="card-body">
              <h3>Weekend · Expedição</h3>
              <div className="meta"><span>⏱ 2 dias · 1 noite</span><span>🚙 10-20 viaturas</span></div>
              <ul className="feat-list">
                <li>Imersão total num território de eleição</li>
                <li>Alojamento local ou acampamento premium</li>
                <li>Navegação diurna + jantar temático</li>
                <li>Convívio e dinâmicas de grupo noturno</li>
                <li>Refeições regionais incluídas</li>
              </ul>
            </div>
          </div>
        </div>
        <p className="small muted center" style={{ marginTop: 16 }}>Cada programa é desenhado à medida do teu grupo — pede orçamento abaixo.</p>
      </div>

      <div className="container section">
        <CompanyTestimonials />
      </div>

      <div className="section banner" style={{ backgroundImage: `linear-gradient(rgba(0,0,0,0.7), rgba(0,0,0,0.9)), url(${IMG.miradouro})` }}>
        <div className="container banner-inner">
          <span className="pretitle">Sem competição · Sem tempos · Sem pressão</span>
          <h2>A recompensa é a paisagem<br />e o momento em conjunto.</h2>
          <p>Traz a família, os amigos ou a equipa. Vem criar memórias divertidas e inesquecíveis.</p>
          <button className="btn" onClick={() => document.getElementById('tb-form').scrollIntoView({ behavior: 'smooth' })}>Falar com a Tectur</button>
        </div>
      </div>

      <div id="tb-form" className="container section">
        <div className="section-header">
          <h2>Pedir Orçamento</h2>
          <div className="underline"></div>
          <p className="muted">Respondemos em 24h com uma proposta desenhada para o teu grupo.</p>
        </div>
        <form className="form-panel" onSubmit={submit} data-testid="tb-form">
          <div className="grid grid-2">
            <div className="form-row"><label>Empresa / Grupo</label><input required data-testid="tb-company" placeholder="Empresa ou nome do grupo (família, amigos…)" value={form.company} onChange={e => setForm({ ...form, company: e.target.value })} /></div>
            <div className="form-row"><label>Nome do contacto</label><input required data-testid="tb-contact" value={form.contact_name} onChange={e => setForm({ ...form, contact_name: e.target.value })} /></div>
            <div className="form-row"><label>Email</label><input type="email" required data-testid="tb-email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} /></div>
            <div className="form-row"><label>Telefone</label><input type="tel" data-testid="tb-phone" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} /></div>
            <div className="form-row"><label>Nº participantes</label><input type="number" data-testid="tb-participants" value={form.participants} onChange={e => setForm({ ...form, participants: e.target.value })} placeholder="Ex: 20" /></div>
            <div className="form-row"><label>Data pretendida</label><input type="date" data-testid="tb-date" value={form.date_preference} onChange={e => setForm({ ...form, date_preference: e.target.value })} /></div>
          </div>
          <div className="form-row"><label>Objetivos / Contexto</label><textarea rows="4" data-testid="tb-objectives" placeholder="Conta-nos o que procuras — team building corporativo, dia diferente em família, aniversário, despedida de solteiro/a, evento de clientes…" value={form.objectives} onChange={e => setForm({ ...form, objectives: e.target.value })}></textarea></div>
          <button className="btn" type="submit" data-testid="tb-submit">Enviar Pedido</button>
          {status && <p className="small muted" style={{ marginTop: 12 }} data-testid="tb-status">{status}</p>}
          {error && <p className="small" style={{ color: '#ff6b1a', marginTop: 12 }} data-testid="tb-error">{error}</p>}
        </form>
      </div>
    </section>
  );
}

function MunicipiosTestimonials() {
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ company: '', author_name: '', position: '', text: '' });
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    apiGet('/testimonials?source=municipios').then(r => setItems(r.items || [])).catch(() => setItems([])).finally(() => setLoaded(true));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setStatus(''); setError('');
    try {
      await apiPost('/testimonials', { ...form, source: 'municipios' });
      setStatus('Obrigado! O testemunho será publicado após validação pela Tectur.');
      setForm({ company: '', author_name: '', position: '', text: '' });
      setTimeout(() => { setShowForm(false); setStatus(''); }, 5000);
    } catch (err) {
      setError(err.message || 'Erro ao enviar testemunho.');
    }
  };

  return (
    <>
      <div className="section-header">
        <h2>O Que Dizem as Autarquias</h2>
        <div className="underline"></div>
        <p className="muted">Feedback real de câmaras e postos de turismo · publicado após moderação.</p>
      </div>

      {loaded && items.length === 0 && (
        <div className="filter-hint" style={{ textAlign: 'center' }}>
          <p className="muted">Ainda sem testemunhos publicados. <strong>Sê o primeiro município a partilhar a experiência.</strong></p>
        </div>
      )}

      {items.length > 0 && (
        <div className="grid grid-3" data-testid="mun-testimonials">
          {items.map(t => (
            <div key={t.id} className="testimonial">
              <span className="quote-mark">&ldquo;</span>
              <p>{t.text}</p>
              <div className="testimonial-author">
                <strong>{t.author_name}</strong>
                <span className="muted small">{t.position ? `${t.position} · ` : ''}{t.company}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      <div style={{ textAlign: 'center', marginTop: 32 }}>
        {!showForm ? (
          <button className="btn btn-outline" onClick={() => setShowForm(true)} data-testid="mun-add-testimonial">Já trabalhaste com a Tectur? Deixa o teu testemunho</button>
        ) : (
          <form className="form-panel" onSubmit={submit} data-testid="mun-testimonial-form" style={{ maxWidth: 720, margin: '0 auto', textAlign: 'left' }}>
            <h3>Partilha a experiência do teu município</h3>
            <p className="small muted" style={{ marginBottom: 20 }}>Todos os testemunhos são validados pela Tectur antes de aparecerem publicamente.</p>
            <div className="grid grid-2">
              <div className="form-row"><label>Câmara / Entidade</label><input required data-testid="mun-testimonial-company" value={form.company} onChange={e => setForm({ ...form, company: e.target.value })} /></div>
              <div className="form-row"><label>O teu nome</label><input required data-testid="mun-testimonial-name" value={form.author_name} onChange={e => setForm({ ...form, author_name: e.target.value })} /></div>
              <div className="form-row" style={{ gridColumn: '1 / -1' }}><label>Cargo (opcional)</label><input data-testid="mun-testimonial-position" value={form.position} onChange={e => setForm({ ...form, position: e.target.value })} placeholder="Ex: Vereador do Turismo, Chefe de Divisão…" /></div>
            </div>
            <div className="form-row"><label>Testemunho (mínimo 20 caracteres)</label><textarea rows="5" required minLength="20" maxLength="1000" data-testid="mun-testimonial-text" value={form.text} onChange={e => setForm({ ...form, text: e.target.value })} placeholder="Conta-nos como foi a colaboração e o impacto no concelho…"></textarea></div>
            <div className="form-nav">
              <button type="button" className="btn btn-outline" onClick={() => setShowForm(false)}>Cancelar</button>
              <button className="btn" type="submit" data-testid="mun-testimonial-submit">Enviar</button>
            </div>
            {status && <p className="small" style={{ color: 'var(--accent)', marginTop: 12 }} data-testid="mun-testimonial-status">{status}</p>}
            {error && <p className="small" style={{ color: '#ff6b1a', marginTop: 12 }} data-testid="mun-testimonial-error">{error}</p>}
          </form>
        )}
      </div>
    </>
  );
}

function Municipios() {
  const [form, setForm] = useState({ municipality: '', contact_name: '', position: '', department: '', email: '', phone: '', route_type: 'Todo-terreno', message: '' });
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  const submit = async (e) => {
    e.preventDefault();
    setStatus(''); setError('');
    try {
      await apiPost('/leads/municipios', form);
      setStatus('Contacto enviado. Respondemos em breve.');
      setForm({ municipality: '', contact_name: '', position: '', department: '', email: '', phone: '', route_type: 'Todo-terreno', message: '' });
    } catch (err) {
      setError(err.message || 'Erro ao enviar contacto.');
    }
  };
  return (
    <section className="section">
      <div className="hero" style={{ backgroundImage: `linear-gradient(rgba(0,0,0,0.6), rgba(0,0,0,0.9)), url(${IMG.aldeia})`, minHeight: 560 }}>
        <div className="container hero-inner">
          <span className="pretitle">Setor Público · Parceria com Autarquias</span>
          <h1>ROTAS 4×4 OFICIAIS<br /><span className="accent">DO TEU CONCELHO</span></h1>
          <p className="lead">A Tectur cria rotas turísticas de todo-terreno para o teu município — traçado, pontos de interesse e integração na app Tectur 4×4. <strong>Turismo qualificado, dinamização do território, melhor gestão do turismo de natureza</strong> e retorno económico direto para o comércio local.</p>
          <div className="hero-cta">
            <button className="btn" onClick={() => document.getElementById('mun-form').scrollIntoView({ behavior: 'smooth' })}>Contactar Equipa</button>
            <button className="btn btn-outline" onClick={() => document.getElementById('mun-como').scrollIntoView({ behavior: 'smooth' })}>Como Funciona</button>
          </div>
        </div>
      </div>

      <section className="concept">
        <div className="container concept-lead">
          <span className="pretitle">O conceito</span>
          <h2>O teu concelho tem trilhos.<br /><span className="accent">Nós transformamo-los em turismo.</span></h2>
          <p>O teu município tem <strong>caminhos rurais, veredas florestais, aldeias remotas e miradouros escondidos</strong> que só quem cá vive conhece — e que ninguém sabe explorar. A Tectur contratualiza com autarquias e postos de turismo a criação de <strong>rotas oficiais de 4×4 turístico</strong>: percorremos o território, desenhamos o traçado, identificamos os pontos de interesse e entregamos tudo pronto para atrair visitantes qualificados ao concelho.</p>
          <p>Cada rota é <strong>publicada na app Tectur 4×4</strong> (com base de utilizadores já formada) e divulgada nos canais Tectur. Sem investir em infraestrutura, o município ganha um novo produto turístico digital, sustentável e com identidade própria.</p>
        </div>
      </section>

      <div className="container section">
        <div className="section-header">
          <h2>Porquê Aderir</h2>
          <div className="underline"></div>
          <p className="muted">Seis razões estratégicas para o teu município apostar em rotas 4×4 oficiais Tectur.</p>
        </div>
        <div className="grid grid-2">
          {[
            { icon: '🎯', t: 'Turismo qualificado', d: 'Turistas 4×4 têm poder de compra acima da média, viajam em grupo, pernoitam no destino e valorizam experiências autênticas. Não são turismo de massas — são visitantes exigentes e respeitadores.' },
            { icon: '🌱', t: 'Dinamização do território', d: 'Levamos gente aos concelhos rurais e do interior — restauração local, alojamento, artesanato e produtos endógenos beneficiam diretamente. Combate à sazonalidade e à desertificação do interior.' },
            { icon: '🛡️', t: 'Melhor gestão do turismo de natureza', d: 'Canalizamos os visitantes para trilhos autorizados, com POIs bem definidos. Reduz o off-trail selvagem, protege áreas sensíveis e mantém a atividade dentro das regras de conservação.' },
            { icon: '📊', t: 'Monitorização e dados reais', d: 'A app Tectur regista quem passa, quando, onde e por quanto tempo. Fornecemos relatórios anónimos à autarquia — dados para planeamento turístico e para candidaturas a fundos europeus.' },
            { icon: '💰', t: 'Retorno económico local mensurável', d: 'Cada visitante 4×4 gera receita direta no comércio, restauração e alojamento. Damos fluxo turístico onde não havia — sem gastar em obras, sinalética física ou infraestrutura.' },
            { icon: '📢', t: 'Divulgação nacional e internacional', d: 'A rota do teu concelho passa a existir na comunidade Tectur (newsletter, YouTube, canais sociais). Alcance imediato a milhares de entusiastas 4×4 em Portugal e Europa.' },
          ].map((b, i) => (
            <div key={i} className="benefit">
              <span className="benefit-icon" style={{ fontSize: 28 }}>{b.icon}</span>
              <div>
                <h4>{b.t}</h4>
                <p className="muted small">{b.d}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="container section" data-testid="mun-tech-section">
        <div className="section-header">
          <h2>Tecnologia Incluída</h2>
          <div className="underline"></div>
          <p className="muted">O que o município passa a ter sem investir em software, servidores ou equipas técnicas.</p>
        </div>
        <div className="grid grid-2">
          {[
            { icon: '🔥', t: 'Alertas IPMA de risco de incêndio', d: 'Notificações automáticas para utilizadores em rota e para o município sempre que o risco de incêndio sobe no concelho — dados oficiais IPMA em tempo real.' },
            { icon: '🆘', t: 'Alertas SOS na rota', d: 'Botão de emergência dentro da app envia coordenadas GPS + tipo de ocorrência para o município e para a Tectur — resposta rápida em zonas isoladas onde o 112 não conhece o local exacto.' },
            { icon: '⚠️', t: 'Avisos de problemas na rota', d: 'Qualquer utilizador reporta obstáculos (trilho cortado, ponte partida, deslizamento) directamente pela app. O município recebe o alerta com foto e GPS, valida e a rota fica sinalizada até resolução.' },
            { icon: '📊', t: 'Dashboard web de gestão', d: 'Painel privado para o município: gerir as rotas do concelho, ver quem usa, quando e onde, exportar relatórios (KML/GPX/PDF/XLSX) para candidaturas, planos turísticos e reuniões de câmara.' },
            { icon: '📱', t: 'App Tectur 4×4 Android', d: 'Aplicação para tablet ou telemóvel em viatura — navegação turn-by-turn nas rotas oficiais, POIs com foto e narrativa, modo offline, velocímetro GPS e integração com todos os dados do município.' },
            { icon: '🗄️', t: 'Dados centralizados', d: 'Toda a informação recolhida (rotas percorridas, tempos, ocorrências, alertas, feedback) fica acessível no dashboard web e na app Android — histórico completo para o município consultar sempre que precisar.' },
          ].map((b, i) => (
            <div key={i} className="benefit" data-testid={`mun-tech-${i}`}>
              <span className="benefit-icon" style={{ fontSize: 28 }}>{b.icon}</span>
              <div>
                <h4>{b.t}</h4>
                <p className="muted small">{b.d}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="visual-strip" data-testid="mun-visual-strip">
        <div className="visual-strip-tile" style={{ backgroundImage: `url(${IMG.miradouro})` }}>
          <span className="visual-strip-caption">Miradouros escondidos</span>
        </div>
        <div className="visual-strip-tile" style={{ backgroundImage: `url(${IMG.aldeia})` }}>
          <span className="visual-strip-caption">Aldeias históricas do interior</span>
        </div>
        <div className="visual-strip-tile" style={{ backgroundImage: `url(${IMG.convoy})` }}>
          <span className="visual-strip-caption">Trilhos rurais e caminhos florestais</span>
        </div>
        <div className="visual-strip-tile" style={{ backgroundImage: `url(${IMG.aerial})` }}>
          <span className="visual-strip-caption">Paisagens únicas de Portugal</span>
        </div>
      </div>

      <div id="mun-como" className="container section">
        <div className="section-header">
          <h2>Como Funciona</h2>
          <div className="underline"></div>
          <p className="muted">Do primeiro reconhecimento à publicação — em 4 fases claras.</p>
        </div>
        <div className="grid grid-4">
          {[
            { n: 1, t: 'Reconhecimento', d: 'A Tectur desloca-se ao território, testa os trilhos, identifica pontos de interesse e valida acessibilidade.' },
            { n: 2, t: 'Curadoria', d: 'Desenhamos o traçado em conjunto com a autarquia e postos de turismo — respeitando conservação e propriedade.' },
            { n: 3, t: 'Mapeamento', d: 'Produção técnica GPX/KML profissional, fotografia de referência, textos e sinalética para cada POI.' },
            { n: 4, t: 'Publicação', d: 'Rota vai online na app Tectur 4×4. Divulgação nos canais Tectur (newsletter · YouTube · redes sociais).' },
          ].map(s => (
            <div key={s.n} className="step">
              <div className="step-num">{s.n}</div>
              <h3>{s.t}</h3>
              <p className="muted small">{s.d}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="container section">
        <div className="section-header">
          <h2>O Que o Município Recebe</h2>
          <div className="underline"></div>
        </div>
        <div className="grid grid-2">
          {[
            { icon: '🗺️', t: 'Rota completa GPX/KML', d: 'Traçado técnico profissional, pronto para carregar em qualquer GPS ou app de navegação.' },
            { icon: '📱', t: 'Publicação na app Tectur 4×4', d: 'Visibilidade imediata para a comunidade já ativa de utilizadores off-road em Portugal e Europa.' },
            { icon: '🌐', t: 'Ficha dedicada na plataforma Tectur', d: 'Página com identidade do município, POIs, fotos e contactos de alojamento e restauração locais.' },
            { icon: '📸', t: 'Fotografia e conteúdo multimédia', d: 'Fotos originais dos trilhos e POIs, textos descritivos otimizados para SEO turístico.' },
            { icon: '📢', t: 'Divulgação nos canais Tectur', d: 'Newsletter, YouTube e redes sociais — audiência qualificada de turistas 4×4.' },
            { icon: '🤝', t: 'Formação da equipa municipal', d: 'Sessão opcional para posto de turismo saber acompanhar visitantes com a app Tectur (extra).' },
          ].map((b, i) => (
            <div key={i} className="benefit">
              <span className="benefit-icon">{b.icon}</span>
              <div>
                <h4>{b.t}</h4>
                <p className="muted small">{b.d}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="section banner" style={{ backgroundImage: `linear-gradient(rgba(0,0,0,0.7), rgba(0,0,0,0.9)), url(${IMG.aerial})` }}>
        <div className="container banner-inner">
          <span className="pretitle">Turismo Sustentável · Território Ativo · Economia Local</span>
          <h2>Um novo produto turístico<br />sem betão, sem obras, sem impacto.</h2>
          <p>O teu concelho passa a existir no mapa 4×4 turístico de Portugal. Turistas qualificados param no restaurante do centro, pernoitam no alojamento local, compram artesanato. Sem infraestrutura nova.</p>
          <button className="btn" onClick={() => document.getElementById('mun-form').scrollIntoView({ behavior: 'smooth' })}>Falar com a Tectur</button>
        </div>
      </div>

      <div className="container section">
        <MunicipiosTestimonials />
      </div>

      <div id="mun-form" className="container section">
        <div className="section-header">
          <h2>Contactar Equipa Tectur</h2>
          <div className="underline"></div>
          <p className="muted">Preenche este formulário e voltamos ao teu contacto em 48h com uma proposta preliminar.</p>
        </div>
        <form className="form-panel" onSubmit={submit} data-testid="mun-form">
          <div className="grid grid-2">
            <div className="form-row"><label>Município</label><input required data-testid="mun-municipality" value={form.municipality} onChange={e => setForm({ ...form, municipality: e.target.value })} placeholder="Ex: Câmara Municipal de …" /></div>
            <div className="form-row"><label>Responsável</label><input required data-testid="mun-contact" value={form.contact_name} onChange={e => setForm({ ...form, contact_name: e.target.value })} /></div>
            <div className="form-row"><label>Cargo</label><input required data-testid="mun-position" value={form.position} onChange={e => setForm({ ...form, position: e.target.value })} placeholder="Ex: Vereador do Turismo" /></div>
            <div className="form-row"><label>Departamento</label><input data-testid="mun-department" value={form.department} onChange={e => setForm({ ...form, department: e.target.value })} placeholder="Ex: Turismo · Cultura · Ambiente" /></div>
            <div className="form-row"><label>Email institucional</label><input type="email" required data-testid="mun-email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} /></div>
            <div className="form-row"><label>Telefone</label><input type="tel" data-testid="mun-phone" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} /></div>
          </div>
          <div className="form-row">
            <label>Tipo de rota pretendida</label>
            <select data-testid="mun-route" value={form.route_type} onChange={e => setForm({ ...form, route_type: e.target.value })}>
              <option>Todo-terreno turístico</option>
              <option>Navigation Challenge (rally paper)</option>
              <option>Combinada — TT + interpretação cultural</option>
              <option>Outra</option>
            </select>
          </div>
          <div className="form-row"><label>Contexto e objetivos</label><textarea rows="4" data-testid="mun-message" value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} placeholder="Conta-nos o território, orçamento aproximado, prazos, objetivos turísticos e outros dados relevantes…"></textarea></div>
          <button className="btn" type="submit" data-testid="mun-submit">Enviar Contacto</button>
          {status && <p className="small muted" style={{ marginTop: 12 }} data-testid="mun-status">{status}</p>}
          {error && <p className="small" style={{ color: '#ff6b1a', marginTop: 12 }} data-testid="mun-error">{error}</p>}
        </form>
      </div>
    </section>
  );
}

function PasswordInput({ value, onChange, required, minLength, placeholder, autoComplete, "data-testid": testId, ...rest }) {
  const [show, setShow] = useState(false);
  const tid = testId || 'pw';
  return (
    <div style={{ position: 'relative', display: 'block' }}>
      <input
        {...rest}
        type={show ? 'text' : 'password'}
        required={required}
        minLength={minLength}
        placeholder={placeholder}
        autoComplete={autoComplete}
        data-testid={tid}
        value={value}
        onChange={onChange}
        style={{ paddingRight: 42, width: '100%', boxSizing: 'border-box' }}
      />
      <button
        type="button"
        onClick={() => setShow(s => !s)}
        aria-label={show ? 'Ocultar password' : 'Mostrar password'}
        title={show ? 'Ocultar password' : 'Mostrar password'}
        data-testid={`${tid}-toggle`}
        tabIndex={-1}
        style={{
          position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)',
          background: 'transparent', border: 0, color: '#bbb', cursor: 'pointer',
          padding: 6, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          lineHeight: 0,
        }}
      >
        {show ? (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.94 10.94 0 0 1 12 20c-7 0-11-8-11-8a19.77 19.77 0 0 1 4.22-5.32"/><path d="M9.9 4.24A10.94 10.94 0 0 1 12 4c7 0 11 8 11 8a19.77 19.77 0 0 1-3.13 3.93"/><path d="M14.12 14.12a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
        ) : (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
        )}
      </button>
    </div>
  );
}

function Login({ onLoggedIn }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotMsg, setForgotMsg] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const user = await apiPost('/auth/login', { email, password });
      onLoggedIn(user);
    } catch (err) {
      setError(err.message || 'Credenciais inválidas.');
    } finally {
      setLoading(false);
    }
  };
  const submitForgot = async (e) => {
    e.preventDefault();
    setForgotMsg(''); setForgotLoading(true);
    try {
      await apiPost('/auth/forgot-password', { email: forgotEmail || email });
      setForgotMsg('Se o email existir na base de dados, receberás um link de reset em breve. Verifica a caixa de entrada e o spam.');
    } catch (err) {
      setForgotMsg('Se o email existir na base de dados, receberás um link de reset em breve.');
    } finally {
      setForgotLoading(false);
    }
  };
  return (
    <section className="section login-section" style={{ backgroundImage: `linear-gradient(rgba(0,0,0,0.7), rgba(0,0,0,0.9)), url(${IMG.heroJipe})` }}>
      <div className="login-box">
        <div className="logo center"><img src={LOGO_URL} alt="Tectur" /></div>
        <h2>GESTÃO TECTUR</h2>
        <div className="underline center"></div>
        <p className="muted small center">Acede ao backoffice</p>
        {!showForgot ? (
          <form onSubmit={submit} data-testid="login-form">
            <div className="form-row"><label>Email</label><input type="email" placeholder="tectur@tectur.pt" required data-testid="login-email" value={email} onChange={e => setEmail(e.target.value)} /></div>
            <div className="form-row"><label>Password</label><PasswordInput required data-testid="login-password" value={password} onChange={e => setPassword(e.target.value)} /></div>
            <label className="check-row"><input type="checkbox" /><span>Manter sessão iniciada</span></label>
            {error && <p className="small" style={{ color: '#ff6b1a', margin: '8px 0' }} data-testid="login-error">{error}</p>}
            <button className="btn btn-block" type="submit" disabled={loading} data-testid="login-submit">{loading ? 'A entrar…' : 'Entrar'}</button>
            <a
              className="link small center-block"
              onClick={() => { setForgotEmail(email); setForgotMsg(''); setShowForgot(true); }}
              data-testid="forgot-link"
              style={{ cursor: 'pointer' }}
            >Esqueci-me da password</a>
          </form>
        ) : (
          <form onSubmit={submitForgot} data-testid="forgot-form">
            <p className="small muted" style={{ marginBottom: 12 }}>
              Introduz o email da tua conta de gestão. Se existir, enviamos-te um link de reset válido por 60 minutos.
            </p>
            <div className="form-row"><label>Email</label><input type="email" required data-testid="forgot-email" value={forgotEmail} onChange={e => setForgotEmail(e.target.value)} placeholder="tectur@tectur.pt" /></div>
            {forgotMsg && <p className="small" style={{ color: '#7ee27e', margin: '8px 0' }} data-testid="forgot-msg">{forgotMsg}</p>}
            <button className="btn btn-block" type="submit" disabled={forgotLoading} data-testid="forgot-submit">{forgotLoading ? 'A enviar…' : 'Enviar link de reset'}</button>
            <a
              className="link small center-block"
              onClick={() => { setShowForgot(false); setForgotMsg(''); }}
              data-testid="forgot-back"
              style={{ cursor: 'pointer' }}
            >← Voltar ao login</a>
          </form>
        )}
      </div>
    </section>
  );
}

function ResetPassword({ onNav }) {
  const [token] = useState(() => new URLSearchParams(window.location.search).get('token') || '');
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setErr(''); setMsg('');
    if (!token) { setErr('Link inválido.'); return; }
    if (pw !== pw2) { setErr('As passwords não coincidem.'); return; }
    if (pw.length < 8) { setErr('A password precisa de pelo menos 8 caracteres.'); return; }
    setLoading(true);
    try {
      await apiPost('/auth/reset-password', { token, new_password: pw });
      setMsg('Password alterada. Vais ser reencaminhado para o login.');
      setDone(true);
      setTimeout(() => onNav('gestao'), 2200);
    } catch (er) {
      setErr(er.message || 'Não foi possível redefinir a password.');
    } finally {
      setLoading(false);
    }
  };
  return (
    <section className="section login-section" style={{ backgroundImage: `linear-gradient(rgba(0,0,0,0.7), rgba(0,0,0,0.9)), url(${IMG.heroJipe})` }}>
      <div className="login-box">
        <div className="logo center"><img src={LOGO_URL} alt="Tectur" /></div>
        <h2>NOVA PASSWORD</h2>
        <div className="underline center"></div>
        <p className="muted small center">Escolhe a tua nova password de gestão</p>
        {!token ? (
          <p className="small" style={{ color: '#ff6b1a', textAlign: 'center' }} data-testid="reset-no-token">
            Link inválido. Volta a pedir um reset em <a onClick={() => onNav('gestao')} style={{ color: '#ff6b1a', cursor: 'pointer' }}>/gestao</a>.
          </p>
        ) : (
          <form onSubmit={submit} data-testid="reset-form">
            <div className="form-row"><label>Nova password (mín. 8 caracteres)</label><PasswordInput required minLength="8" value={pw} onChange={e => setPw(e.target.value)} data-testid="reset-pw" /></div>
            <div className="form-row"><label>Confirma a nova password</label><PasswordInput required minLength="8" value={pw2} onChange={e => setPw2(e.target.value)} data-testid="reset-pw2" /></div>
            {err && <p className="small" style={{ color: '#ff6b1a', margin: '8px 0' }} data-testid="reset-err">{err}</p>}
            {msg && <p className="small" style={{ color: '#7ee27e', margin: '8px 0' }} data-testid="reset-msg">{msg}</p>}
            <button className="btn btn-block" type="submit" disabled={loading || done} data-testid="reset-submit">{loading ? 'A guardar…' : (done ? 'Concluído' : 'Guardar nova password')}</button>
          </form>
        )}
      </div>
    </section>
  );
}

// YouTube IFrame API — load once
let _ytApiPromise = null;

// Devolve a melhor qualidade YouTube consoante a ligação de rede.
// IMPORTANTE: o vq= no URL do iframe SÓ aceita ['small','medium','large','hd720','hd1080'].
// 'highres' só é válido via JS API — se for passado no URL, o YT ignora e cai em auto (~144p).
// Por isso este helper devolve valores separados para URL vs JS API.
function getPreferredQuality(target = 'jsapi') {
  try {
    const c = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (c && c.saveData) return target === 'url' ? 'medium' : 'medium';
    const t = c && c.effectiveType;
    if (t === 'slow-2g' || t === '2g') return 'medium';
    if (t === '3g') return 'hd720';
    // 4g, wifi, ethernet ou desconhecido → máximo
    return target === 'url' ? 'hd1080' : 'highres';
  } catch (e) {
    return target === 'url' ? 'hd1080' : 'highres';
  }
}

function loadYouTubeAPI() {
  if (_ytApiPromise) return _ytApiPromise;
  _ytApiPromise = new Promise((resolve) => {
    if (window.YT && window.YT.Player) return resolve(window.YT);
    const prev = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      if (typeof prev === 'function') prev();
      resolve(window.YT);
    };
    const tag = document.createElement('script');
    tag.src = 'https://www.youtube.com/iframe_api';
    document.head.appendChild(tag);
  });
  return _ytApiPromise;
}

function YouTubePlayer({ videoId, title }) {
  const containerRef = React.useRef(null);
  const playerRef = React.useRef(null);
  const domId = React.useMemo(() => `yt-${videoId}-${Math.random().toString(36).slice(2, 8)}`, [videoId]);

  useEffect(() => {
    let cancelled = false;
    loadYouTubeAPI().then((YT) => {
      if (cancelled || !containerRef.current) return;
      playerRef.current = new YT.Player(domId, {
        videoId,
        width: 1920,
        height: 1080,
        playerVars: {
          autoplay: 1, mute: 1, loop: 1, playlist: videoId,
          controls: 1, modestbranding: 1, rel: 0, playsinline: 1, iv_load_policy: 3,
          hd: 1, vq: getPreferredQuality('url'),
          origin: window.location.origin,
        },
        events: {
          onReady: (e) => {
            try {
              e.target.mute();
              const q = getPreferredQuality();
              // Método 1: setPlaybackQualityRange (undocumented, força qualidade entre min e max)
              try { e.target.setPlaybackQualityRange && e.target.setPlaybackQualityRange('hd1080', 'hd2160'); } catch (_) { /* noop */ }
              // Método 2: setPlaybackQuality (deprecated mas ainda respeitado como sugestão)
              try { e.target.setPlaybackQuality(q); } catch (_) { /* noop */ }
              e.target.playVideo();
              // Reafirma depois do buffer inicial
              setTimeout(() => {
                try { e.target.setPlaybackQualityRange && e.target.setPlaybackQualityRange('hd1080', 'hd2160'); } catch (_) {}
                try { e.target.setPlaybackQuality(q); } catch (_) {}
              }, 1500);
            } catch (err) { /* noop */ }
          },
          onStateChange: (e) => {
            // If the video ends (loop should prevent, but as backup)
            if (e.data === 0) { try { e.target.playVideo(); } catch (err) { /* noop */ } }
            // Sempre que muda para playing (1) ou buffering (3), reafirma a qualidade preferida.
            if (e.data === 1 || e.data === 3) {
              try { e.target.setPlaybackQuality(getPreferredQuality()); } catch (_) { /* noop */ }
            }
          },
          onPlaybackQualityChange: (e) => {
            // Se o YT baixou automaticamente, tentamos voltar a subir para o preferido.
            const want = getPreferredQuality();
            const highTiers = ['hd1080', 'hd1440', 'hd2160', 'highres'];
            if (e && e.data && want === 'highres' && !highTiers.includes(e.data)) {
              try { e.target.setPlaybackQuality('highres'); } catch (_) { /* noop */ }
            } else if (e && e.data && e.data !== want && (want === 'hd720')) {
              try { e.target.setPlaybackQuality(want); } catch (_) { /* noop */ }
            }
          },
        },
      });
    });
    return () => {
      cancelled = true;
      try { playerRef.current && playerRef.current.destroy(); } catch (e) { /* ignore */ }
    };
  }, [videoId, domId]);

  return (
    <div className="video-wrap" data-testid={`video-${videoId}`} title={title}>
      <div id={domId} ref={containerRef} />
    </div>
  );
}

function VideosCarousel({ videos }) {
  const [pair, setPair] = useState(0);
  const list = videos && videos.length ? videos : [];
  const total = list.length;
  const slots = 2;

  useEffect(() => {
    if (total <= slots) return;
    const numPairs = Math.ceil(total / slots);
    const t = setInterval(() => setPair(p => (p + 1) % numPairs), 30000);
    return () => clearInterval(t);
  }, [total]);

  if (total === 0) return <p className="muted">A carregar vídeos…</p>;

  const start = (pair * slots) % total;
  const showing = [];
  for (let i = 0; i < Math.min(slots, total); i++) {
    showing.push(list[(start + i) % total]);
  }
  const numPairs = Math.max(1, Math.ceil(total / slots));

  return (
    <>
      <div className="videos-grid videos-grid-2" data-testid="videos-grid">
        {showing.map((v, i) => (
          <YouTubePlayer key={`${pair}-${v.id}-${i}`} videoId={v.id} title={v.title} />
        ))}
      </div>
      {numPairs > 1 && (
        <div className="videos-dots" data-testid="videos-dots">
          {Array.from({ length: numPairs }).map((_, i) => (
            <button key={i} className={`videos-dot ${i === pair ? 'active' : ''}`} onClick={() => setPair(i)} aria-label={`Ir para par ${i + 1}`} />
          ))}
        </div>
      )}
    </>
  );
}

function VideosManager() {
  const [cfg, setCfg] = useState({ youtube_channel_id: '', youtube_videos_limit: 6 });
  const [previewItems, setPreviewItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');

  const loadCfg = useCallback(async () => {
    try {
      const c = await apiGet('/admin/config');
      setCfg({
        youtube_channel_id: c.youtube_channel_id || '',
        youtube_videos_limit: c.youtube_videos_limit || 6,
      });
      const pub = await apiGet('/config/homepage-videos');
      setPreviewItems(pub.items || []);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadCfg(); }, [loadCfg]);

  const save = async () => {
    setSaving(true); setStatus(''); setError('');
    try {
      await apiPut('/admin/config', {
        youtube_channel_id: cfg.youtube_channel_id.trim() || null,
        youtube_videos_limit: parseInt(cfg.youtube_videos_limit) || 6,
      });
      // refresh cache and preview
      const res = await apiPost('/admin/config/refresh-videos', {});
      setPreviewItems(res.items || []);
      setStatus(`Guardado. ${res.count} vídeos sincronizados do canal.`);
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  const refresh = async () => {
    setRefreshing(true); setStatus(''); setError('');
    try {
      const res = await apiPost('/admin/config/refresh-videos', {});
      setPreviewItems(res.items || []);
      setStatus(`Atualizado. ${res.count} vídeos sincronizados.`);
    } catch (e) { setError(e.message); }
    finally { setRefreshing(false); }
  };

  if (loading) return <p className="muted">A carregar…</p>;

  return (
    <div className="form-panel" data-testid="videos-manager">
      <h3>Vídeos da Homepage · sync automático do YouTube</h3>
      <p className="muted small">
        Cola aqui o <strong>Channel ID</strong> do canal YouTube da Tectur (começa por <code>UC…</code>). O site puxa os últimos vídeos automaticamente via RSS (cache 1h). Sempre que carregares um vídeo novo no YouTube, ele aparece aqui sem tocar em nada.
      </p>
      <div className="form-row">
        <label>YouTube Channel ID</label>
        <input data-testid="cfg-channel-id" value={cfg.youtube_channel_id} onChange={e => setCfg({ ...cfg, youtube_channel_id: e.target.value })} placeholder="UCSnp596TtEk7KCgfAGY0GDQ" />
      </div>
      <div className="form-row">
        <label>Nº de vídeos a mostrar (1–15)</label>
        <input type="number" min="1" max="15" data-testid="cfg-videos-limit" value={cfg.youtube_videos_limit} onChange={e => setCfg({ ...cfg, youtube_videos_limit: e.target.value })} />
      </div>
      <div className="form-nav">
        <button className="btn" onClick={save} disabled={saving} data-testid="videos-save">{saving ? 'A guardar…' : 'Guardar & sincronizar'}</button>
        <button className="btn btn-outline" onClick={refresh} disabled={refreshing} data-testid="videos-refresh">{refreshing ? 'A atualizar…' : 'Atualizar cache agora'}</button>
      </div>
      {status && <p className="small muted" style={{ marginTop: 12 }} data-testid="videos-status">{status}</p>}
      {error && <p className="small" style={{ color: '#ff6b1a', marginTop: 12 }} data-testid="videos-error">{error}</p>}

      <h4 style={{ marginTop: 32, marginBottom: 12 }}>Pré-visualização ({previewItems.length})</h4>
      <ul className="feat-list" data-testid="videos-preview">
        {previewItems.map(v => (
          <li key={v.id}>
            <a className="link" href={`https://www.youtube.com/watch?v=${v.id}`} target="_blank" rel="noreferrer">{v.title}</a>
            <span className="muted small"> · {v.id}</span>
          </li>
        ))}
        {previewItems.length === 0 && <li className="muted">Sem vídeos ainda — verifica o Channel ID.</li>}
      </ul>
      <p className="small muted" style={{ marginTop: 12 }}>
        Como obter o Channel ID: no YouTube, abre o canal → clica em «…» → «Partilhar canal» → «Copiar ID do canal». Ou vai a <code>https://www.youtube.com/@tectur1468</code>, vê o código-fonte e procura por <code>channel_id=</code>.
      </p>
    </div>
  );
}

function TestemunhosManager() {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ pending: 0, approved: 0, rejected: 0 });
  const [status, setStatus] = useState('pending');
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const [trips, setTrips] = useState([]);
  const load = useCallback(async () => {
    try {
      const r = await apiGet(`/admin/testimonials?status=${status}`);
      setItems(r.items || []); setCounts(r.counts || r.stats || counts);
    } catch (e) { setErr(e.message); }
  }, [status]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { apiGet('/admin/trips').then(r => setTrips(r.items || [])).catch(() => setTrips([])); }, []);

  const act = async (id, action) => {
    setMsg(''); setErr('');
    try {
      if (action === 'delete') await apiDelete(`/admin/testimonials/${id}`);
      else await apiPost(`/admin/testimonials/${id}/${action}`, {});
      setMsg(action === 'approve' ? 'Aprovado.' : action === 'reject' ? 'Rejeitado.' : 'Eliminado.');
      load();
    } catch (e) { setErr(e.message); }
  };

  const assignTrip = async (id, trip_id) => {
    setMsg(''); setErr('');
    try {
      await apiPut(`/admin/testimonials/${id}/trip`, { trip_id: trip_id || null });
      setMsg(trip_id ? 'Associado ao passeio.' : 'Associação removida.');
      load();
    } catch (e) { setErr(e.message); }
  };

  return (
    <div data-testid="testemunhos-manager">
      <div className="admin-tabs" style={{ marginBottom: 12 }}>
        <button className={`admin-tab ${status === 'pending' ? 'active' : ''}`} data-testid="test-filter-pending" onClick={() => setStatus('pending')}>Pendentes ({counts.pending})</button>
        <button className={`admin-tab ${status === 'approved' ? 'active' : ''}`} data-testid="test-filter-approved" onClick={() => setStatus('approved')}>Aprovados ({counts.approved})</button>
        <button className={`admin-tab ${status === 'rejected' ? 'active' : ''}`} data-testid="test-filter-rejected" onClick={() => setStatus('rejected')}>Rejeitados ({counts.rejected})</button>
      </div>
      {msg && <p className="small muted">{msg}</p>}
      {err && <p className="small" style={{ color: '#ff6b1a' }}>{err}</p>}
      <div className="grid grid-2">
        {items.map(t => (
          <div key={t.id} className="card"><div className="card-body">
            <h3>{t.company || t.company_name || t.author_name || t.author || 'Anónimo'}</h3>
            <p className="muted small">Origem: <strong>{t.source || '—'}</strong> · {t.created_at ? new Date(t.created_at).toLocaleDateString('pt-PT') : '—'}</p>
            <p>&ldquo;{t.text}&rdquo;</p>
            {(t.author_name || t.author) && <p className="small muted">— {t.author_name || t.author}{t.position ? `, ${t.position}` : ''}</p>}

            <div className="form-row" style={{ marginTop: 10 }}>
              <label className="small">🚙 Passeio associado (aparece em &ldquo;Passeios Realizados&rdquo;)</label>
              <select value={t.trip_id || ''} onChange={e => assignTrip(t.id, e.target.value)} data-testid={`test-trip-${t.id}`}>
                <option value="">— Nenhum —</option>
                {trips.map(tr => <option key={tr.id} value={tr.id}>{tr.title}{tr.date_label ? ` · ${tr.date_label}` : ''}</option>)}
              </select>
            </div>

            <div className="form-nav" style={{ marginTop: 8, gap: 8 }}>
              {t.status !== 'approved' && <button className="btn" data-testid={`test-approve-${t.id}`} onClick={() => act(t.id, 'approve')}>Aprovar</button>}
              {t.status !== 'rejected' && <button className="btn btn-outline" data-testid={`test-reject-${t.id}`} onClick={() => act(t.id, 'reject')}>Rejeitar</button>}
              <button className="btn btn-outline" style={{ color: '#ff6b1a' }} data-testid={`test-del-${t.id}`} onClick={() => { if (window.confirm('Eliminar definitivamente?')) act(t.id, 'delete'); }}>Eliminar</button>
            </div>
          </div></div>
        ))}
        {items.length === 0 && <p className="muted">Sem testemunhos {status}.</p>}
      </div>
    </div>
  );
}

function InscricoesManager() {
  const [items, setItems] = useState([]);
  const [trips, setTrips] = useState([]);
  const [status, setStatus] = useState('all');
  const [tripId, setTripId] = useState('all');
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  useEffect(() => { apiGet('/admin/trips').then(r => setTrips(r.items || [])).catch(() => {}); }, []);
  const load = useCallback(async () => {
    try {
      const qs = [];
      if (status !== 'all') qs.push(`status=${status}`);
      if (tripId !== 'all') qs.push(`trip_id=${tripId}`);
      const r = await apiGet(`/admin/inscriptions${qs.length ? '?' + qs.join('&') : ''}`);
      setItems(r.items || []);
    } catch (e) { setErr(e.message); }
  }, [status, tripId]);
  useEffect(() => { load(); }, [load]);

  const tripTitle = (id) => trips.find(t => t.id === id)?.title || id;

  const confirm = async (id) => {
    setMsg(''); setErr('');
    try { await apiPost(`/admin/inscriptions/${id}/confirm-payment`, {}); setMsg('Pagamento confirmado. Email pronto para envio.'); load(); }
    catch (e) { setErr(e.message); }
  };
  const revert = async (id) => {
    if (!window.confirm('Reverter para NÃO pago? A inscrição volta ao estado pendente.')) return;
    setMsg(''); setErr('');
    try { await apiPost(`/admin/inscriptions/${id}/revert-payment`, {}); setMsg('Revertido para pendente.'); load(); }
    catch (e) { setErr(e.message); }
  };
  const mailOne = async (id) => {
    try {
      const t = await apiGet(`/admin/inscriptions/${id}/mail-template`);
      // Abre Gmail Web (funciona sem cliente de email configurado)
      const url = `https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(t.to)}&su=${encodeURIComponent(t.subject)}&body=${encodeURIComponent(t.body)}`;
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (e) { setErr(e.message); }
  };
  const mailBulk = async () => {
    if (tripId === 'all') { alert('Escolhe um passeio primeiro no filtro.'); return; }
    const trip = trips.find(t => t.id === tripId);
    const tripLabel = trip ? `${trip.title}${trip.date_label ? ` (${trip.date_label})` : ''}` : 'este passeio';
    if (!window.confirm(
      `Enviar o EMAIL FINAL DA INSCRIÇÃO (todos os detalhes do passeio + ponto de encontro + GPX + KML) a todos os inscritos PAGOS de:\n\n${tripLabel}\n\n` +
      `Cada inscrito recebe um link de download único para os ficheiros. Certifica-te que já carregaste o GPX/KML e definiste o ponto de encontro. Continuar?`
    )) return;
    setMsg(''); setErr('');
    try {
      const r = await apiPost(`/admin/trips/${tripId}/send-meeting-details`, {});
      const parts = [];
      if (r.has_meeting_point) parts.push('ponto de encontro');
      if (r.has_maps_url) parts.push('Google Maps');
      if (r.has_gpx) parts.push('GPX');
      if (r.has_kml) parts.push('KML');
      setMsg(`✅ Email final enviado para ${r.recipients} inscrito(s) pago(s). Inclui: ${parts.join(', ') || 'apenas texto do passeio'}. Verifica a caixa em ~1 minuto.`);
    } catch (e) { setErr(e.message); }
  };
  const del = async (id) => {
    if (!window.confirm('Eliminar inscrição?')) return;
    try { await apiDelete(`/admin/inscriptions/${id}`); load(); } catch (e) { setErr(e.message); }
  };

  return (
    <div data-testid="inscricoes-manager">
      <div className="admin-tabs" style={{ marginBottom: 8 }}>
        {['all', 'pending_payment', 'paid', 'cancelled'].map(s => (
          <button key={s} className={`admin-tab ${status === s ? 'active' : ''}`} data-testid={`ins-filter-${s}`} onClick={() => setStatus(s)}>{s === 'all' ? 'Todas' : s === 'pending_payment' ? 'Pendentes' : s === 'paid' ? 'Pagas' : 'Canceladas'}</button>
        ))}
      </div>
      <div className="form-nav" style={{ marginBottom: 12, gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <label className="small muted" style={{ marginRight: 4 }}>Passeio:</label>
        <select data-testid="ins-trip-filter" value={tripId} onChange={e => setTripId(e.target.value)} style={{ minWidth: 280, padding: '10px 12px', background: '#0a0a0a', color: '#fff', border: '1px solid var(--border)' }}>
          <option value="all">Todos os passeios ({trips.length})</option>
          {trips.map(t => <option key={t.id} value={t.id}>{t.title}{t.date_label ? ` · ${t.date_label}` : ''}</option>)}
        </select>
        {tripId !== 'all' && status === 'paid' && (
          <button className="btn btn-outline" onClick={mailBulk} data-testid="ins-mail-bulk" title="Envia via SMTP o email FINAL da inscrição (todos os detalhes do passeio + ponto de encontro + GPX + KML) a todos os inscritos pagos">✉️ Enviar email final (detalhes + GPX + KML) aos inscritos pagos</button>
        )}
        <span style={{ flex: 1 }} />
        <a className="btn" href={`${API}/admin/inscriptions/export/xlsx`} target="_blank" rel="noreferrer" data-testid="ins-export-xlsx">Exportar Excel</a>
        <a className="btn btn-outline" href={`${API}/admin/inscriptions/export/csv`} target="_blank" rel="noreferrer" data-testid="ins-export">CSV</a>
      </div>
      {msg && <p className="small muted">{msg}</p>}
      {err && <p className="small" style={{ color: '#ff6b1a' }}>{err}</p>}
      <div className="table-wrap">
        <table className="admin-table" data-testid="inscricoes-table">
          <thead><tr><th>Ref.</th><th>Passeio</th><th>Condutor</th><th>Email</th><th>Total</th><th>Estado</th><th>Data</th><th></th></tr></thead>
          <tbody>
            {items.map(i => (
              <tr key={i.id}>
                <td><strong>{i.reference}</strong></td>
                <td>{tripTitle(i.trip_id)}</td>
                <td>{i.driver_name}{i.team_name ? ` · ${i.team_name}` : ''}</td>
                <td>{i.driver_email}</td>
                <td>{Number(i.total || 0).toFixed(0)} €</td>
                <td><span className={`status-pill ${i.status}`}>{i.status === 'paid' ? 'Pago' : i.status === 'pending_payment' ? 'Pendente' : i.status === 'cancelled' ? 'Cancelada' : i.status}</span></td>
                <td>{new Date(i.created_at).toLocaleDateString('pt-PT')}</td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  {i.status === 'paid'
                    ? <>
                        <button className="btn" data-testid={`ins-mail-${i.id}`} onClick={() => mailOne(i.id)} title="Abrir Gmail com email de pagamento confirmado">✉️ Email</button>{' '}
                        <button className="btn btn-outline" data-testid={`ins-revert-${i.id}`} onClick={() => revert(i.id)}>↺ Reverter</button>
                      </>
                    : <>
                        <button className="btn btn-outline" data-testid={`ins-mail-${i.id}`} onClick={() => mailOne(i.id)} title="Abrir Gmail com dados de pagamento">✉️ Enviar dados</button>{' '}
                        <button className="btn" data-testid={`ins-confirm-${i.id}`} onClick={() => confirm(i.id)}>Confirmar</button>
                      </>}
                  {' '}
                  <button className="btn btn-outline" style={{ color: '#ff6b1a' }} data-testid={`ins-del-${i.id}`} onClick={() => del(i.id)}>×</button>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan="8" className="muted">Sem inscrições.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function LeadsManager() {
  const [source, setSource] = useState('team-building');
  const [items, setItems] = useState([]);
  const [err, setErr] = useState('');
  const load = useCallback(async () => {
    try { const r = await apiGet(`/admin/leads/${source}`); setItems(r.items || []); }
    catch (e) { setErr(e.message); }
  }, [source]);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, newStatus) => {
    try { await apiPut(`/admin/leads/${source}/${id}`, { status: newStatus }); load(); } catch (e) { setErr(e.message); }
  };

  return (
    <div data-testid="leads-manager">
      <div className="admin-tabs" style={{ marginBottom: 12 }}>
        <button className={`admin-tab ${source === 'team-building' ? 'active' : ''}`} data-testid="leads-tab-tb" onClick={() => setSource('team-building')}>Team Building</button>
        <button className={`admin-tab ${source === 'municipios' ? 'active' : ''}`} data-testid="leads-tab-mun" onClick={() => setSource('municipios')}>Municípios</button>
      </div>
      {err && <p className="small" style={{ color: '#ff6b1a' }}>{err}</p>}
      <div className="grid grid-2">
        {items.map(l => (
          <div key={l.id} className="card"><div className="card-body">
            <h3>{l.company || l.municipality || 'Sem nome'}</h3>
            <p className="small muted">{new Date(l.created_at).toLocaleDateString('pt-PT')} · <strong>{l.status || 'new'}</strong></p>
            <p><strong>{l.contact_name || l.responsible_name}</strong> · {l.email} · {l.phone}</p>
            {l.participants && <p className="small muted">Participantes: {l.participants} · Data pretendida: {l.date_preference || '—'}</p>}
            {l.population && <p className="small muted">População: {l.population} · Distrito: {l.district || '—'}</p>}
            {(l.objectives || l.message) && <p style={{ whiteSpace: 'pre-wrap' }}>{l.objectives || l.message}</p>}
            <div className="form-nav" style={{ gap: 8, marginTop: 8 }}>
              {['contacted', 'proposal', 'won', 'lost'].map(s => (
                <button key={s} className={`btn ${l.status === s ? '' : 'btn-outline'}`} data-testid={`lead-${l.id}-${s}`} onClick={() => setStatus(l.id, s)}>{s}</button>
              ))}
            </div>
          </div></div>
        ))}
        {items.length === 0 && <p className="muted">Sem leads {source}.</p>}
      </div>
    </div>
  );
}

function NewsletterManager() {
  const [section, setSection] = useState('campaigns');
  return (
    <div data-testid="newsletter-manager">
      <div className="admin-tabs" style={{ marginBottom: 12 }}>
        <button className={`admin-tab ${section === 'campaigns' ? 'active' : ''}`} onClick={() => setSection('campaigns')} data-testid="news-tab-campaigns">Campanhas</button>
        <button className={`admin-tab ${section === 'subs' ? 'active' : ''}`} onClick={() => setSection('subs')} data-testid="news-tab-subs">Subscritores</button>
      </div>
      {section === 'campaigns' ? <CampaignsSection /> : <SubscribersSection />}
    </div>
  );
}

function SubscribersSection() {
  const [items, setItems] = useState([]);
  const [count, setCount] = useState(0);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const fileRef = useRef(null);
  const load = useCallback(async () => {
    try { const r = await apiGet('/admin/newsletter/subscribers'); setItems(r.items || []); setCount(r.count || 0); }
    catch (e) { setErr(e.message); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const importCsv = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append('file', file);
    try {
      const r = await fetch(`${API}/admin/newsletter/import`, { method: 'POST', credentials: 'include', body: fd });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(_errMsg(d.detail, r.status));
      setMsg(`+${d.added} adicionados, ${d.skipped} ignorados.`); load();
    } catch (er) { setErr(er.message); }
    finally { if (fileRef.current) fileRef.current.value = ''; }
  };
  return (
    <div>
      <div className="form-nav" style={{ marginBottom: 12, gap: 8 }}>
        <span className="muted">Total: <strong>{count || items.length}</strong></span>
        <a className="btn btn-outline" href={`${API}/admin/newsletter/export`} target="_blank" rel="noreferrer" data-testid="news-export">Exportar CSV</a>
        <label className="btn btn-outline" style={{ cursor: 'pointer' }} data-testid="news-import-label">
          Importar CSV<input ref={fileRef} type="file" accept=".csv" style={{ display: 'none' }} onChange={importCsv} data-testid="news-import" />
        </label>
      </div>
      <p className="small muted">CSV com uma coluna <code>email</code> (opcional <code>name</code>). Aceita ficheiros exportados do Mailchimp, Gmail, Excel, etc.</p>
      {msg && <p className="small muted">{msg}</p>}
      {err && <p className="small" style={{ color: '#ff6b1a' }}>{err}</p>}
      <div className="table-wrap">
        <table className="admin-table">
          <thead><tr><th>Email</th><th>Nome</th><th>Origem</th><th>Ativo</th><th>Data</th></tr></thead>
          <tbody>
            {items.map(s => (
              <tr key={s.id}><td>{s.email}</td><td>{s.name || '—'}</td><td>{s.source}</td><td>{s.active ? 'Sim' : 'Não'}</td><td>{new Date(s.created_at).toLocaleDateString('pt-PT')}</td></tr>
            ))}
            {items.length === 0 && <tr><td colSpan="5" className="muted">Sem subscritores. Importa o teu CSV pelo botão acima.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function CampaignsSection() {
  const [items, setItems] = useState([]);
  const [trips, setTrips] = useState([]);
  const [smtp, setSmtp] = useState(null);
  const [editing, setEditing] = useState(null);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [sendingId, setSendingId] = useState(null);
  const load = useCallback(async () => {
    try {
      const r = await apiGet('/admin/newsletter/campaigns');
      setItems(r.items || []);
    } catch (e) { setErr(e.message); }
  }, []);
  useEffect(() => {
    load();
    apiGet('/admin/trips').then(r => setTrips(r.items || [])).catch(() => {});
    apiGet('/admin/config/smtp').then(setSmtp).catch(() => {});
  }, [load]);

  const [selTrip, setSelTrip] = useState('');
  const createFromTrip = async () => {
    if (!selTrip) { alert('Escolhe um passeio primeiro.'); return; }
    try {
      const c = await apiPost(`/admin/newsletter/campaigns/from-trip/${selTrip}`, {});
      setEditing(c); load();
    } catch (e) { setErr(e.message); }
  };
  const createFreeform = async () => {
    try {
      const c = await apiPost(`/admin/newsletter/campaigns`, {
        subject: 'Nova notícia Tectur',
        body_md: '',
        banner_url: '',
      });
      setEditing(c); load();
    } catch (e) { setErr(e.message); }
  };
  const del = async (id) => {
    if (!window.confirm('Eliminar rascunho?')) return;
    try { await apiDelete(`/admin/newsletter/campaigns/${id}`); load(); } catch (e) { setErr(e.message); }
  };
  const send = async (id) => {
    if (!window.confirm('Enviar campanha a TODOS os subscritores ativos? Envio a 200/hora, demora ~5-10 minutos para 100 subs. Confirma?')) return;
    setSendingId(id); setMsg(''); setErr('');
    try {
      const r = await apiPost(`/admin/newsletter/campaigns/${id}/send`, {});
      setMsg(`✅ Enviados ${r.sent}/${r.total} · Falhas: ${r.failed}`); load();
    } catch (e) { setErr(e.message); } finally { setSendingId(null); }
  };

  if (editing) return <CampaignEditor campaign={editing} onCancel={() => setEditing(null)} onSaved={() => { setEditing(null); load(); setMsg('Rascunho guardado.'); }} />;

  const smtpReady = smtp && smtp.smtp_password_set;
  return (
    <div>
      <div className="form-nav" style={{ marginBottom: 12, gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <select value={selTrip} onChange={e => setSelTrip(e.target.value)} data-testid="camp-trip-select" style={{ minWidth: 260, padding: '10px 12px', background: '#0a0a0a', color: '#fff', border: '1px solid var(--border)' }}>
          <option value="">— Escolher passeio de destaque —</option>
          {trips.filter(t => t.published).map(t => <option key={t.id} value={t.id}>{t.title}{t.date_label ? ` · ${t.date_label}` : ''}</option>)}
        </select>
        <button className="btn" onClick={createFromTrip} data-testid="camp-new">+ Nova campanha (a partir do passeio)</button>
        <button className="btn btn-outline" onClick={createFreeform} data-testid="camp-new-free">+ Nova campanha livre (notícia / imagem)</button>
      </div>
      {!smtpReady && (
        <p className="small" style={{ padding: 12, background: 'rgba(255,107,26,.08)', border: '1px solid rgba(255,107,26,.4)', color: '#ff6b1a' }}>
          ⚠️ SMTP ainda não configurado — vai a <strong>Definições → SMTP</strong> e adiciona a password para poder enviar campanhas.
        </p>
      )}
      {msg && <p className="small muted">{msg}</p>}
      {err && <p className="small" style={{ color: '#ff6b1a' }}>{err}</p>}
      <div className="table-wrap" style={{ marginTop: 12 }}>
        <table className="admin-table">
          <thead><tr><th>Assunto</th><th>Passeio</th><th>Estado</th><th>Criada</th><th>Enviada</th><th></th></tr></thead>
          <tbody>
            {items.map(c => (
              <tr key={c.id}>
                <td><strong>{c.subject}</strong></td>
                <td>{trips.find(t => t.id === c.featured_trip_id)?.title || '—'}</td>
                <td><span className={`status-pill ${c.status === 'sent' ? 'paid' : c.status === 'sending' ? 'pending_payment' : ''}`}>{c.status === 'sent' ? `Enviada · ${c.sent_count}` : c.status === 'sending' ? '⏳ A enviar' : 'Rascunho'}</span></td>
                <td>{new Date(c.created_at).toLocaleDateString('pt-PT')}</td>
                <td>{c.sent_at ? new Date(c.sent_at).toLocaleDateString('pt-PT') : '—'}</td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  {c.status === 'draft' && <button className="btn btn-outline" onClick={() => setEditing(c)} data-testid={`camp-edit-${c.id}`}>Editar</button>}{' '}
                  <a className="btn btn-outline" href={`${API}/admin/newsletter/campaigns/${c.id}/preview`} target="_blank" rel="noreferrer" data-testid={`camp-preview-${c.id}`}>Preview</a>{' '}
                  {c.status === 'draft' && smtpReady && <button className="btn" onClick={() => send(c.id)} disabled={sendingId === c.id} data-testid={`camp-send-${c.id}`}>{sendingId === c.id ? '⏳ A enviar...' : '📧 Enviar'}</button>}{' '}
                  {c.status === 'draft' && <button className="btn btn-outline" style={{ color: '#ff6b1a' }} onClick={() => del(c.id)} data-testid={`camp-del-${c.id}`}>×</button>}
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan="6" className="muted">Sem campanhas. Escolhe um passeio acima para criar a primeira.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function CampaignEditor({ campaign, onCancel, onSaved }) {
  const [f, setF] = useState({ subject: '', body_md: '', banner_url: '', featured_trip_id: '', ...campaign });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const save = async () => {
    setSaving(true); setErr(''); setMsg('');
    try {
      await apiPut(`/admin/newsletter/campaigns/${campaign.id}`, {
        subject: f.subject, body_md: f.body_md, banner_url: f.banner_url, featured_trip_id: f.featured_trip_id,
      });
      onSaved();
    } catch (e) { setErr(e.message); } finally { setSaving(false); }
  };
  const testSend = async () => {
    setTesting(true); setErr(''); setMsg('');
    try {
      // Guarda primeiro para o teste refletir alterações actuais
      await apiPut(`/admin/newsletter/campaigns/${campaign.id}`, {
        subject: f.subject, body_md: f.body_md, banner_url: f.banner_url, featured_trip_id: f.featured_trip_id,
      });
      const r = await apiPost(`/admin/newsletter/campaigns/${campaign.id}/test-send`, {});
      setMsg(`✅ Email de teste enviado para ${r.sent_to}. Verifica a caixa de entrada em ~1 minuto.`);
    } catch (e) { setErr(e.message); } finally { setTesting(false); }
  };
  return (
    <div className="form-panel" data-testid="campaign-editor">
      <div className="form-nav" style={{ marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
        <button className="btn btn-outline" onClick={onCancel} data-testid="camp-back">← Voltar</button>
        <button className="btn btn-outline" onClick={() => setShowPreview(!showPreview)} data-testid="camp-toggle-preview">{showPreview ? 'Ocultar preview' : 'Ver preview'}</button>
        <button className="btn" onClick={save} disabled={saving} data-testid="camp-save">{saving ? 'A guardar…' : 'Guardar rascunho'}</button>
        <a className="btn btn-outline" href={`${API}/admin/newsletter/campaigns/${campaign.id}/preview`} target="_blank" rel="noreferrer" data-testid="camp-open-preview">Preview em nova aba</a>
        <button className="btn btn-outline" onClick={testSend} disabled={testing} data-testid="camp-test-send" style={{ borderColor: '#4caf50', color: '#4caf50' }}>
          {testing ? '⏳ A enviar teste…' : '📧 Enviar teste (só para mim)'}
        </button>
      </div>
      {err && <p className="small" style={{ color: '#ff6b1a' }}>{err}</p>}
      {msg && <p className="small" style={{ color: '#4caf50', padding: '8px 12px', background: 'rgba(76,175,80,.08)', border: '1px solid rgba(76,175,80,.3)' }}>{msg}</p>}

      <div style={{ display: 'grid', gridTemplateColumns: showPreview ? '1fr 1fr' : '1fr', gap: 24 }}>
        <div>
          <div className="form-row">
            <label>Assunto do email</label>
            <input data-testid="camp-subject" value={f.subject} onChange={e => setF({ ...f, subject: e.target.value })} />
          </div>
          <ImageUpload label="Imagem principal (corpo do email · 600px de largura recomendado)" value={f.banner_url} onChange={v => setF({ ...f, banner_url: v })} testid="camp-banner" />
          <div className="form-row">
            <label>Texto adicional (opcional · Markdown: **negrito**, *itálico*, [link](url), listas com &quot;- &quot;)</label>
            <textarea rows="16" data-testid="camp-body" value={f.body_md} onChange={e => setF({ ...f, body_md: e.target.value })} style={{ fontFamily: 'ui-monospace,Consolas,monospace', fontSize: 13 }} placeholder="Deixa vazio se a imagem já contém toda a mensagem. O bloco de texto só aparece no email se aqui tiver alguma coisa." />
            <p className="small muted" style={{ marginTop: 6 }}>
              💡 <strong>Se a imagem já tem tudo o que queres comunicar</strong> (banner AI com texto, títulos, CTAs), deixa esta caixa <strong>vazia</strong> — o email é enviado só com a imagem + rodapé.
              Podes ainda usar <code>{'{{TRIP_URL}}'}</code> (link para o passeio) e <code>{'{{UNSUB_URL}}'}</code> (cancelar subscrição) se preencheres esta caixa.
            </p>
          </div>
        </div>
        {showPreview && (
          <div>
            <label className="small muted" style={{ display: 'block', marginBottom: 8 }}>Preview em tempo real</label>
            <iframe
              key={`${f.subject}-${(f.body_md || '').length}-${f.banner_url}`}
              title="preview"
              src={`${API}/admin/newsletter/campaigns/${campaign.id}/preview`}
              style={{ width: '100%', height: 700, background: '#0f0f0f', border: '1px solid var(--border)' }}
              data-testid="camp-iframe-preview"
            />
            <p className="small muted">Guarda o rascunho para o preview refletir as tuas alterações.</p>
          </div>
        )}
      </div>
    </div>
  );
}

function SmtpSettings() {
  const [cfg, setCfg] = useState(null);
  const [pw, setPw] = useState('');
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const [testing, setTesting] = useState(false);
  const [errors, setErrors] = useState([]);
  const loadErrors = () => {
    apiGet('/admin/email-errors').then(r => setErrors(r.items || [])).catch(() => {});
  };
  useEffect(() => {
    apiGet('/admin/config/smtp').then(c => {
      setCfg({
        smtp_host: c.smtp_host || 'mail.tectur.pt',
        smtp_port: c.smtp_port || 465,
        smtp_user: c.smtp_user || 'tectur@tectur.pt',
        smtp_use_ssl: c.smtp_use_ssl !== false,
        smtp_from_name: c.smtp_from_name || 'Tectur',
        smtp_password_set: !!c.smtp_password_set,
        test_recipient: c.test_recipient || '',
      });
    }).catch(e => setErr(e.message));
    loadErrors();
  }, []);
  const save = async () => {
    setMsg(''); setErr('');
    try {
      const payload = {
        smtp_host: cfg.smtp_host, smtp_port: parseInt(cfg.smtp_port) || 465,
        smtp_user: cfg.smtp_user, smtp_use_ssl: cfg.smtp_use_ssl,
        smtp_from_name: cfg.smtp_from_name || 'Tectur',
        test_recipient: cfg.test_recipient || null,
      };
      if (pw) payload.smtp_password = pw;
      const r = await apiPut('/admin/config/smtp', payload);
      setMsg('Guardado. ' + (r.smtp_password_set ? 'Password configurada ✓' : ''));
      setPw('');
      apiGet('/admin/config/smtp').then(c => setCfg(x => ({ ...x, ...c })));
    } catch (e) { setErr(e.message); }
  };
  const test = async () => {
    setMsg(''); setErr(''); setTesting(true);
    try {
      const body = cfg.test_recipient && cfg.test_recipient.trim() ? { to: cfg.test_recipient.trim() } : {};
      const r = await apiPost('/admin/config/smtp/test', body);
      if (r.ok) {
        setMsg(`✅ Email de teste enviado para ${r.sent_to}. Verifica a tua caixa (também no Spam).`);
      } else if (r.network_blocked) {
        setMsg('⚠️ ' + r.detail);
      } else {
        setErr('❌ ' + (_errMsg(r.detail, 500) || 'Erro desconhecido'));
      }
    } catch (e) { setErr('❌ ' + e.message); } finally { setTesting(false); loadErrors(); }
  };
  if (!cfg) return <p className="muted">A carregar…</p>;
  return (
    <>
      <h3>📧 SMTP · Servidor de envio de emails</h3>
      <p className="small muted" style={{ marginBottom: 16 }}>
        Configura o servidor SMTP do teu email <code>tectur@tectur.pt</code> para enviar emails automáticos
        (inscrições, confirmações de pagamento, newsletter). A password fica guardada apenas no servidor,
        nunca é devolvida à interface.
      </p>
      <div className="grid grid-2">
        <div className="form-row"><label>Servidor SMTP</label><input data-testid="smtp-host" value={cfg.smtp_host || ''} onChange={e => setCfg({ ...cfg, smtp_host: e.target.value })} placeholder="mail.tectur.pt" /></div>
        <div className="form-row"><label>Porta</label><input type="number" data-testid="smtp-port" value={cfg.smtp_port || 465} onChange={e => setCfg({ ...cfg, smtp_port: e.target.value })} placeholder="465" /></div>
        <div className="form-row"><label>Utilizador (email)</label><input data-testid="smtp-user" value={cfg.smtp_user || ''} onChange={e => setCfg({ ...cfg, smtp_user: e.target.value })} placeholder="tectur@tectur.pt" /></div>
        <div className="form-row"><label>Nome de remetente</label><input data-testid="smtp-fromname" value={cfg.smtp_from_name || 'Tectur'} onChange={e => setCfg({ ...cfg, smtp_from_name: e.target.value })} /></div>
        <div className="form-row" style={{ gridColumn: '1/-1' }}>
          <label>Password {cfg.smtp_password_set && <span style={{ color: '#4caf50', marginLeft: 8 }}>✓ Configurada</span>}</label>
          <PasswordInput data-testid="smtp-pw" value={pw} onChange={e => setPw(e.target.value)} placeholder={cfg.smtp_password_set ? '••••••••  (deixa em branco para manter a atual)' : 'Password de tectur@tectur.pt'} autoComplete="new-password" />
          <p className="small muted" style={{ marginTop: 6 }}>
            🔒 A password vai <strong>diretamente</strong> deste browser para o servidor via HTTPS. Nunca aparece em chats, logs ou frontend.
          </p>
        </div>
        <div className="form-row">
          <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input type="checkbox" checked={cfg.smtp_use_ssl !== false} onChange={e => setCfg({ ...cfg, smtp_use_ssl: e.target.checked })} data-testid="smtp-ssl" />
            Usar SSL (porta 465). Desativa para STARTTLS (porta 587).
          </label>
        </div>
      </div>
      <div className="form-nav" style={{ gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
        <button className="btn" onClick={save} data-testid="smtp-save">Guardar configuração</button>
      </div>
      {cfg.smtp_password_set && (
        <div style={{ marginTop: 16, padding: 12, background: 'rgba(255,107,26,.06)', border: '1px solid var(--border)', borderRadius: 6 }}>
          <label className="small" style={{ display: 'block', marginBottom: 6, fontWeight: 600 }}>
            📧 Testar envio de email
          </label>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <input
              type="email"
              placeholder="Destinatário (ex: oteu@gmail.com) — vazio envia para o admin"
              value={cfg.test_recipient || ''}
              onChange={e => setCfg({ ...cfg, test_recipient: e.target.value })}
              data-testid="smtp-test-to"
              style={{ flex: '1 1 300px', minWidth: 240 }}
            />
            <button className="btn btn-outline" onClick={test} disabled={testing} data-testid="smtp-test">
              {testing ? 'A enviar…' : 'Enviar teste'}
            </button>
          </div>
          <p className="small muted" style={{ marginTop: 8, marginBottom: 0 }}>
            💡 Este email fica <strong>guardado</strong> quando carregas em &quot;Guardar configuração&quot; e passa a ser usado também no botão de teste das <strong>campanhas newsletter</strong>. Se o servidor rejeitar com <code>550 spam</code>, é frequente ser por causa do padrão &quot;From=To&quot; — usar um Gmail externo resolve.
          </p>
        </div>
      )}
      {msg && <p className="small muted" style={{ marginTop: 8 }}>{msg}</p>}
      {err && <p className="small" style={{ color: '#ff6b1a', marginTop: 8 }}>{err}</p>}

      <div style={{ marginTop: 24, paddingTop: 16, borderTop: '1px solid var(--border)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
          <h4 style={{ margin: 0 }}>🩺 Últimos erros de envio</h4>
          <button className="btn btn-outline" onClick={loadErrors} data-testid="smtp-errors-reload" style={{ padding: '4px 10px', fontSize: 12 }}>↻ Actualizar</button>
        </div>
        {errors.length === 0
          ? <p className="small muted" style={{ margin: 0 }}>Sem erros recentes.</p>
          : (
            <ul className="small" style={{ margin: 0, paddingLeft: 18, maxHeight: 200, overflowY: 'auto' }} data-testid="smtp-errors-list">
              {errors.map((e, i) => (
                <li key={i} style={{ marginBottom: 6, color: '#ff6b1a' }}>
                  <code style={{ color: '#888' }}>{e.at?.slice(0, 19).replace('T', ' ')}</code>{' → '}
                  <strong>{e.to}</strong>{' · '}<span>{e.error}</span>
                </li>
              ))}
            </ul>
          )}
      </div>
    </>
  );
}

function DefinicoesManager() {
  const [cfg, setCfg] = useState(null);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [pw, setPw] = useState({ current_password: '', new_password: '' });
  const [pwMsg, setPwMsg] = useState('');
  useEffect(() => { apiGet('/admin/config').then(setCfg).catch(e => setErr(e.message)); }, []);

  const save = async () => {
    setMsg(''); setErr('');
    try {
      const upd = {
        company_name: cfg.company_name, nif: cfg.nif,
        iban: cfg.iban, bank_holder: cfg.bank_holder, bank_name: cfg.bank_name,
        payment_deadline_days: parseInt(cfg.payment_deadline_days) || 7,
        download_link_days: parseInt(cfg.download_link_days) || 30,
        responsibility_term: cfg.responsibility_term,
        since_year: parseInt(cfg.since_year) || null,
        stats_manual_trips_done: cfg.stats_manual_trips_done ? parseInt(cfg.stats_manual_trips_done) : null,
        stats_manual_total_km: cfg.stats_manual_total_km ? parseInt(cfg.stats_manual_total_km) : null,
      };
      await apiPut('/admin/config', upd);
      setMsg('Guardado.');
      setTimeout(() => setMsg(''), 4000);
    } catch (e) { setErr(e.message); }
  };

  const changePw = async (e) => {
    e.preventDefault(); setPwMsg('');
    try { await apiPut('/admin/users/me/password', pw); setPwMsg('Password alterada.'); setPw({ current_password: '', new_password: '' }); }
    catch (er) { setPwMsg(er.message); }
  };

  if (!cfg) return <p className="muted">A carregar…</p>;

  return (
    <div data-testid="definicoes-manager">
      <div className="form-panel">
        <h3>Dados de faturação e pagamento</h3>
        <div className="grid grid-2">
          <div className="form-row"><label>Nome da empresa</label><input data-testid="cfg-company" value={cfg.company_name || ''} onChange={e => setCfg({ ...cfg, company_name: e.target.value })} /></div>
          <div className="form-row"><label>NIF</label><input data-testid="cfg-nif" value={cfg.nif || ''} onChange={e => setCfg({ ...cfg, nif: e.target.value })} /></div>
          <div className="form-row"><label>IBAN de recebimento</label><input data-testid="cfg-iban" value={cfg.iban || ''} onChange={e => setCfg({ ...cfg, iban: e.target.value })} /></div>
          <div className="form-row"><label>Titular da conta</label><input data-testid="cfg-holder" value={cfg.bank_holder || ''} onChange={e => setCfg({ ...cfg, bank_holder: e.target.value })} /></div>
          <div className="form-row"><label>Banco</label><input data-testid="cfg-bank" value={cfg.bank_name || ''} onChange={e => setCfg({ ...cfg, bank_name: e.target.value })} /></div>
          <div className="form-row"><label>Prazo de pagamento (dias)</label><input type="number" data-testid="cfg-deadline" value={cfg.payment_deadline_days || 7} onChange={e => setCfg({ ...cfg, payment_deadline_days: e.target.value })} /></div>
          <div className="form-row"><label>Validade link download (dias)</label><input type="number" data-testid="cfg-dllink" value={cfg.download_link_days || 30} onChange={e => setCfg({ ...cfg, download_link_days: e.target.value })} /></div>
        </div>
        <div className="form-row"><label>Termo de responsabilidade</label><textarea rows="12" data-testid="cfg-term" value={cfg.responsibility_term || ''} onChange={e => setCfg({ ...cfg, responsibility_term: e.target.value })} /></div>
        <h4 style={{ marginTop: 16, marginBottom: 8 }}>📊 Contadores do hero (topo do site)</h4>
        <p className="small muted" style={{ marginTop: 0, marginBottom: 12 }}>
          Deixa vazio para calcular automaticamente a partir dos passeios realizados. Preenche para forçar um valor visível no site (útil enquanto o histórico ainda não está todo importado).
        </p>
        <div className="grid grid-2">
          <div className="form-row"><label>Passeios realizados (manual)</label><input type="number" min="0" data-testid="cfg-stats-trips" value={cfg.stats_manual_trips_done || ''} onChange={e => setCfg({ ...cfg, stats_manual_trips_done: e.target.value })} placeholder="ex: 45" /></div>
          <div className="form-row"><label>Km percorridos (manual)</label><input type="number" min="0" data-testid="cfg-stats-km" value={cfg.stats_manual_total_km || ''} onChange={e => setCfg({ ...cfg, stats_manual_total_km: e.target.value })} placeholder="ex: 12500" /></div>
          <div className="form-row"><label>Desde (ano)</label><input type="number" min="1990" max="2100" data-testid="cfg-since-year" value={cfg.since_year || 2022} onChange={e => setCfg({ ...cfg, since_year: e.target.value })} /></div>
        </div>
        <button className="btn" onClick={save} data-testid="cfg-save">Guardar definições</button>
        {msg && <p className="small muted" style={{ marginTop: 8 }} data-testid="cfg-save-toast">{msg}</p>}
        {err && <p className="small" style={{ color: '#ff6b1a', marginTop: 8 }} data-testid="cfg-save-error">{err}</p>}
      </div>

      <div className="form-panel" style={{ marginTop: 24 }}>
        <SmtpSettings />
      </div>

      <div className="form-panel" style={{ marginTop: 24 }}>
        <h3>Alterar password</h3>
        <form onSubmit={changePw}>
          <div className="form-row"><label>Password atual</label><PasswordInput required data-testid="pw-current" value={pw.current_password} onChange={e => setPw({ ...pw, current_password: e.target.value })} /></div>
          <div className="form-row"><label>Nova password</label><PasswordInput required minLength="6" data-testid="pw-new" value={pw.new_password} onChange={e => setPw({ ...pw, new_password: e.target.value })} /></div>
          <button className="btn" type="submit" data-testid="pw-submit">Alterar password</button>
          {pwMsg && <p className="small muted" style={{ marginTop: 8 }}>{pwMsg}</p>}
        </form>
      </div>
    </div>
  );
}

function PasseiosManager() {
  const [items, setItems] = useState([]);
  const [groups, setGroups] = useState({ teambuilding: [], municipio: [] });
  const [filter, setFilter] = useState('all');
  const [editing, setEditing] = useState(null); // null | {} | trip
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const load = useCallback(async () => {
    try {
      const r = await apiGet('/admin/trips');
      setItems(r.items || []);
      const [tb, mu] = await Promise.all([
        apiGet('/admin/groups?type=teambuilding').catch(() => ({ items: [] })),
        apiGet('/admin/groups?type=municipio').catch(() => ({ items: [] })),
      ]);
      setGroups({ teambuilding: tb.items || [], municipio: mu.items || [] });
    } catch (e) { setErr(e.message); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const groupName = (id) => {
    if (!id) return '—';
    return [...groups.teambuilding, ...groups.municipio].find(g => g.id === id)?.name || '—';
  };
  const filtered = filter === 'all' ? items : items.filter(t => (t.visibility || 'public') === filter);
  const visLabel = (v) => ({ draft: 'Rascunho', public: 'Público', teambuilding: 'Team Building', municipio: 'Município' }[v || 'public']);

  const quickUpdate = async (trip, patch) => {
    setErr(''); setMsg('');
    try {
      await apiPut(`/admin/trips/${trip.id}`, { ...trip, ...patch });
      setMsg('Classificação atualizada.');
      load();
    } catch (e) { setErr(e.message); }
  };

  const del = async (id) => {
    if (!window.confirm('Eliminar passeio?')) return;
    try { await apiDelete(`/admin/trips/${id}`); load(); } catch (e) { setErr(e.message); }
  };
  const dup = async (id) => {
    try { await apiPost(`/admin/trips/${id}/duplicate`, {}); setMsg('Passeio duplicado.'); load(); } catch (e) { setErr(e.message); }
  };

  if (editing !== null) {
    return <PasseioEditor trip={editing} onCancel={() => setEditing(null)} onSaved={() => { setEditing(null); load(); setMsg('Guardado.'); }} />;
  }

  const inlineSelectStyle = { padding: '6px 8px', background: '#0a0a0a', color: '#fff', border: '1px solid var(--border)', fontSize: 12, borderRadius: 4 };

  return (
    <div data-testid="passeios-manager">
      <div className="form-nav" style={{ marginBottom: 12, gap: 8, flexWrap: 'wrap' }}>
        <button className="btn" data-testid="passeio-new" onClick={() => setEditing({})}>+ Novo passeio</button>
        <span style={{ flex: 1 }} />
        <label className="small muted">Filtro:</label>
        {['all', 'draft', 'public', 'teambuilding', 'municipio'].map(v => (
          <button key={v} className={`admin-tab ${filter === v ? 'active' : ''}`} style={{ padding: '6px 12px', fontSize: 12 }} onClick={() => setFilter(v)} data-testid={`p-filter-${v}`}>{v === 'all' ? 'Todos' : visLabel(v)}</button>
        ))}
      </div>
      {msg && <p className="small muted">{msg}</p>}
      {err && <p className="small" style={{ color: '#ff6b1a' }}>{err}</p>}
      <div className="table-wrap">
        <table className="admin-table">
          <thead><tr><th>Título</th><th style={{ minWidth: 180 }}>Classificação</th><th style={{ minWidth: 180 }}>Grupo</th><th>Data</th><th>Preço</th><th>Vagas</th><th></th></tr></thead>
          <tbody>
            {filtered.map(t => {
              const currentVis = t.visibility || 'public';
              const needsGroup = currentVis === 'teambuilding' || currentVis === 'municipio';
              const groupOptions = currentVis === 'teambuilding' ? groups.teambuilding : currentVis === 'municipio' ? groups.municipio : [];
              return (
                <tr key={t.id}>
                  <td><strong>{t.title}</strong><br /><span className="small muted">{t.type === 'navigation' ? 'Navigation Challenge' : 'Guiado'} · {t.region || '—'}</span></td>
                  <td>
                    <select
                      value={currentVis}
                      onChange={e => quickUpdate(t, { visibility: e.target.value, group_id: e.target.value === 'public' || e.target.value === 'draft' ? null : t.group_id })}
                      data-testid={`p-vis-${t.id}`}
                      style={inlineSelectStyle}
                    >
                      <option value="draft">Rascunho</option>
                      <option value="public">Público</option>
                      <option value="teambuilding">Team Building</option>
                      <option value="municipio">Município</option>
                    </select>
                  </td>
                  <td>
                    {needsGroup ? (
                      <select
                        value={t.group_id || ''}
                        onChange={e => quickUpdate(t, { group_id: e.target.value || null })}
                        data-testid={`p-grp-${t.id}`}
                        style={inlineSelectStyle}
                      >
                        <option value="">— escolher —</option>
                        {groupOptions.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
                      </select>
                    ) : (
                      <span className="small muted">—</span>
                    )}
                  </td>
                  <td>{t.date_label || t.date_iso || '—'}</td>
                  <td>{Number(t.price_from || 0).toFixed(0)} €</td>
                  <td>
                    {t.vagas_disponiveis}/{t.vagas_total}
                    {t.inscriptions_closed && <><br /><span className="small" style={{ color: '#ff6b1a', fontWeight: 700 }}>🔒 Encerradas</span></>}
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    <button className="btn btn-outline" data-testid={`passeio-edit-${t.id}`} onClick={() => setEditing(t)}>Editar</button>{' '}
                    <button className="btn btn-outline" data-testid={`passeio-dup-${t.id}`} onClick={() => dup(t.id)}>Dup</button>{' '}
                    <button className="btn btn-outline" style={{ color: '#ff6b1a' }} data-testid={`passeio-del-${t.id}`} onClick={() => del(t.id)}>×</button>
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && <tr><td colSpan="7" className="muted">Sem passeios neste filtro.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function TripFileUpload({ tripId, kind, currentFile, onUploaded }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const ref = useRef(null);
  const upload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setBusy(true); setErr('');
    try {
      const fd = new FormData(); fd.append('file', file);
      const r = await fetch(`${API}/admin/trips/${tripId}/upload/${kind}`, { method: 'POST', credentials: 'include', body: fd });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Erro no upload');
      onUploaded(d.file);
    } catch (er) { setErr(er.message); }
    finally { setBusy(false); if (ref.current) ref.current.value = ''; }
  };
  return (
    <div className="form-row">
      <label>{kind.toUpperCase()} · Ficheiro de navegação (enviado apenas ao inscrito pago)</label>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        {currentFile
          ? <span className="small muted" style={{ padding: '6px 10px', background: 'rgba(76,175,80,.15)', color: '#4caf50', border: '1px solid #4caf50' }}>✓ {currentFile}</span>
          : <span className="small muted" style={{ padding: '6px 10px', background: 'rgba(255,107,26,.1)', color: '#ff6b1a', border: '1px solid rgba(255,107,26,.4)' }}>Sem ficheiro carregado</span>}
        <label className="btn btn-outline" style={{ cursor: 'pointer' }} data-testid={`p-${kind}-btn`}>
          {busy ? 'A carregar…' : (currentFile ? `Substituir ${kind.toUpperCase()}` : `Carregar ${kind.toUpperCase()}`)}
          <input ref={ref} type="file" accept={`.${kind}`} style={{ display: 'none' }} onChange={upload} data-testid={`p-${kind}-input`} />
        </label>
      </div>
      {err && <p className="small" style={{ color: '#ff6b1a', marginTop: 6 }}>{err}</p>}
    </div>
  );
}

function ImageUpload({ label, value, onChange, testid }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const ref = useRef(null);
  const upload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setBusy(true); setErr('');
    try {
      const fd = new FormData(); fd.append('file', file);
      const r = await fetch(`${API}/admin/uploads/image`, { method: 'POST', credentials: 'include', body: fd });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(_errMsg(d.detail, r.status) || 'Erro no upload');
      onChange(d.url);
    } catch (er) { setErr(er.message); }
    finally { setBusy(false); if (ref.current) ref.current.value = ''; }
  };
  const preview = value ? (value.startsWith('http') ? value : `${process.env.REACT_APP_BACKEND_URL}${value}`) : null;
  return (
    <div className="form-row">
      <label>{label}</label>
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
        {preview && <img src={preview} alt="preview" style={{ width: 120, height: 80, objectFit: 'cover', border: '1px solid var(--border)' }} data-testid={testid + '-preview'} />}
        <div style={{ flex: 1 }}>
          <label className="btn btn-outline" style={{ cursor: 'pointer', display: 'inline-block' }} data-testid={testid + '-btn'}>
            {busy ? 'A carregar…' : (value ? 'Substituir imagem' : 'Carregar imagem')}
            <input ref={ref} type="file" accept="image/*" style={{ display: 'none' }} onChange={upload} data-testid={testid + '-input'} />
          </label>
          {value && <button type="button" className="btn btn-outline" style={{ marginLeft: 8, color: '#ff6b1a' }} onClick={() => onChange('')} data-testid={testid + '-clear'}>Remover</button>}
          {err && <p className="small" style={{ color: '#ff6b1a', marginTop: 6 }}>{err}</p>}
          {value && <p className="small muted" style={{ marginTop: 6, wordBreak: 'break-all' }}>{value}</p>}
        </div>
      </div>
    </div>
  );
}

function GalleryUpload({ value, onChange, testid }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const ref = useRef(null);
  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true); setErr('');
    try {
      const urls = [];
      for (const file of files) {
        const fd = new FormData(); fd.append('file', file);
        const r = await fetch(`${API}/admin/uploads/image`, { method: 'POST', credentials: 'include', body: fd });
        const d = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(_errMsg(d.detail, r.status) || 'Erro no upload');
        urls.push(d.url);
      }
      onChange([...(value || []), ...urls]);
    } catch (er) { setErr(er.message); }
    finally { setBusy(false); if (ref.current) ref.current.value = ''; }
  };
  const move = (i, delta) => {
    const arr = [...(value || [])];
    const j = i + delta;
    if (j < 0 || j >= arr.length) return;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    onChange(arr);
  };
  const items = value || [];
  return (
    <div className="form-row">
      <label>Galeria</label>
      <label className="btn btn-outline" style={{ cursor: 'pointer', display: 'inline-block' }} data-testid={testid + '-btn'}>
        {busy ? 'A carregar…' : `+ Adicionar imagens${items.length ? ` (${items.length})` : ''}`}
        <input ref={ref} type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={upload} data-testid={testid + '-input'} />
      </label>
      {items.length > 0 && (
        <p className="small muted" style={{ marginTop: 8, marginBottom: 0 }}>
          💡 A <strong>1ª foto</strong> aparece em destaque (grande) na página pública. Usa as setas ⬅ ➡ para reordenar. Podes carregar quantas fotos quiseres — o layout adapta-se.
        </p>
      )}
      {err && <p className="small" style={{ color: '#ff6b1a', marginTop: 6 }}>{err}</p>}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(140px,1fr))', gap: 8, marginTop: 12 }}>
        {items.map((url, i) => {
          const src = url.startsWith('http') ? url : `${process.env.REACT_APP_BACKEND_URL}${url}`;
          return (
            <div key={i} style={{ position: 'relative', border: i === 0 ? '2px solid #ff6b1a' : '1px solid var(--border)' }}>
              <img src={src} alt="" style={{ width: '100%', height: 90, objectFit: 'cover', display: 'block' }} />
              {i === 0 && <span style={{ position: 'absolute', top: 4, left: 4, background: '#ff6b1a', color: '#fff', fontSize: 10, fontWeight: 800, padding: '2px 6px', borderRadius: 3, letterSpacing: '.5px' }}>DESTAQUE</span>}
              <div style={{ position: 'absolute', bottom: 4, left: 4, display: 'flex', gap: 3 }}>
                <button type="button" onClick={() => move(i, -1)} disabled={i === 0} data-testid={`${testid}-up-${i}`} title="Mover para a esquerda" style={{ background: 'rgba(0,0,0,.85)', color: '#fff', border: '1px solid #666', width: 22, height: 22, cursor: i === 0 ? 'not-allowed' : 'pointer', opacity: i === 0 ? .3 : 1, fontSize: 11 }}>◀</button>
                <button type="button" onClick={() => move(i, 1)} disabled={i === items.length - 1} data-testid={`${testid}-down-${i}`} title="Mover para a direita" style={{ background: 'rgba(0,0,0,.85)', color: '#fff', border: '1px solid #666', width: 22, height: 22, cursor: i === items.length - 1 ? 'not-allowed' : 'pointer', opacity: i === items.length - 1 ? .3 : 1, fontSize: 11 }}>▶</button>
              </div>
              <button type="button" onClick={() => onChange(items.filter((_, j) => j !== i))} data-testid={`${testid}-rm-${i}`} style={{ position: 'absolute', top: 4, right: 4, background: 'rgba(0,0,0,.85)', color: '#ff6b1a', border: '1px solid #ff6b1a', width: 24, height: 24, cursor: 'pointer', fontWeight: 800 }}>×</button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ListEditor({ label, value, onChange, suggestions = [], testid }) {
  const [tmp, setTmp] = useState('');
  const add = () => { if (tmp.trim()) { onChange([...(value || []), tmp.trim()]); setTmp(''); } };
  return (
    <div className="form-row">
      <label>{label}</label>
      <div style={{ display: 'flex', gap: 6 }}>
        <input list={testid + '-list'} value={tmp} onChange={e => setTmp(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); add(); } }} data-testid={testid + '-input'} placeholder="Escreve e Enter para adicionar" />
        <datalist id={testid + '-list'}>{suggestions.map(s => <option key={s} value={s} />)}</datalist>
        <button type="button" className="btn btn-outline" onClick={add} data-testid={testid + '-add'}>+</button>
      </div>
      <ul className="feat-list" style={{ marginTop: 8 }}>
        {(value || []).map((v, i) => (
          <li key={i}>{v} <a className="link small" style={{ marginLeft: 8, color: '#ff6b1a', cursor: 'pointer' }} onClick={() => onChange(value.filter((_, j) => j !== i))} data-testid={`${testid}-rm-${i}`}>remover</a></li>
        ))}
      </ul>
    </div>
  );
}

function PortalLogin({ onLoggedIn }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setErr(''); setLoading(true);
    try {
      const r = await apiPost('/portal/login', { email, password });
      onLoggedIn(r);
    } catch (er) { setErr(er.message); } finally { setLoading(false); }
  };
  const loginGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirect = window.location.origin + '/entrar';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirect)}`;
  };
  return (
    <section className="section">
      <div className="container" style={{ maxWidth: 460 }}>
        <div className="section-header">
          <h2>Entrar</h2>
          <div className="underline"></div>
          <p className="muted">Área privada de clientes, empresas e municípios Tectur.</p>
        </div>
        <button
          type="button"
          onClick={loginGoogle}
          data-testid="portal-google-btn"
          className="btn btn-block"
          style={{ background: '#fff', color: '#1f1f1f', border: '1px solid #ddd', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, marginBottom: 16 }}
        >
          <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.6-6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 5.1 29.3 3 24 3 12.4 3 3 12.4 3 24s9.4 21 21 21 21-9.4 21-21c0-1.2-.1-2.4-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.5 15.7 18.9 13 24 13c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 5.1 29.3 3 24 3 16.3 3 9.7 7.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 45c5.2 0 9.9-2 13.4-5.2l-6.2-5.2c-2.1 1.5-4.7 2.4-7.2 2.4-5.3 0-9.7-3.4-11.3-8l-6.6 5.1C9.5 40.6 16.2 45 24 45z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C41.1 34.5 45 29.7 45 24c0-1.2-.1-2.4-.4-3.5z"/></svg>
          Entrar com Google
        </button>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '4px 0 16px', color: '#666' }}>
          <div style={{ flex: 1, height: 1, background: '#2a2a2a' }} />
          <span className="small" style={{ letterSpacing: '.1em' }}>OU</span>
          <div style={{ flex: 1, height: 1, background: '#2a2a2a' }} />
        </div>
        <form onSubmit={submit} className="form-panel" data-testid="portal-login-form">
          <div className="form-row"><label>Email</label><input type="email" required value={email} onChange={e => setEmail(e.target.value)} data-testid="portal-email" /></div>
          <div className="form-row"><label>Password</label><PasswordInput required value={password} onChange={e => setPassword(e.target.value)} data-testid="portal-password" /></div>
          {err && <p className="small" style={{ color: '#ff6b1a' }} data-testid="portal-err">{err}</p>}
          <button type="submit" className="btn" disabled={loading} data-testid="portal-submit">{loading ? 'A entrar…' : 'Entrar'}</button>
        </form>
        <p className="small muted" style={{ marginTop: 20 }}>
          Ainda não tens conta? Os clientes recebem credenciais automaticamente após a primeira inscrição paga.<br />
          Para entrar com Google usa o mesmo email da tua inscrição. Empresas e municípios: entra em contacto em <a href="mailto:tectur@tectur.pt">tectur@tectur.pt</a>.
        </p>
      </div>
    </section>
  );
}

function PortalArea({ session, onLogout }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [pwForm, setPwForm] = useState({ current_password: '', new_password: '' });
  const [pwMsg, setPwMsg] = useState('');
  const [showPw, setShowPw] = useState(session.must_change_pw);
  const load = useCallback(async () => {
    try { setData(await apiGet('/portal/me')); } catch (e) { setErr(e.message); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const logout = async () => {
    try { await apiPost('/portal/logout', {}); } catch (e) { /* ignore */ }
    onLogout();
  };
  const changePw = async (e) => {
    e.preventDefault(); setPwMsg('');
    try { await apiPost('/portal/change-password', pwForm); setPwMsg('Password alterada.'); setPwForm({ current_password: '', new_password: '' }); setShowPw(false); load(); }
    catch (er) { setPwMsg(er.message); }
  };

  if (!data) return <section className="section"><div className="container"><p className="muted">A carregar…</p></div></section>;
  const isClient = data.type === 'client';
  const isMunicipio = !isClient && data.entity?.type === 'municipio';
  const entity = data.entity;

  return (
    <section className="section">
      <div className="container">
        <div className="section-header" style={{ position: 'relative' }}>
          <h2>Área {isClient ? 'Pessoal' : (isMunicipio ? 'do Município' : 'da Empresa')}</h2>
          <div className="underline"></div>
          <p className="muted">Olá {entity.name || entity.email} 👋</p>
          <button className="btn btn-outline" onClick={logout} data-testid="portal-logout" style={{ position: 'absolute', top: 0, right: 0 }}>Sair</button>
        </div>

        {data.must_change_pw && (
          <div className="form-panel" style={{ background: 'rgba(255,107,26,.08)', border: '1px solid rgba(255,107,26,.4)', marginBottom: 16 }}>
            <p style={{ margin: 0, color: '#ff6b1a', fontWeight: 700 }}>⚠️ Estás a usar a password temporária. Recomendamos que a troques agora.</p>
            <button className="btn" style={{ marginTop: 10 }} onClick={() => setShowPw(!showPw)} data-testid="portal-toggle-pw">{showPw ? 'Fechar' : 'Alterar password'}</button>
          </div>
        )}
        {showPw && (
          <form onSubmit={changePw} className="form-panel" style={{ marginBottom: 24 }} data-testid="portal-pw-form">
            <h3 style={{ marginTop: 0 }}>Alterar password</h3>
            <div className="form-row"><label>Password atual</label><PasswordInput required value={pwForm.current_password} onChange={e => setPwForm({ ...pwForm, current_password: e.target.value })} data-testid="portal-pw-current" /></div>
            <div className="form-row"><label>Nova password (mín. 6 caracteres)</label><PasswordInput required minLength="6" value={pwForm.new_password} onChange={e => setPwForm({ ...pwForm, new_password: e.target.value })} data-testid="portal-pw-new" /></div>
            <button type="submit" className="btn" data-testid="portal-pw-submit">Guardar nova password</button>
            {pwMsg && <p className="small muted" style={{ marginTop: 8 }}>{pwMsg}</p>}
          </form>
        )}

        {err && <p className="small" style={{ color: '#ff6b1a' }}>{err}</p>}

        {isClient ? (
          <>
            <h3>🚗 Os meus passeios</h3>
            {(!data.inscriptions || data.inscriptions.length === 0) ? (
              <p className="muted">Ainda não te inscreveste em nenhum passeio. Vê os passeios disponíveis em <a onClick={() => window.location.assign('/?p=passeios')} style={{ color: '#ff6b1a', cursor: 'pointer' }}>Passeios</a>.</p>
            ) : (
              <div className="grid grid-2">
                {data.inscriptions.map(i => {
                  const t = data.trips[i.trip_id] || {};
                  return (
                    <div key={i.id} className="card"><div className="card-body">
                      <h3>{t.title || i.trip_id}</h3>
                      <p className="small muted">{t.date_label || t.date_iso || ''}</p>
                      <p><strong>Ref.:</strong> {i.reference} · <span className={`status-pill ${i.status}`}>{i.status === 'paid' ? 'Pago' : 'Pendente'}</span></p>
                      <p><strong>Total:</strong> {Number(i.total || 0).toFixed(0)} €</p>
                      {i.status === 'paid' && t.meeting_point && <p className="small"><strong>Ponto encontro:</strong> {t.meeting_point}</p>}
                      {i.status === 'paid' && t.google_maps_url && <p className="small"><a href={t.google_maps_url} target="_blank" rel="noreferrer" style={{ color: '#ff6b1a' }}>🗺️ Abrir no Google Maps</a></p>}
                    </div></div>
                  );
                })}
              </div>
            )}
          </>
        ) : (
          <>
            <h3>🗺️ Os nossos passeios {isMunicipio ? 'oficiais' : 'de team building'}</h3>
            {data.trips.length === 0 ? (
              <p className="muted">Ainda sem passeios associados. A Tectur irá criar em breve.</p>
            ) : (
              <div className="grid grid-2">
                {data.trips.map(t => (
                  <div key={t.id} className="card"><div className="card-body">
                    <h3>{t.title}</h3>
                    <p className="small muted">{t.date_label || t.date_iso || ''} · {t.difficulty || '—'} · {t.distance_km ? `${t.distance_km} km` : ''}</p>
                    <p>{t.short_desc}</p>
                    {isMunicipio && (
                      <div style={{ marginTop: 10, padding: 10, background: 'rgba(255,107,26,.05)', border: '1px solid rgba(255,107,26,.3)' }}>
                        <p className="small" style={{ margin: '0 0 8px', fontWeight: 700 }}>📦 Kit publicitário</p>
                        <div className="form-nav" style={{ gap: 6, flexWrap: 'wrap' }}>
                          <a className="btn btn-outline" href={`${API}/social/trip/${t.id}/og.jpg`} download style={{ fontSize: 12, padding: '6px 12px' }}>Cartaz FB/LinkedIn</a>
                          <a className="btn btn-outline" href={`${API}/social/trip/${t.id}/square.jpg`} download style={{ fontSize: 12, padding: '6px 12px' }}>Instagram</a>
                          <a className="btn btn-outline" href={`${API}/social/trip/${t.id}/story.jpg`} download style={{ fontSize: 12, padding: '6px 12px' }}>Stories</a>
                        </div>
                        {t.google_maps_url && <p className="small" style={{ margin: '8px 0 0' }}><a href={t.google_maps_url} target="_blank" rel="noreferrer" style={{ color: '#ff6b1a' }}>🗺️ Google Maps</a></p>}
                      </div>
                    )}
                  </div></div>
                ))}
              </div>
            )}

            <h3 style={{ marginTop: 32 }}>👥 Inscritos ({data.inscriptions.length})</h3>
            {data.inscriptions.length === 0 ? (
              <p className="muted small">Sem inscritos ainda.</p>
            ) : (
              <div className="table-wrap">
                <table className="admin-table">
                  <thead><tr><th>Nome</th><th>Email</th><th>Estado</th></tr></thead>
                  <tbody>
                    {data.inscriptions.map(i => (
                      <tr key={i.id}>
                        <td>{i.driver_name}{i.team_name ? ` · ${i.team_name}` : ''}</td>
                        <td>{i.driver_email}</td>
                        <td><span className={`status-pill ${i.status}`}>{i.status === 'paid' ? 'Pago' : 'Pendente'}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
}

function SendRecapButton({ tripId, tripTitle }) {
  const [target, setTarget] = useState('paid');
  const [sending, setSending] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  const send = async () => {
    setErr(''); setMsg('');
    const label = target === 'all' ? 'TODOS os inscritos (pagos + pendentes)' : 'todos os inscritos PAGOS';
    if (!window.confirm(
      `Enviar email de recap (fotos do dia + links dos vídeos YouTube) para ${label} de:\n\n${tripTitle}\n\n` +
      `O email é enviado via SMTP tectur@tectur.pt. Guarda o passeio antes de enviar. Continuar?`
    )) return;
    setSending(true);
    try {
      const qs = target === 'all' ? '?all_inscribed=true' : '';
      const r = await apiPost(`/admin/trips/${tripId}/send-recap${qs}`, {});
      setMsg(`✅ Recap enviado para ${r.recipients} destinatário(s) · ${r.photos} foto(s) · ${r.videos} vídeo(s). Verifica em ~1 minuto.`);
    } catch (e) {
      setErr(e.message || 'Erro ao enviar recap.');
    } finally { setSending(false); }
  };

  return (
    <div style={{ marginTop: 20, padding: '16px 18px', background: 'rgba(255,107,26,.08)', border: '1px dashed rgba(255,107,26,.5)', borderRadius: 8 }} data-testid="send-recap-block">
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 240 }}>
          <div style={{ fontWeight: 700, fontSize: 14, color: '#ff6b1a', letterSpacing: '.06em', textTransform: 'uppercase', marginBottom: 4 }}>📧 Enviar recap aos inscritos</div>
          <div className="small muted">Envia por email as fotos do dia e os links dos vídeos YouTube a todos os inscritos deste passeio.</div>
        </div>
        <select value={target} onChange={e => setTarget(e.target.value)} data-testid="recap-target" style={{ padding: '8px 10px', background: '#0a0a0a', color: '#fff', border: '1px solid var(--border)' }}>
          <option value="paid">Só inscritos pagos</option>
          <option value="all">Todos os inscritos (pagos + pendentes)</option>
        </select>
        <button type="button" className="btn" onClick={send} disabled={sending} data-testid="recap-send-btn">
          {sending ? 'A enviar…' : '✉️ Enviar recap agora'}
        </button>
      </div>
      {msg && <p className="small" style={{ color: '#4caf50', marginTop: 10 }} data-testid="recap-msg">{msg}</p>}
      {err && <p className="small" style={{ color: '#ff6b1a', marginTop: 10 }} data-testid="recap-err"><strong>⚠ Não foi enviado:</strong> {err}</p>}
      <p className="small muted" style={{ marginTop: 10, marginBottom: 0 }}>
        💡 Guarda primeiro o passeio (botão &ldquo;Guardar&rdquo; acima) para os inscritos receberem as fotos/vídeos mais recentes.
      </p>
    </div>
  );
}


function SocialShareBlock({ trip }) {
  const BASE = process.env.REACT_APP_BACKEND_URL;
  const publicUrl = `${BASE}/passeios/${trip.slug || trip.id}`;
  const shareText = trip.short_desc || (trip.long_desc || '').split('\n')[0].slice(0, 200) || `Nova rota Tectur — ${trip.title}`;
  const wa = `https://wa.me/?text=${encodeURIComponent(shareText + '\n\n' + publicUrl)}`;
  const fb = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(publicUrl)}`;
  const tw = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(publicUrl)}`;
  const li = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(publicUrl)}`;
  const mailto = `mailto:?subject=${encodeURIComponent(trip.title)}&body=${encodeURIComponent(shareText + '\n\n' + publicUrl)}`;
  const copy = async () => {
    try { await navigator.clipboard.writeText(publicUrl); alert('Link copiado!'); }
    catch { window.prompt('Copia este link:', publicUrl); }
  };
  const formats = [
    { key: 'og', label: 'Facebook / LinkedIn / Twitter', size: '1200×630' },
    { key: 'square', label: 'Instagram Feed', size: '1080×1080' },
    { key: 'story', label: 'Instagram Stories / WhatsApp Status', size: '1080×1920' },
  ];
  return (
    <div>
      <p className="small muted" style={{ marginBottom: 12 }}>
        Imagens geradas automaticamente com a foto e informação desta rota. Descarrega para publicar nas redes.
        <br />O texto sugerido é: <em>&quot;{shareText.slice(0, 120)}{shareText.length > 120 ? '…' : ''}&quot;</em>
      </p>
      <div className="grid grid-3" style={{ gap: 16 }}>
        {formats.map(fmt => {
          const url = `${BASE}/api/social/trip/${trip.id}/${fmt.key}.jpg`;
          return (
            <div key={fmt.key} style={{ border: '1px solid var(--border)', background: '#0a0a0a', overflow: 'hidden' }}>
              <img src={url} alt={fmt.label} style={{ width: '100%', display: 'block', aspectRatio: fmt.key === 'story' ? '9/16' : (fmt.key === 'square' ? '1/1' : '1200/630'), objectFit: 'cover' }} data-testid={`social-preview-${fmt.key}`} />
              <div style={{ padding: 12 }}>
                <p style={{ margin: 0, fontWeight: 800, fontSize: 13 }}>{fmt.label}</p>
                <p className="small muted" style={{ margin: '4px 0 10px' }}>{fmt.size}</p>
                <a className="btn btn-outline" href={url} download={`tectur-${trip.slug || trip.id}-${fmt.key}.jpg`} style={{ display: 'inline-block', width: '100%', textAlign: 'center' }} data-testid={`social-dl-${fmt.key}`}>⬇ Descarregar</a>
              </div>
            </div>
          );
        })}
      </div>
      <h4 style={{ marginTop: 24 }}>Partilha rápida</h4>
      <div className="form-nav" style={{ gap: 8, flexWrap: 'wrap' }}>
        <a className="btn btn-outline" href={wa} target="_blank" rel="noreferrer" data-testid="share-wa">💬 WhatsApp</a>
        <a className="btn btn-outline" href={fb} target="_blank" rel="noreferrer" data-testid="share-fb">📘 Facebook</a>
        <a className="btn btn-outline" href={tw} target="_blank" rel="noreferrer" data-testid="share-tw">🐦 X/Twitter</a>
        <a className="btn btn-outline" href={li} target="_blank" rel="noreferrer" data-testid="share-li">💼 LinkedIn</a>
        <a className="btn btn-outline" href={mailto} data-testid="share-email">✉️ Email</a>
        <button className="btn btn-outline" onClick={copy} type="button" data-testid="share-copy">🔗 Copiar link</button>
      </div>
      <p className="small muted" style={{ marginTop: 12 }}>
        💡 O Instagram não permite publicar automaticamente por link. Descarrega a imagem <em>Square/Story</em>, abre a app e publica manualmente com o texto acima.
      </p>
    </div>
  );
}

function PasseioEditor({ trip, onCancel, onSaved }) {
  const isNew = !trip.id;
  const [f, setF] = useState({
    title: '', slug: '', type: 'guided', date_iso: '', date_label: '',
    duration: '', difficulty: '', meeting_point: '', region: '',
    distance_km: '', vagas_total: 12, vagas_disponiveis: 12, price_from: 0,
    image: '', detail_image: '', gallery: [], google_maps_url: '',
    short_desc: '', long_desc: '',
    highlights: [], included: [], not_included: [], vehicle_requirements: [],
    videos: [], tiers: [], extras: [], published: true,
    visibility: 'draft', group_id: '',
    ...trip,
    // Se o passeio já existe mas tem visibility=null (seed legado antes do campo
    // ser adicionado), assume "public" — bate certo com o comportamento do site
    // que já os serve em /passeios.
    visibility: (trip && trip.id && !trip.visibility) ? 'public' : (trip?.visibility || 'draft'),
  });
  const [groups, setGroups] = useState({ teambuilding: [], municipio: [] });
  const [sug, setSug] = useState({ highlights: [], included: [], not_included: [], vehicle_requirements: [], region: [], duration: [], difficulty: [], tier_labels: [] });
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    apiGet('/admin/trips/suggestions').then(setSug).catch(() => {});
    Promise.all([
      apiGet('/admin/groups?type=teambuilding').catch(() => ({ items: [] })),
      apiGet('/admin/groups?type=municipio').catch(() => ({ items: [] })),
    ]).then(([tb, mu]) => setGroups({ teambuilding: tb.items || [], municipio: mu.items || [] }));
  }, []);

  const save = async () => {
    setSaving(true); setErr('');
    try {
      const payload = {
        ...f,
        distance_km: parseFloat(f.distance_km) || 0,
        vagas_total: parseInt(f.vagas_total) || 12,
        vagas_disponiveis: parseInt(f.vagas_disponiveis) || 12,
        price_from: parseFloat(f.price_from) || 0,
        tiers: (f.tiers || []).map(t => ({ ...t, price: parseFloat(t.price) || 0, qty: parseInt(t.qty) || 0 })),
      };
      if (isNew) await apiPost('/admin/trips', payload);
      else await apiPut(`/admin/trips/${trip.id}`, payload);
      onSaved();
    } catch (e) { setErr(e.message); } finally { setSaving(false); }
  };

  const setTier = (i, key, val) => {
    const t = [...(f.tiers || [])]; t[i] = { ...t[i], [key]: val }; setF({ ...f, tiers: t });
  };

  const toggleInscriptionsClosed = async () => {
    const willClose = !f.inscriptions_closed;
    const msg = willClose
      ? `Fechar as inscrições de "${f.title || 'este passeio'}"?\n\n` +
        `A partir deste momento o site vai deixar de aceitar novas inscrições e o botão "Inscrever" vai desaparecer da página pública.\n\n` +
        `Podes reabrir mais tarde no mesmo botão.`
      : `Reabrir as inscrições de "${f.title || 'este passeio'}"?\n\nO botão "Inscrever" volta a aparecer no site.`;
    if (!window.confirm(msg)) return;
    setErr('');
    try {
      await apiPost(`/admin/trips/${trip.id}/close-inscriptions?closed=${willClose}`, {});
      setF({ ...f, inscriptions_closed: willClose });
    } catch (e) { setErr(e.message || 'Erro ao alterar estado das inscrições.'); }
  };

  return (
    <div className="form-panel" data-testid="passeio-editor">
      <div className="form-nav" style={{ marginBottom: 12, gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <button className="btn btn-outline" onClick={onCancel} data-testid="passeio-cancel">← Voltar</button>
        <span style={{ flex: 1 }} />
        {!isNew && (
          <button
            type="button"
            className="btn btn-outline"
            onClick={toggleInscriptionsClosed}
            data-testid="passeio-toggle-closed"
            style={{ borderColor: f.inscriptions_closed ? '#4caf50' : '#ff6b1a', color: f.inscriptions_closed ? '#4caf50' : '#ff6b1a' }}
            title={f.inscriptions_closed ? 'Reabrir inscrições — o botão Inscrever volta a aparecer no site' : 'Fechar inscrições — o site deixa de aceitar novas inscrições e o botão desaparece'}
          >
            {f.inscriptions_closed ? '🔓 Reabrir inscrições' : '🔒 Fechar inscrições'}
          </button>
        )}
        <button className="btn" onClick={save} disabled={saving} data-testid="passeio-save">{saving ? 'A guardar…' : (isNew ? 'Criar passeio' : 'Guardar')}</button>
      </div>
      {!isNew && f.inscriptions_closed && (
        <div style={{ padding: '10px 14px', background: 'rgba(255,107,26,.12)', border: '1px solid #ff6b1a', color: '#ff6b1a', borderRadius: 6, marginBottom: 12 }} data-testid="closed-banner">
          🔒 <strong>Inscrições encerradas.</strong> O site não aceita novas inscrições e o botão &ldquo;Inscrever&rdquo; está oculto. Podes reabrir a qualquer momento.
        </div>
      )}
      {err && <p className="small" style={{ color: '#ff6b1a' }}>{err}</p>}

      {/* Classificação — muito visível no topo do formulário */}
      <div style={{ background: 'rgba(255,107,26,.08)', border: '1px solid rgba(255,107,26,.4)', padding: '18px 20px', marginBottom: 24 }}>
        <h3 style={{ margin: '0 0 10px', color: '#ff6b1a', fontSize: 14, letterSpacing: '.12em', textTransform: 'uppercase' }}>Classificação do passeio</h3>
        <div className="grid grid-2" style={{ gap: 12 }}>
          <div className="form-row" style={{ margin: 0 }}>
            <label>Tipo de visibilidade</label>
            <select value={f.visibility} onChange={e => setF({ ...f, visibility: e.target.value })} data-testid="p-visibility">
              <option value="draft">📝 Rascunho — só o admin vê</option>
              <option value="public">🌐 Público — aparece no site (tectur.pt/passeios)</option>
              <option value="teambuilding">🏢 Team Building — grupo de empresa</option>
              <option value="municipio">🏛️ Município — entidade pública</option>
            </select>
          </div>
          {(f.visibility === 'teambuilding' || f.visibility === 'municipio') && (
            <div className="form-row" style={{ margin: 0 }}>
              <label>{f.visibility === 'teambuilding' ? 'Empresa associada' : 'Município associado'} *</label>
              <select value={f.group_id || ''} onChange={e => setF({ ...f, group_id: e.target.value })} data-testid="p-group">
                <option value="">— Escolher {f.visibility === 'teambuilding' ? 'empresa' : 'município'} —</option>
                {(f.visibility === 'teambuilding' ? groups.teambuilding : groups.municipio).map(g => (
                  <option key={g.id} value={g.id}>{g.name}</option>
                ))}
              </select>
              {(f.visibility === 'teambuilding' ? groups.teambuilding : groups.municipio).length === 0 && (
                <p className="small" style={{ color: '#ff6b1a', marginTop: 6 }}>
                  ⚠️ Sem {f.visibility === 'teambuilding' ? 'empresas' : 'municípios'} criados. Vai a <strong>{f.visibility === 'teambuilding' ? 'Empresas TB' : 'Municípios'}</strong> primeiro para criar.
                </p>
              )}
            </div>
          )}
        </div>
        <p className="small muted" style={{ margin: '10px 0 0' }}>
          {f.visibility === 'draft' && '💡 Rascunho: podes trabalhar no passeio sem que apareça em lado nenhum.'}
          {f.visibility === 'public' && '💡 Público: será listado no site em /passeios e qualquer pessoa pode inscrever-se.'}
          {f.visibility === 'teambuilding' && '💡 Team Building: só será visível para a empresa associada. Envia o link de convite pela aba Empresas TB.'}
          {f.visibility === 'municipio' && '💡 Município: rota oficial para o município. Depois de guardar, o município pode descarregar o kit publicitário na sua área.'}
        </p>
      </div>

      <h3>Básico</h3>
      <div className="grid grid-2">
        <div className="form-row"><label>Título</label><input required data-testid="p-title" value={f.title} onChange={e => setF({ ...f, title: e.target.value })} /></div>
        <div className="form-row"><label>Slug (URL)</label><input data-testid="p-slug" value={f.slug || ''} onChange={e => setF({ ...f, slug: e.target.value })} placeholder="auto se vazio" /></div>
        <div className="form-row"><label>Tipo</label><select data-testid="p-type" value={f.type} onChange={e => setF({ ...f, type: e.target.value })}><option value="guided">Passeio guiado</option><option value="navigation">Navigation Challenge</option></select></div>
        <div className="form-row"><label>Data (YYYY-MM-DD)</label><input type="date" data-testid="p-date" value={f.date_iso || ''} onChange={e => setF({ ...f, date_iso: e.target.value })} /></div>
        <div className="form-row"><label>Etiqueta da data (ex: 15 de Março)</label><input data-testid="p-datelabel" value={f.date_label || ''} onChange={e => setF({ ...f, date_label: e.target.value })} /></div>
        <div className="form-row"><label>Duração</label><input list="dur-list" data-testid="p-duration" value={f.duration || ''} onChange={e => setF({ ...f, duration: e.target.value })} /><datalist id="dur-list">{sug.duration.map(v => <option key={v} value={v} />)}</datalist></div>
        <div className="form-row"><label>Dificuldade</label><input list="dif-list" data-testid="p-difficulty" value={f.difficulty || ''} onChange={e => setF({ ...f, difficulty: e.target.value })} /><datalist id="dif-list">{sug.difficulty.map(v => <option key={v} value={v} />)}</datalist></div>
        <div className="form-row"><label>Região</label><input list="reg-list" data-testid="p-region" value={f.region || ''} onChange={e => setF({ ...f, region: e.target.value })} /><datalist id="reg-list">{sug.region.map(v => <option key={v} value={v} />)}</datalist></div>
        <div className="form-row"><label>Distância (km)</label><input type="number" step="0.1" data-testid="p-distance" value={f.distance_km} onChange={e => setF({ ...f, distance_km: e.target.value })} /></div>
        <div className="form-row"><label>URL Google Maps (PRIVADO — enviado ao inscrito pago, com rota traçada)</label><input data-testid="p-mapsurl" value={f.google_maps_url || ''} onChange={e => setF({ ...f, google_maps_url: e.target.value })} placeholder="https://maps.app.goo.gl/… ou https://www.google.com/maps/dir/…" /></div>
        <div className="form-row"><label>Ponto de encontro (PRIVADO — só enviado a pagos)</label><input data-testid="p-meeting" value={f.meeting_point || ''} onChange={e => setF({ ...f, meeting_point: e.target.value })} /></div>
        <div className="form-row"><label>Vagas totais</label><input type="number" data-testid="p-vagast" value={f.vagas_total} onChange={e => setF({ ...f, vagas_total: e.target.value })} /></div>
        <div className="form-row"><label>Vagas disponíveis</label><input type="number" data-testid="p-vagasd" value={f.vagas_disponiveis} onChange={e => setF({ ...f, vagas_disponiveis: e.target.value })} /></div>
        <div className="form-row"><label>Preço a partir de (€)</label><input type="number" step="0.01" data-testid="p-price" value={f.price_from} onChange={e => setF({ ...f, price_from: e.target.value })} /></div>
      </div>

      <h3>Imagens</h3>
      <div className="grid grid-2">
        <ImageUpload label="Imagem principal (card do passeio)" value={f.image} onChange={v => setF({ ...f, image: v })} testid="p-image" />
        <ImageUpload label="Imagem de detalhe (topo da página do passeio)" value={f.detail_image} onChange={v => setF({ ...f, detail_image: v })} testid="p-detimg" />
      </div>
      <GalleryUpload value={f.gallery} onChange={v => setF({ ...f, gallery: v })} testid="p-gallery" />

      <h3>Ficheiros de navegação (GPX / KML)</h3>
      {isNew ? (
        <p className="muted small" style={{ padding: 12, background: 'rgba(255,107,26,.08)', border: '1px solid rgba(255,107,26,.3)' }}>
          Guarda o passeio primeiro (botão em cima) para poder carregar os ficheiros GPX/KML. Podes abrir inscrições sem eles — envia mais tarde.
        </p>
      ) : (
        <div className="grid grid-2">
          <TripFileUpload tripId={trip.id} kind="gpx" currentFile={f.gpx_file} onUploaded={fn => setF({ ...f, gpx_file: fn })} />
          <TripFileUpload tripId={trip.id} kind="kml" currentFile={f.kml_file} onUploaded={fn => setF({ ...f, kml_file: fn })} />
        </div>
      )}

      <h3>Descrição</h3>
      <div className="form-row"><label>Descrição curta</label><textarea rows="2" data-testid="p-shortdesc" value={f.short_desc || ''} onChange={e => setF({ ...f, short_desc: e.target.value })} /></div>
      <div className="form-row"><label>Descrição longa</label><textarea rows="6" data-testid="p-longdesc" value={f.long_desc || ''} onChange={e => setF({ ...f, long_desc: e.target.value })} /></div>

      <h3>Detalhes</h3>
      <ListEditor label="Destaques" value={f.highlights} onChange={v => setF({ ...f, highlights: v })} suggestions={sug.highlights} testid="p-highlights" />
      <ListEditor label="Incluído" value={f.included} onChange={v => setF({ ...f, included: v })} suggestions={sug.included} testid="p-included" />
      <ListEditor label="Não incluído" value={f.not_included} onChange={v => setF({ ...f, not_included: v })} suggestions={sug.not_included} testid="p-notincluded" />
      <ListEditor label="Requisitos da viatura" value={f.vehicle_requirements} onChange={v => setF({ ...f, vehicle_requirements: v })} suggestions={sug.vehicle_requirements} testid="p-vehicle" />

      <h3>Escalões de preço (tiers)</h3>
      <div className="form-nav" style={{ marginBottom: 8 }}>
        <button type="button" className="btn btn-outline" data-testid="p-tier-add" onClick={() => setF({ ...f, tiers: [...(f.tiers || []), { id: 'tier_' + Date.now(), label: '', price: 0, qty: 0 }] })}>+ Adicionar escalão</button>
      </div>
      {(f.tiers || []).map((t, i) => (
        <div key={i} className="grid grid-4" style={{ marginBottom: 8, alignItems: 'end' }}>
          <div className="form-row"><label>Etiqueta</label><input list="tierlbl-list" data-testid={`p-tier-label-${i}`} value={t.label} onChange={e => setTier(i, 'label', e.target.value)} /><datalist id="tierlbl-list">{sug.tier_labels.map(v => <option key={v} value={v} />)}</datalist></div>
          <div className="form-row"><label>Preço (€)</label><input type="number" step="0.01" data-testid={`p-tier-price-${i}`} value={t.price} onChange={e => setTier(i, 'price', e.target.value)} /></div>
          <div className="form-row"><label>Qtd (mock)</label><input type="number" data-testid={`p-tier-qty-${i}`} value={t.qty} onChange={e => setTier(i, 'qty', e.target.value)} /></div>
          <div><button type="button" className="btn btn-outline" style={{ color: '#ff6b1a' }} onClick={() => setF({ ...f, tiers: f.tiers.filter((_, j) => j !== i) })} data-testid={`p-tier-rm-${i}`}>Remover</button></div>
        </div>
      ))}

      <h3>Vídeos YouTube</h3>
      <div className="form-nav" style={{ marginBottom: 8 }}>
        <button type="button" className="btn btn-outline" data-testid="p-video-add" onClick={() => setF({ ...f, videos: [...(f.videos || []), { id: '', title: '' }] })}>+ Adicionar vídeo</button>
      </div>
      {(f.videos || []).map((v, i) => (
        <div key={i} className="grid grid-4" style={{ marginBottom: 8, alignItems: 'end' }}>
          <div className="form-row"><label>YouTube ID</label><input data-testid={`p-video-id-${i}`} value={v.id} onChange={e => { const nv = [...f.videos]; nv[i] = { ...nv[i], id: e.target.value }; setF({ ...f, videos: nv }); }} /></div>
          <div className="form-row" style={{ gridColumn: 'span 2' }}><label>Título</label><input data-testid={`p-video-title-${i}`} value={v.title || ''} onChange={e => { const nv = [...f.videos]; nv[i] = { ...nv[i], title: e.target.value }; setF({ ...f, videos: nv }); }} /></div>
          <div><button type="button" className="btn btn-outline" style={{ color: '#ff6b1a' }} onClick={() => setF({ ...f, videos: f.videos.filter((_, j) => j !== i) })} data-testid={`p-video-rm-${i}`}>Remover</button></div>
        </div>
      ))}

      <div className="form-row">
        <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <input type="checkbox" data-testid="p-published" checked={f.published} onChange={e => setF({ ...f, published: e.target.checked })} />
          Publicado (visível no site)
        </label>
      </div>

      {/* --- Passeio Realizado (arquivo) --- */}
      <div style={{ background: 'rgba(76,175,80,.06)', border: '1px solid rgba(76,175,80,.35)', padding: '18px 20px', marginTop: 24, marginBottom: 12, borderRadius: 10 }}>
        <h3 style={{ margin: '0 0 6px', color: '#4caf50', fontSize: 14, letterSpacing: '.12em', textTransform: 'uppercase' }}>📸 Passeio Realizado (Arquivo)</h3>
        <p className="small muted" style={{ margin: '0 0 14px' }}>Preenche depois do passeio acontecer. Aparece automaticamente em <strong>tectur.pt/passeios-realizados</strong> assim que a data ficar no passado.</p>

        <div className="form-row">
          <label>Como correu (curto texto que aparece no topo do arquivo)</label>
          <textarea rows={3} data-testid="p-recap" value={f.completed_recap || ''} onChange={e => setF({ ...f, completed_recap: e.target.value })} placeholder="Ex: Dia perfeito, 8 viaturas, chegámos ao topo pelas 15h. Sem incidentes." />
        </div>

        <div className="grid grid-2" style={{ gap: 12 }}>
          <div className="form-row" style={{ margin: 0 }}>
            <label>Nº de participantes</label>
            <input type="number" min="0" data-testid="p-participants" value={f.completed_participants ?? ''} onChange={e => setF({ ...f, completed_participants: e.target.value === '' ? null : parseInt(e.target.value) })} />
          </div>
          <div className="form-row" style={{ margin: 0 }}>
            <label>Nº de viaturas</label>
            <input type="number" min="0" data-testid="p-vehicles" value={f.completed_vehicles ?? ''} onChange={e => setF({ ...f, completed_vehicles: e.target.value === '' ? null : parseInt(e.target.value) })} />
          </div>
        </div>

        <div className="form-row" style={{ marginTop: 12 }}>
          <label>Fotos do dia (Upload)</label>
          <GalleryUpload value={f.completed_gallery || []} onChange={v => setF({ ...f, completed_gallery: v })} testid="p-completed-gallery" />
        </div>

        <div className="form-row" style={{ marginTop: 12 }}>
          <label>Vídeos YouTube do dia</label>
          <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
            <button type="button" className="btn btn-outline btn-sm" data-testid="p-completed-video-add" onClick={() => setF({ ...f, completed_videos: [...(f.completed_videos || []), { id: '', title: '' }] })}>+ Adicionar vídeo</button>
          </div>
          {(f.completed_videos || []).map((v, i) => (
            <div key={i} className="grid grid-4" style={{ marginBottom: 6, alignItems: 'end' }}>
              <div className="form-row"><label className="small">YouTube ID</label><input data-testid={`p-completed-video-id-${i}`} value={v.id || ''} onChange={e => { const nv = [...(f.completed_videos || [])]; nv[i] = { ...nv[i], id: e.target.value }; setF({ ...f, completed_videos: nv }); }} /></div>
              <div className="form-row" style={{ gridColumn: 'span 2' }}><label className="small">Título</label><input data-testid={`p-completed-video-title-${i}`} value={v.title || ''} onChange={e => { const nv = [...(f.completed_videos || [])]; nv[i] = { ...nv[i], title: e.target.value }; setF({ ...f, completed_videos: nv }); }} /></div>
              <div><button type="button" className="btn btn-outline btn-sm" style={{ color: '#ff6b1a' }} onClick={() => setF({ ...f, completed_videos: (f.completed_videos || []).filter((_, j) => j !== i) })}>Remover</button></div>
            </div>
          ))}
        </div>

        {!isNew && (
          <SendRecapButton tripId={trip.id} tripTitle={f.title || trip.title} />
        )}
      </div>

      {!isNew && f.visibility === 'public' && (
        <>
          <h3>📱 Partilhar nas Redes Sociais</h3>
          <SocialShareBlock trip={trip} />
        </>
      )}

      <div className="form-nav">
        <button className="btn btn-outline" onClick={onCancel} data-testid="passeio-cancel-bottom">Cancelar</button>
        <button className="btn" onClick={save} disabled={saving} data-testid="passeio-save-bottom">{saving ? 'A guardar…' : (isNew ? 'Criar passeio' : 'Guardar alterações')}</button>
      </div>
    </div>
  );
}

function LegalPage({ title, children }) {
  return (
    <section className="section">
      <div className="container" style={{ maxWidth: 860 }}>
        <div className="section-header">
          <h2>{title}</h2>
          <div className="underline"></div>
        </div>
        <div className="legal-content">{children}</div>
      </div>
    </section>
  );
}

function PoliticaPrivacidade() {
  return (
    <LegalPage title="Política de Privacidade">
      <p>A Tectur — Turismo Off-Road (RNAAT nº 143/2022) é responsável pelo tratamento dos dados pessoais que recolhe através deste website em conformidade com o Regulamento Geral sobre a Proteção de Dados (RGPD).</p>
      <h3>1. Dados recolhidos</h3>
      <ul>
        <li><strong>Inscrições em rotas:</strong> nome, email, telefone, NIF, número CC (necessários para faturação e seguro obrigatório).</li>
        <li><strong>Newsletter:</strong> apenas email (e nome, opcional).</li>
        <li><strong>Contactos B2B (Team Building / Municípios):</strong> dados de contacto da empresa/entidade e objetivos do serviço.</li>
        <li><strong>Testemunhos:</strong> nome (opcional), empresa (opcional) e texto submetido — publicados só após moderação e com o seu consentimento.</li>
      </ul>
      <h3>2. Finalidades e base legal</h3>
      <ul>
        <li>Execução de contrato (inscrições, pagamentos, prestação do serviço)</li>
        <li>Consentimento (newsletter, testemunhos publicados)</li>
        <li>Obrigação legal (faturação, RNAAT, seguro)</li>
      </ul>
      <h3>3. Prazos de conservação</h3>
      <p>Dados fiscais 10 anos (obrigação legal). Emails de newsletter até cancelar subscrição. Dados de leads B2B até 24 meses após o último contacto.</p>
      <h3>4. Partilha de dados</h3>
      <p>Não vendemos nem partilhamos dados com terceiros para fins de marketing. Partilhamos apenas com: seguradora do passeio (nome e CC), autoridades competentes quando exigido por lei, e prestadores técnicos (email, alojamento) sob contrato de confidencialidade.</p>
      <h3>5. Os teus direitos</h3>
      <p>Podes exercer os direitos de acesso, retificação, apagamento, portabilidade, oposição e limitação escrevendo para <a href="mailto:tectur@tectur.pt">tectur@tectur.pt</a>. Tens ainda o direito de reclamar junto da CNPD (<a href="https://www.cnpd.pt" target="_blank" rel="noreferrer">cnpd.pt</a>).</p>
      <h3>6. Contactos</h3>
      <p>Tectur · RNAAT nº 143/2022 · <a href="mailto:tectur@tectur.pt">tectur@tectur.pt</a></p>
      <p className="small muted">Última atualização: Fevereiro 2026</p>
    </LegalPage>
  );
}

function PoliticaCookies() {
  return (
    <LegalPage title="Política de Cookies">
      <p>Este site usa cookies para garantir o funcionamento correto (sessão, preferências) e para melhorar a experiência de forma anónima. <strong>Não usamos cookies de publicidade nem partilhamos dados com terceiros.</strong></p>
      <h3>Tipos de cookies</h3>
      <ul>
        <li><strong>Essenciais (sempre ativos):</strong> mantêm a sessão de administrador iniciada, guardam o passeio que estava a inscrever, memorizam se já aceitou a política de cookies. Não podem ser desativados.</li>
        <li><strong>Análise (opcional):</strong> contadores de visitas anónimos que nos ajudam a perceber que páginas são mais úteis. Não identificam indivíduos.</li>
      </ul>
      <h3>Como controlar</h3>
      <p>Pode aceitar apenas essenciais ou aceitar todos no banner que aparece na primeira visita. Para alterar mais tarde, limpe os cookies do site nas definições do browser — na próxima visita o banner reaparece.</p>
      <h3>Duração</h3>
      <p>Cookies de sessão são apagados quando fecha o browser. Preferências ficam guardadas até 12 meses.</p>
      <p className="small muted">Última atualização: Fevereiro 2026</p>
    </LegalPage>
  );
}

function TermosCondicoes() {
  return (
    <LegalPage title="Termos e Condições">
      <p>Ao utilizar o site tectur.pt e ao inscrever-se em qualquer passeio Tectur, o utilizador aceita os presentes termos e condições.</p>
      <h3>1. Sobre a Tectur</h3>
      <p>A Tectur é uma marca de turismo off-road licenciada junto do Turismo de Portugal (RNAAT nº 143/2022). Toda a atividade cumpre a legislação portuguesa aplicável a turismo de natureza.</p>
      <h3>2. Inscrições e pagamento</h3>
      <ul>
        <li>A inscrição só é considerada confirmada após pagamento integral por transferência bancária, dentro do prazo indicado no email de confirmação.</li>
        <li>Após confirmação do pagamento, o participante recebe por email o ponto de encontro exato, localização Google Maps e ficheiros GPX/KML da rota (quando aplicável).</li>
      </ul>
      <h3>3. Política de cancelamento</h3>
      <ul>
        <li>Cancelamento pelo participante até 15 dias antes: reembolso de 80%.</li>
        <li>Cancelamento até 7 dias antes: reembolso de 50%.</li>
        <li>Menos de 7 dias ou não comparência: sem reembolso.</li>
        <li>Cancelamento pela Tectur (meteorologia adversa, condições inseguras): reembolso integral ou remarcação sem custos.</li>
      </ul>
      <h3>4. Responsabilidade do participante</h3>
      <p>O participante é responsável pela viatura, pelo cumprimento do Código da Estrada, pela segurança dos seus passageiros e pela subscrição do termo de responsabilidade específico entregue no briefing. Menores devem estar acompanhados por representante legal.</p>
      <h3>5. Livro de Reclamações</h3>
      <p>Nos termos legais, disponibilizamos o <a href="https://www.livroreclamacoes.pt/Inicio/" target="_blank" rel="noreferrer">Livro de Reclamações Eletrónico</a>.</p>
      <h3>6. Resolução Alternativa de Litígios de Consumo (RAL)</h3>
      <p>Em cumprimento da <strong>Lei nº 144/2015 de 8 de Setembro</strong>, informamos que, em caso de litígio de consumo, o consumidor pode recorrer a uma entidade de resolução alternativa de litígios de consumo:</p>
      <ul>
        <li><strong>CACCL</strong> — Centro de Arbitragem de Conflitos de Consumo de Lisboa · <a href="https://www.centroarbitragemlisboa.pt/" target="_blank" rel="noreferrer">centroarbitragemlisboa.pt</a></li>
        <li><strong>CNIACC</strong> — Centro Nacional de Informação e Arbitragem de Conflitos de Consumo · <a href="https://www.cniacc.pt/" target="_blank" rel="noreferrer">cniacc.pt</a></li>
        <li>Lista completa em <a href="https://www.consumidor.gov.pt/parceiros-2/rede-de-arbitragem-de-consumo.aspx" target="_blank" rel="noreferrer">Direção-Geral do Consumidor</a>.</li>
      </ul>
      <p>Plataforma Europeia de Resolução de Litígios em Linha (ODR): <a href="https://ec.europa.eu/consumers/odr" target="_blank" rel="noreferrer">ec.europa.eu/consumers/odr</a>.</p>
      <h3>7. Seguro Obrigatório</h3>
      <p>Todos os passeios Tectur incluem <strong>seguro de responsabilidade civil e seguro de acidentes pessoais</strong> obrigatórios ao abrigo do artigo 26.º do Decreto-Lei nº 108/2009 (RNAAT). A apólice está disponível para consulta em <a href="mailto:tectur@tectur.pt">tectur@tectur.pt</a>.</p>
      <h3>8. Foro competente</h3>
      <p>Para qualquer litígio decorrente do uso deste site ou dos serviços Tectur, o foro competente é o da Comarca de Coimbra, com renúncia expressa a qualquer outro.</p>
      <p className="small muted">Última atualização: Fevereiro 2026</p>
    </LegalPage>
  );
}

function SobreNos() {
  const [cfg, setCfg] = useState({ nif: '', company_name: 'Tectur' });
  useEffect(() => { apiGet('/config/public').then(setCfg).catch(() => {}); }, []);
  const nif = cfg?.nif && cfg.nif !== '(por preencher)' ? cfg.nif : null;
  return (
    <section className="section">
      <div className="container" style={{ maxWidth: 1080 }}>
        <div className="section-header">
          <h2>Sobre a Tectur</h2>
          <div className="underline"></div>
          <p className="muted">Turismo off-road licenciado — feito em Portugal.</p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 24, color: '#d0d0d0', lineHeight: 1.75, fontSize: 16 }}>
          <p>A <strong>Tectur</strong> é uma marca portuguesa de <strong>turismo off-road</strong> nascida em 2022, dedicada a <strong>rotas testadas no terreno</strong> e à tecnologia própria que suporta essa experiência: as apps <strong>Tectur 4×4 Navigation</strong> e <strong>Tectur Car Launcher</strong>, e o <strong>equipamento rugged</strong> distribuído pela loja online.</p>
          <p>Cada rota é conduzida antes de ser publicada. Cada trilho é validado pelos guias Tectur. Cada passeio é organizado dentro do quadro legal do <strong>Registo Nacional dos Agentes de Animação Turística</strong>, com seguros obrigatórios, briefing, viatura de apoio e reportagem visual incluídos.</p>
        </div>

        {/* Certifications block */}
        <div style={{ marginTop: 56, background: '#151515', border: '1px solid #2a2a2a', borderLeft: '4px solid #ff6b1a', padding: '40px 36px' }} data-testid="sobre-certs">
          <h3 style={{ marginBottom: 8 }}>Certificações & Credenciais Oficiais</h3>
          <p className="muted small" style={{ marginBottom: 32 }}>Todos os selos apresentados são verificáveis publicamente. Clica em cada um para confirmar.</p>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 24 }}>

            {/* Turismo de Portugal / RNAAT */}
            <div style={{ background: '#0f0f0f', border: '1px solid #2a2a2a', padding: 24 }}>
              <div style={{ background: '#fff', padding: '10px 14px', borderRadius: 2, display: 'inline-flex', height: 56, alignItems: 'center', marginBottom: 16 }}>
                <img src={`${API}/uploads/turismo-de-portugal.png`} alt="Turismo de Portugal" style={{ height: 36, width: 'auto', display: 'block' }} />
              </div>
              <h4 style={{ fontSize: 18, marginBottom: 6 }}>Turismo de Portugal · RNAAT</h4>
              <p className="muted small" style={{ margin: '0 0 12px' }}>Registo Nacional dos Agentes de Animação Turística. Obrigatório para operar em Portugal.</p>
              <div className="small" style={{ color: '#e5e5e5', lineHeight: 1.7 }}>
                <div><strong>Nº registo:</strong> 143/2022</div>
                <div><strong>Emissão:</strong> 2022</div>
                <div><strong>Estado:</strong> <span style={{ color: '#7ee27e' }}>● Activo</span></div>
              </div>
              <a href="https://rnt.turismodeportugal.pt/RNT/RNAAT.aspx?nr=143/2022" target="_blank" rel="noreferrer" className="link small" style={{ marginTop: 14, display: 'inline-block' }} data-testid="verify-rnaat">Verificar no registo oficial →</a>
            </div>

            {/* Turismo de Natureza */}
            <div style={{ background: '#0f0f0f', border: '1px solid #2a2a2a', padding: 24 }}>
              <div style={{ height: 56, marginBottom: 16, display: 'flex', alignItems: 'center' }}>
                <img src={`${API}/uploads/turismo-natureza.png`} alt="Turismo de Natureza" style={{ height: 56, width: 'auto', display: 'block', filter: 'drop-shadow(0 0 8px rgba(200,180,110,.25))' }} />
              </div>
              <h4 style={{ fontSize: 18, marginBottom: 6 }}>Turismo de Natureza</h4>
              <p className="muted small" style={{ margin: '0 0 12px' }}>Reconhecimento como agente de Turismo de Natureza ao abrigo do código de conduta da Portaria nº 651/2009.</p>
              <div className="small" style={{ color: '#e5e5e5', lineHeight: 1.7 }}>
                <div><strong>Base legal:</strong> Portaria 651/2009</div>
                <div><strong>Áreas:</strong> Sistema Nacional de Áreas Classificadas (SNAC)</div>
                <div><strong>Estado:</strong> <span style={{ color: '#7ee27e' }}>● Activo</span></div>
              </div>
              <a href="https://business.turismodeportugal.pt/pt/Planear_Iniciar/Licenciamento_Registo_da_Atividade/Agentes_Animacao_Turistica/Paginas/reconhecimento-das-atividades-como-turismo-de-natureza.aspx" target="_blank" rel="noreferrer" className="link small" style={{ marginTop: 14, display: 'inline-block' }} data-testid="verify-natureza">Ver código de conduta →</a>
            </div>

            {/* Turismo Centro de Portugal */}
            <div style={{ background: '#0f0f0f', border: '1px solid #2a2a2a', padding: 24 }}>
              <div style={{ background: '#fff', padding: '10px 14px', borderRadius: 2, display: 'inline-flex', height: 56, alignItems: 'center', marginBottom: 16 }}>
                <img src={`${API}/uploads/turismo-centro-de-portugal.png`} alt="Turismo Centro de Portugal" style={{ height: 34, width: 'auto', display: 'block' }} />
              </div>
              <h4 style={{ fontSize: 18, marginBottom: 6 }}>Turismo Centro de Portugal</h4>
              <p className="muted small" style={{ margin: '0 0 12px' }}>Entidade Regional de Turismo do Centro de Portugal — região onde operamos a maior parte das rotas.</p>
              <div className="small" style={{ color: '#e5e5e5', lineHeight: 1.7 }}>
                <div><strong>Região:</strong> Centro de Portugal</div>
                <div><strong>Sede:</strong> Aveiro</div>
                <div><strong>Estado:</strong> <span style={{ color: '#7ee27e' }}>● Parceria activa</span></div>
              </div>
              <a href="https://turismodocentro.pt/" target="_blank" rel="noreferrer" className="link small" style={{ marginTop: 14, display: 'inline-block' }} data-testid="verify-centro">Ir para turismodocentro.pt →</a>
            </div>

            {/* Livro de Reclamações */}
            <div style={{ background: '#0f0f0f', border: '1px solid #2a2a2a', padding: 24 }}>
              <div style={{ background: '#fff', padding: '10px 14px', borderRadius: 2, display: 'inline-flex', height: 56, alignItems: 'center', marginBottom: 16 }}>
                <img src="https://www.livroreclamacoes.pt/Content/Images/logo_lr.png" alt="Livro de Reclamações" style={{ height: 36, width: 'auto', display: 'block' }} onError={(e) => { e.target.style.display = 'none'; }} />
              </div>
              <h4 style={{ fontSize: 18, marginBottom: 6 }}>Livro de Reclamações Eletrónico</h4>
              <p className="muted small" style={{ margin: '0 0 12px' }}>Disponibilizado nos termos do Decreto-Lei nº 74/2017 para todos os consumidores.</p>
              <div className="small" style={{ color: '#e5e5e5', lineHeight: 1.7 }}>
                <div><strong>Base legal:</strong> DL 74/2017</div>
                <div><strong>Estado:</strong> <span style={{ color: '#7ee27e' }}>● Sempre disponível</span></div>
              </div>
              <a href="https://www.livroreclamacoes.pt/Inicio/" target="_blank" rel="noreferrer" className="link small" style={{ marginTop: 14, display: 'inline-block' }} data-testid="verify-livro">Abrir Livro de Reclamações →</a>
            </div>
          </div>
        </div>

        {/* Identity block */}
        <div style={{ marginTop: 48, background: '#0f0f0f', border: '1px solid #2a2a2a', padding: '32px 36px' }}>
          <h3 style={{ marginBottom: 20 }}>Identificação da Empresa</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 20, color: '#e5e5e5', fontSize: 15 }}>
            <div><div className="pretitle">Denominação social</div><div style={{ marginTop: 4 }}>{cfg?.company_name || 'Tectur'}</div></div>
            {nif && <div><div className="pretitle">NIF</div><div style={{ marginTop: 4 }}>{nif}</div></div>}
            <div><div className="pretitle">RNAAT</div><div style={{ marginTop: 4 }}>Nº 143/2022</div></div>
            <div><div className="pretitle">Email</div><div style={{ marginTop: 4 }}><a href="mailto:tectur@tectur.pt">tectur@tectur.pt</a></div></div>
            <div><div className="pretitle">Loja online</div><div style={{ marginTop: 4 }}><a href="https://loja.tectur.pt" target="_blank" rel="noreferrer">loja.tectur.pt</a></div></div>
          </div>
        </div>

        <p className="small muted" style={{ marginTop: 40, textAlign: 'center' }}>Última verificação das certificações: Fevereiro 2026</p>
      </div>
    </section>
  );
}

function ClientesManager() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState('');
  const [type, setType] = useState('all');
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [editing, setEditing] = useState(null); // null | new | client
  const load = useCallback(async () => {
    try {
      const qs = [];
      if (q) qs.push(`q=${encodeURIComponent(q)}`);
      if (type !== 'all') qs.push(`type=${type}`);
      const r = await apiGet(`/admin/clients${qs.length ? '?' + qs.join('&') : ''}`);
      setItems(r.items || []);
    } catch (e) { setErr(e.message); }
  }, [q, type]);
  useEffect(() => { load(); }, [load]);

  const del = async (id) => { if (!window.confirm('Eliminar cliente?')) return; try { await apiDelete(`/admin/clients/${id}`); load(); } catch (e) { setErr(e.message); } };
  const reset = async (id) => {
    try {
      const r = await apiPost(`/admin/clients/${id}/reset-password`, {});
      setMsg(`Nova password: ${r.new_password} — enviada ao cliente pelo botão ✉️`); load();
    } catch (e) { setErr(e.message); }
  };
  const mailCreds = async (id) => {
    try {
      const t = await apiGet(`/admin/clients/${id}/mail-credentials`);
      const url = `https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(t.to)}&su=${encodeURIComponent(t.subject)}&body=${encodeURIComponent(t.body)}`;
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (e) { setErr(e.message); }
  };

  if (editing) return <ClienteEditor client={editing} onCancel={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />;

  return (
    <div data-testid="clientes-manager">
      <div className="form-nav" style={{ marginBottom: 12, gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <input placeholder="Procurar por nome, email, NIF…" value={q} onChange={e => setQ(e.target.value)} data-testid="cli-search" style={{ minWidth: 220 }} />
        <select value={type} onChange={e => setType(e.target.value)} data-testid="cli-type" style={{ padding: '10px 12px', background: '#0a0a0a', color: '#fff', border: '1px solid var(--border)' }}>
          <option value="all">Todos os tipos</option>
          <option value="particular">Particular</option>
          <option value="empresa">Empresa</option>
          <option value="municipio">Município</option>
          <option value="imprensa">Imprensa</option>
        </select>
        <span style={{ flex: 1 }} />
        <button className="btn" onClick={() => setEditing({})} data-testid="cli-new">+ Novo cliente</button>
      </div>
      {msg && <p className="small muted">{msg}</p>}
      {err && <p className="small" style={{ color: '#ff6b1a' }}>{err}</p>}
      <div className="table-wrap">
        <table className="admin-table">
          <thead><tr><th>Nome</th><th>Email</th><th>Tipo</th><th>Passeios</th><th>Criado</th><th>Password temp</th><th></th></tr></thead>
          <tbody>
            {items.map(c => (
              <tr key={c.id}>
                <td><strong>{c.name || '—'}</strong>{c.phone && <><br/><span className="small muted">{c.phone}</span></>}</td>
                <td>{c.email}</td>
                <td><span className="status-pill">{c.type}</span></td>
                <td>{(c.inscription_ids || []).length}</td>
                <td>{new Date(c.created_at).toLocaleDateString('pt-PT')}</td>
                <td>{c.temp_password ? <code style={{ fontSize: 12, color: '#ff6b1a' }}>{c.temp_password}</code> : <span className="small muted">—</span>}</td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  <button className="btn btn-outline" onClick={() => setEditing(c)} data-testid={`cli-edit-${c.id}`}>Editar</button>{' '}
                  <button className="btn btn-outline" onClick={() => mailCreds(c.id)} data-testid={`cli-mail-${c.id}`}>✉️</button>{' '}
                  <button className="btn btn-outline" onClick={() => reset(c.id)} data-testid={`cli-reset-${c.id}`}>🔑</button>{' '}
                  <button className="btn btn-outline" style={{ color: '#ff6b1a' }} onClick={() => del(c.id)} data-testid={`cli-del-${c.id}`}>×</button>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan="7" className="muted">Sem clientes. São criados automaticamente quando um pagamento é confirmado.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ClienteEditor({ client, onCancel, onSaved }) {
  const isNew = !client.id;
  const [f, setF] = useState({ email: '', name: '', phone: '', nif: '', cc: '', type: 'particular', tags: [], notes: '', ...client });
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true); setErr('');
    try {
      const payload = { email: f.email, name: f.name, phone: f.phone, nif: f.nif, cc: f.cc, type: f.type, tags: f.tags || [], notes: f.notes };
      if (isNew) await apiPost('/admin/clients', payload);
      else await apiPut(`/admin/clients/${client.id}`, payload);
      onSaved();
    } catch (e) { setErr(e.message); } finally { setSaving(false); }
  };
  return (
    <div className="form-panel" data-testid="cliente-editor">
      <div className="form-nav" style={{ marginBottom: 12 }}>
        <button className="btn btn-outline" onClick={onCancel}>← Voltar</button>
        <button className="btn" onClick={save} disabled={saving} data-testid="cli-save">{saving ? 'A guardar…' : (isNew ? 'Criar cliente' : 'Guardar')}</button>
      </div>
      {err && <p className="small" style={{ color: '#ff6b1a' }}>{err}</p>}
      <div className="grid grid-2">
        <div className="form-row"><label>Email *</label><input required type="email" data-testid="cli-email" value={f.email} onChange={e => setF({ ...f, email: e.target.value })} disabled={!isNew} /></div>
        <div className="form-row"><label>Tipo</label><select data-testid="cli-typef" value={f.type} onChange={e => setF({ ...f, type: e.target.value })}>
          <option value="particular">Particular</option><option value="empresa">Empresa</option><option value="municipio">Município</option><option value="imprensa">Imprensa</option>
        </select></div>
        <div className="form-row"><label>Nome</label><input value={f.name || ''} onChange={e => setF({ ...f, name: e.target.value })} /></div>
        <div className="form-row"><label>Telefone</label><input value={f.phone || ''} onChange={e => setF({ ...f, phone: e.target.value })} /></div>
        <div className="form-row"><label>NIF</label><input value={f.nif || ''} onChange={e => setF({ ...f, nif: e.target.value })} /></div>
        <div className="form-row"><label>CC</label><input value={f.cc || ''} onChange={e => setF({ ...f, cc: e.target.value })} /></div>
      </div>
      <ListEditor label="Etiquetas (ex: VIP, Coimbra, Frota Nissan)" value={f.tags} onChange={v => setF({ ...f, tags: v })} testid="cli-tags" />
      <div className="form-row"><label>Notas internas</label><textarea rows="4" value={f.notes || ''} onChange={e => setF({ ...f, notes: e.target.value })} /></div>
    </div>
  );
}

// ==============================================================
// App Users Manager — utilizadores vindos de app.tectur.pt (source='app')
// ==============================================================
function AppUsersManager() {
  const [items, setItems] = useState([]);
  const [err, setErr] = useState('');
  const [q, setQ] = useState('');
  const load = async () => {
    try {
      const url = q ? `/admin/clients?source=app&q=${encodeURIComponent(q)}` : '/admin/clients?source=app';
      const r = await apiGet(url); setItems(r.items || []);
    } catch (e) { setErr(e.message); }
  };
  useEffect(() => { load(); }, []);
  const statusBadge = (u) => {
    const s = u.app_status || 'unknown';
    const color = { trial: '#f0ad4e', active: '#4caf50', expired: '#999', cancelled: '#ff6b1a' }[s] || '#666';
    return <span style={{ padding: '2px 8px', borderRadius: 3, background: color + '22', color, fontSize: 11, fontWeight: 600, textTransform: 'uppercase' }}>{s}</span>;
  };
  const daysLeft = (iso) => {
    if (!iso) return '—';
    const d = Math.ceil((new Date(iso) - new Date()) / 86400000);
    return d > 0 ? `${d} dias` : (d === 0 ? 'hoje' : `expirado há ${-d}d`);
  };
  return (
    <div data-testid="app-users-manager">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, gap: 8, flexWrap: 'wrap' }}>
        <h3>Utilizadores da App · {items.length}</h3>
        <div style={{ display: 'flex', gap: 8 }}>
          <input value={q} onChange={e => setQ(e.target.value)} onKeyDown={e => e.key === 'Enter' && load()} placeholder="Procurar por email/nome" data-testid="app-users-search" style={{ minWidth: 220 }} />
          <button className="btn btn-outline" onClick={load} data-testid="app-users-refresh">↻ Actualizar</button>
        </div>
      </div>
      <p className="small muted" style={{ marginBottom: 16 }}>
        Utilizadores registados na app.tectur.pt (navegação GPS + rotas municípios). A subscrição (trial 10 dias / activa / expirada / cancelada) é gerida pela app.tectur.pt — este ecrã é só para monitorização.
      </p>
      {err && <p className="small" style={{ color: '#ff6b1a' }}>{err}</p>}
      <table className="admin-table" data-testid="app-users-table">
        <thead>
          <tr><th>Email</th><th>Nome</th><th>Estado</th><th>Trial acaba</th><th>Pago até</th><th>Origem</th><th>Registo</th></tr>
        </thead>
        <tbody>
          {items.length === 0 && (
            <tr><td colSpan="7" className="muted">Sem utilizadores da app ainda. Assim que a app.tectur.pt começar a criar registos, aparecem aqui.</td></tr>
          )}
          {items.map(u => (
            <tr key={u.id} data-testid={`app-user-${u.id}`}>
              <td><code style={{ fontSize: 12 }}>{u.email}</code></td>
              <td>{u.name || '—'}</td>
              <td>{statusBadge(u)}</td>
              <td className="small">{daysLeft(u.app_trial_ends_at)}</td>
              <td className="small">{u.app_paid_until ? (u.app_paid_until || '').slice(0, 10) : '—'}</td>
              <td className="small muted">{u.source === 'both' ? '📱+🚗 App+Tectur' : '📱 App'}</td>
              <td className="small muted">{(u.created_at || '').slice(0, 10)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ==============================================================
// Staff Manager — utilizadores backoffice com role='staff'
// ==============================================================
function StaffManager() {
  const [items, setItems] = useState([]);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    try { const r = await apiGet('/admin/staff'); setItems(r.items || []); }
    catch (e) { setErr(e.message); }
  };
  useEffect(() => { load(); }, []);

  const del = async (id) => {
    if (!window.confirm('Eliminar este utilizador staff? Perde acesso imediato ao backoffice.')) return;
    try { await apiDelete(`/admin/staff/${id}`); load(); }
    catch (e) { setErr(e.message); }
  };
  const resetPw = async (id) => {
    if (!window.confirm('Gerar nova password temporária? A anterior deixa de funcionar imediatamente.')) return;
    try {
      const r = await apiPost(`/admin/staff/${id}/reset-password`, {});
      setMsg(`Nova password: ${r.new_password} (copia agora — não volta a aparecer)`);
      load();
    } catch (e) { setErr(e.message); }
  };

  return (
    <div data-testid="staff-manager">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h3>Utilizadores Staff · Backoffice + app.tectur.pt</h3>
        <button className="btn" onClick={() => { setEditing(null); setShowForm(true); }} data-testid="staff-new">+ Novo Staff</button>
      </div>
      <p className="small muted" style={{ marginBottom: 16 }}>
        Estes utilizadores podem entrar no backoffice deste site E na app.tectur.pt com as mesmas credenciais.
        Diferente do admin, o staff pode ter permissões limitadas — a definir por endpoint.
      </p>
      {err && <p className="small" style={{ color: '#ff6b1a' }} data-testid="staff-err">{err}</p>}
      {msg && <p className="small muted" data-testid="staff-msg">{msg}</p>}

      {showForm && (
        <StaffForm
          initial={editing}
          onClose={() => { setShowForm(false); setEditing(null); }}
          onSaved={(m) => { setMsg(m); setShowForm(false); setEditing(null); load(); }}
          onErr={setErr}
        />
      )}

      <table className="admin-table" data-testid="staff-table">
        <thead>
          <tr><th>Nome</th><th>Email</th><th>Telefone</th><th>Password Temp</th><th>Estado</th><th>Criado</th><th></th></tr>
        </thead>
        <tbody>
          {items.length === 0 && (
            <tr><td colSpan="7" className="muted">Sem staff. Cria o primeiro com o botão acima.</td></tr>
          )}
          {items.map(u => (
            <tr key={u.id} data-testid={`staff-row-${u.id}`}>
              <td>{u.name || '—'}</td>
              <td>{u.email}</td>
              <td>{u.phone || '—'}</td>
              <td><code style={{ color: u.temp_password ? '#ff6b1a' : '#666' }}>{u.temp_password || 'já alterou'}</code></td>
              <td>{u.active ? '✓ Activo' : '✗ Inactivo'}</td>
              <td className="small muted">{(u.created_at || '').slice(0, 10)}</td>
              <td style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                <button className="btn btn-outline" style={{ padding: '4px 10px', fontSize: 12 }} onClick={() => { setEditing(u); setShowForm(true); }} data-testid={`staff-edit-${u.id}`}>✏️ Editar</button>
                <button className="btn btn-outline" style={{ padding: '4px 10px', fontSize: 12 }} onClick={() => resetPw(u.id)} data-testid={`staff-reset-${u.id}`}>🔑 Reset</button>
                <button className="btn btn-outline" style={{ padding: '4px 10px', fontSize: 12, borderColor: '#ff6b1a', color: '#ff6b1a' }} onClick={() => del(u.id)} data-testid={`staff-del-${u.id}`}>🗑️</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function StaffForm({ initial, onClose, onSaved, onErr }) {
  const isNew = !initial;
  const [f, setF] = useState({
    name: initial?.name || '',
    email: initial?.email || '',
    phone: initial?.phone || '',
    notes: initial?.notes || '',
    active: initial?.active !== false,
  });
  const save = async () => {
    if (!f.name || !f.email) { onErr('Nome e email são obrigatórios.'); return; }
    try {
      if (isNew) {
        const r = await apiPost('/admin/staff', f);
        onSaved(`Criado ${r.name}. Password temporária: ${r.temp_password} (copia agora — só aparece 1×)`);
      } else {
        await apiPut(`/admin/staff/${initial.id}`, f);
        onSaved(`${f.name} actualizado.`);
      }
    } catch (e) { onErr(e.message); }
  };
  return (
    <div className="modal-overlay" onClick={onClose} data-testid="staff-form-overlay">
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 560 }}>
        <h3>{isNew ? 'Novo utilizador Staff' : 'Editar Staff'}</h3>
        <div className="form-row"><label>Nome completo *</label><input value={f.name} onChange={e => setF({ ...f, name: e.target.value })} data-testid="staff-form-name" /></div>
        <div className="form-row"><label>Email *</label><input type="email" value={f.email} onChange={e => setF({ ...f, email: e.target.value })} data-testid="staff-form-email" /></div>
        <div className="form-row"><label>Telefone</label><input value={f.phone} onChange={e => setF({ ...f, phone: e.target.value })} data-testid="staff-form-phone" /></div>
        <div className="form-row"><label>Notas internas</label><textarea rows="3" value={f.notes} onChange={e => setF({ ...f, notes: e.target.value })} data-testid="staff-form-notes" /></div>
        <div className="form-row"><label><input type="checkbox" checked={f.active} onChange={e => setF({ ...f, active: e.target.checked })} data-testid="staff-form-active" /> Utilizador activo (pode entrar)</label></div>
        <div className="form-nav" style={{ gap: 8 }}>
          <button className="btn" onClick={save} data-testid="staff-form-save">Guardar</button>
          <button className="btn btn-outline" onClick={onClose} data-testid="staff-form-cancel">Cancelar</button>
        </div>
      </div>
    </div>
  );
}

function GruposManager({ type }) {
  const isTB = type === 'teambuilding';
  const label = isTB ? 'Empresa' : 'Município';
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [detail, setDetail] = useState(null); // grupo id sendo visualizado
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const load = useCallback(async () => {
    try { const r = await apiGet(`/admin/groups?type=${type}`); setItems(r.items || []); }
    catch (e) { setErr(e.message); }
  }, [type]);
  useEffect(() => { load(); }, [load]);

  const del = async (id) => { if (!window.confirm(`Eliminar ${label}? (as rotas associadas voltam a rascunho)`)) return; try { await apiDelete(`/admin/groups/${id}`); load(); } catch (e) { setErr(e.message); } };
  const reset = async (id) => {
    try { const r = await apiPost(`/admin/groups/${id}/reset-password`, {}); setMsg(`Nova password: ${r.new_password}`); load(); }
    catch (e) { setErr(e.message); }
  };
  const mailInvite = async (id) => {
    try {
      const t = await apiGet(`/admin/groups/${id}/mail-invite`);
      const url = `https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(t.to)}&su=${encodeURIComponent(t.subject)}&body=${encodeURIComponent(t.body)}`;
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (e) { setErr(e.message); }
  };

  if (editing) return <GrupoEditor group={editing} type={type} onCancel={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />;
  if (detail) return <GrupoDetail groupId={detail} type={type} onBack={() => setDetail(null)} />;

  return (
    <div data-testid={`grupos-${type}`}>
      <div className="form-nav" style={{ marginBottom: 12 }}>
        <button className="btn" onClick={() => setEditing({})} data-testid={`grp-new-${type}`}>+ Nova {label}</button>
      </div>
      {msg && <p className="small muted">{msg}</p>}
      {err && <p className="small" style={{ color: '#ff6b1a' }}>{err}</p>}
      <div className="table-wrap">
        <table className="admin-table">
          <thead><tr><th>{label}</th><th>Contacto</th><th>Email</th><th>Password temp</th><th>Criado</th><th></th></tr></thead>
          <tbody>
            {items.map(g => (
              <tr key={g.id}>
                <td><a onClick={() => setDetail(g.id)} style={{ cursor: 'pointer', color: '#ff6b1a', textDecoration: 'underline' }} data-testid={`grp-open-${g.id}`}><strong>{g.name}</strong></a>{g.address && <><br/><span className="small muted">{g.address}</span></>}</td>
                <td>{g.contact_name || '—'}<br/><span className="small muted">{g.contact_phone || ''}</span></td>
                <td>{g.contact_email}</td>
                <td>{g.temp_password ? <code style={{ fontSize: 12, color: '#ff6b1a' }}>{g.temp_password}</code> : <span className="small muted">—</span>}</td>
                <td>{new Date(g.created_at).toLocaleDateString('pt-PT')}</td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  <button className="btn btn-outline" onClick={() => setDetail(g.id)} data-testid={`grp-detail-${g.id}`}>Ver grupo</button>{' '}
                  <button className="btn btn-outline" onClick={() => setEditing(g)} data-testid={`grp-edit-${g.id}`}>Editar</button>{' '}
                  <button className="btn btn-outline" onClick={() => mailInvite(g.id)} data-testid={`grp-mail-${g.id}`}>✉️</button>{' '}
                  <button className="btn btn-outline" onClick={() => reset(g.id)} data-testid={`grp-reset-${g.id}`}>🔑</button>{' '}
                  <button className="btn btn-outline" style={{ color: '#ff6b1a' }} onClick={() => del(g.id)} data-testid={`grp-del-${g.id}`}>×</button>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan="6" className="muted">Sem {label.toLowerCase()}s. Cria a primeira com o botão acima. Ao criar, será gerada automaticamente uma password temporária e ficará pronta para receber o link de convite.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function GrupoDetail({ groupId, type, onBack }) {
  const isTB = type === 'teambuilding';
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const load = useCallback(async () => {
    try { setData(await apiGet(`/admin/groups/${groupId}`)); } catch (e) { setErr(e.message); }
  }, [groupId]);
  useEffect(() => { load(); }, [load]);

  const mailEveryone = () => {
    if (!data || !data.inscriptions?.length) { alert('Sem participantes inscritos ainda.'); return; }
    const paid = data.inscriptions.filter(i => i.status === 'paid');
    const emails = [...new Set((paid.length ? paid : data.inscriptions).map(i => i.driver_email))].join(',');
    const subject = `Info · ${data.group.name}`;
    const body = `Olá a todos,\n\nAtualização sobre o evento Tectur do vosso grupo ${data.group.name}:\n\n[escreve aqui a mensagem]\n\n\nEquipa Tectur\nRNAAT nº 143/2022`;
    const url = `https://mail.google.com/mail/?view=cm&fs=1&bcc=${encodeURIComponent(emails)}&su=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  if (!data) return <p className="muted">A carregar…</p>;
  const g = data.group;
  const paidCount = data.inscriptions.filter(i => i.status === 'paid').length;

  return (
    <div data-testid="grupo-detail">
      <div className="form-nav" style={{ marginBottom: 16 }}>
        <button className="btn btn-outline" onClick={onBack} data-testid="grp-back">← Voltar</button>
      </div>
      {err && <p className="small" style={{ color: '#ff6b1a' }}>{err}</p>}
      {msg && <p className="small muted">{msg}</p>}

      <div className="form-panel">
        <h3 style={{ marginTop: 0 }}>🏢 {g.name}</h3>
        <div className="grid grid-2">
          <div><p className="small muted">Contacto</p><p><strong>{g.contact_name || '—'}</strong></p><p>{g.contact_email}</p><p>{g.contact_phone || '—'}</p></div>
          <div><p className="small muted">Acesso à área privada</p><p><strong>Email:</strong> {g.contact_email}</p>{g.temp_password && <p><strong>Password temporária:</strong> <code style={{ color: '#ff6b1a' }}>{g.temp_password}</code></p>}<p className="small muted">{g.last_login_at ? `Último login: ${new Date(g.last_login_at).toLocaleString('pt-PT')}` : 'Ainda não fez login.'}</p></div>
        </div>
        {g.address && <p className="small muted">{g.address}</p>}
        {g.notes && <p className="small">📝 {g.notes}</p>}
      </div>

      <div className="form-panel" style={{ marginTop: 16 }}>
        <h3 style={{ marginTop: 0 }}>🚗 Rotas associadas ({data.trips.length})</h3>
        {data.trips.length === 0 ? (
          <p className="muted small">Ainda não há rotas associadas a este grupo. Vai a <strong>Passeios → +Novo passeio</strong>, escolhe visibilidade <strong>&quot;{isTB ? 'Team Building' : 'Município'}&quot;</strong> e seleciona <strong>{g.name}</strong> no dropdown de grupo.</p>
        ) : (
          <ul className="feat-list">
            {data.trips.map(t => (
              <li key={t.id}>
                <strong>{t.title}</strong>{t.date_label ? ` · ${t.date_label}` : ''}
                <span className="small muted"> · {t.type === 'navigation' ? 'Navigation Challenge' : 'Guiado'} · {t.vagas_disponiveis}/{t.vagas_total} vagas</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="form-panel" style={{ marginTop: 16 }}>
        <div className="form-nav" style={{ marginBottom: 12 }}>
          <h3 style={{ margin: 0 }}>👥 Grupo · Participantes inscritos ({data.inscriptions.length})</h3>
          <span style={{ flex: 1 }} />
          {data.inscriptions.length > 0 && (
            <button className="btn" onClick={mailEveryone} data-testid="grp-mail-everyone">✉️ Email a todos ({paidCount} pagos)</button>
          )}
        </div>
        {data.inscriptions.length === 0 ? (
          <p className="muted small">Sem inscrições ainda. Envia o link de convite ao contacto (botão ✉️) para começarem a inscrever-se.</p>
        ) : (
          <div className="table-wrap">
            <table className="admin-table">
              <thead><tr><th>Ref.</th><th>Nome</th><th>Email</th><th>Total</th><th>Estado</th></tr></thead>
              <tbody>
                {data.inscriptions.map(i => (
                  <tr key={i.id}>
                    <td><strong>{i.reference}</strong></td>
                    <td>{i.driver_name}{i.team_name ? ` · ${i.team_name}` : ''}</td>
                    <td>{i.driver_email}</td>
                    <td>{Number(i.total || 0).toFixed(0)} €</td>
                    <td><span className={`status-pill ${i.status}`}>{i.status === 'paid' ? 'Pago' : i.status === 'pending_payment' ? 'Pendente' : i.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function GrupoEditor({ group, type, onCancel, onSaved }) {
  const isNew = !group.id;
  const label = type === 'teambuilding' ? 'empresa' : 'município';
  const [f, setF] = useState({ name: '', contact_name: '', contact_email: '', contact_phone: '', address: '', notes: '', tags: [], ...group, type });
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true); setErr('');
    try {
      const payload = { type, name: f.name, contact_name: f.contact_name, contact_email: f.contact_email, contact_phone: f.contact_phone, address: f.address, notes: f.notes, tags: f.tags || [] };
      if (isNew) await apiPost('/admin/groups', payload);
      else await apiPut(`/admin/groups/${group.id}`, payload);
      onSaved();
    } catch (e) { setErr(e.message); } finally { setSaving(false); }
  };
  return (
    <div className="form-panel" data-testid="grupo-editor">
      <div className="form-nav" style={{ marginBottom: 12 }}>
        <button className="btn btn-outline" onClick={onCancel}>← Voltar</button>
        <button className="btn" onClick={save} disabled={saving} data-testid="grp-save">{saving ? 'A guardar…' : (isNew ? `Criar ${label}` : 'Guardar')}</button>
      </div>
      {err && <p className="small" style={{ color: '#ff6b1a' }}>{err}</p>}
      <div className="grid grid-2">
        <div className="form-row"><label>Nome da {label} *</label><input required data-testid="grp-name" value={f.name} onChange={e => setF({ ...f, name: e.target.value })} /></div>
        <div className="form-row"><label>Email de contacto *</label><input required type="email" data-testid="grp-email" value={f.contact_email} onChange={e => setF({ ...f, contact_email: e.target.value })} /></div>
        <div className="form-row"><label>Nome do responsável</label><input value={f.contact_name || ''} onChange={e => setF({ ...f, contact_name: e.target.value })} /></div>
        <div className="form-row"><label>Telefone</label><input value={f.contact_phone || ''} onChange={e => setF({ ...f, contact_phone: e.target.value })} /></div>
        <div className="form-row" style={{ gridColumn: '1/-1' }}><label>Morada</label><input value={f.address || ''} onChange={e => setF({ ...f, address: e.target.value })} /></div>
      </div>
      <ListEditor label="Etiquetas" value={f.tags} onChange={v => setF({ ...f, tags: v })} testid="grp-tags" />
      <div className="form-row"><label>Notas internas</label><textarea rows="4" value={f.notes || ''} onChange={e => setF({ ...f, notes: e.target.value })} /></div>
      {!isNew && (
        <p className="small muted">💡 Depois de guardar, cria uma rota no separador Passeios com visibilidade &quot;{type === 'teambuilding' ? 'Team Building' : 'Município'}&quot; e associa a este grupo.</p>
      )}
    </div>
  );
}

function GestaoDashboard({ user, onLogout }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [tab, setTab] = useState('overview');
  useEffect(() => {
    apiGet('/admin/dashboard').then(setData).catch(err => setError(err.message));
  }, []);
  const s = data?.stats || {};
  return (
    <section className="section">
      <div className="container">
        <div className="section-header" style={{ position: 'relative' }}>
          <h2>Backoffice Tectur</h2>
          <div className="underline"></div>
          <p className="muted">Olá {user?.name || user?.email}. Sessão iniciada.</p>
          <a
            href="https://app.tectur.pt"
            target="_blank"
            rel="noreferrer"
            className="btn"
            data-testid="app-backoffice-link"
            style={{ position: 'absolute', top: 0, right: 0, fontSize: 13 }}
          >📱 App Backoffice</a>
        </div>

        <div className="admin-tabs" data-testid="admin-tabs">
          <button className={`admin-tab ${tab === 'overview' ? 'active' : ''}`} onClick={() => setTab('overview')} data-testid="tab-overview">Resumo</button>
          <button className={`admin-tab ${tab === 'passeios' ? 'active' : ''}`} onClick={() => setTab('passeios')} data-testid="tab-passeios">Passeios</button>
          <button className={`admin-tab ${tab === 'inscricoes' ? 'active' : ''}`} onClick={() => setTab('inscricoes')} data-testid="tab-inscricoes">Inscrições</button>
          <button className={`admin-tab ${tab === 'clientes' ? 'active' : ''}`} onClick={() => setTab('clientes')} data-testid="tab-clientes">Clientes</button>
          <button className={`admin-tab ${tab === 'empresas' ? 'active' : ''}`} onClick={() => setTab('empresas')} data-testid="tab-empresas">Empresas TB</button>
          <button className={`admin-tab ${tab === 'municipios' ? 'active' : ''}`} onClick={() => setTab('municipios')} data-testid="tab-municipios">Municípios</button>
          <button className={`admin-tab ${tab === 'staff' ? 'active' : ''}`} onClick={() => setTab('staff')} data-testid="tab-staff">Staff</button>
          <button className={`admin-tab ${tab === 'users-app' ? 'active' : ''}`} onClick={() => setTab('users-app')} data-testid="tab-users-app">Users App</button>
          <button className={`admin-tab ${tab === 'testemunhos' ? 'active' : ''}`} onClick={() => setTab('testemunhos')} data-testid="tab-testemunhos">Testemunhos</button>
          <button className={`admin-tab ${tab === 'leads' ? 'active' : ''}`} onClick={() => setTab('leads')} data-testid="tab-leads">Leads</button>
          <button className={`admin-tab ${tab === 'newsletter' ? 'active' : ''}`} onClick={() => setTab('newsletter')} data-testid="tab-newsletter">Newsletter</button>
          <button className={`admin-tab ${tab === 'videos' ? 'active' : ''}`} onClick={() => setTab('videos')} data-testid="tab-videos">Vídeos</button>
          <button className={`admin-tab ${tab === 'definicoes' ? 'active' : ''}`} onClick={() => setTab('definicoes')} data-testid="tab-definicoes">Definições</button>
        </div>

        {error && <p className="small" style={{ color: '#ff6b1a' }} data-testid="dashboard-error">{error}</p>}

        {tab === 'overview' && data && (
          <div data-testid="dashboard-overview">
            <div className="grid grid-4" data-testid="dashboard-stats" style={{ marginBottom: 24 }}>
              <div className="card"><div className="card-body"><h3>Inscrições</h3><p className="price">{s.total_inscriptions ?? '—'}</p></div></div>
              <div className="card"><div className="card-body"><h3>Pagas</h3><p className="price">{s.paid_inscriptions ?? '—'}</p></div></div>
              <div className="card"><div className="card-body"><h3>Pendentes</h3><p className="price">{s.pending_inscriptions ?? '—'}</p></div></div>
              <div className="card"><div className="card-body"><h3>Receita paga</h3><p className="price">{(s.total_revenue || 0).toFixed(0)} €</p></div></div>
            </div>
            <div className="grid grid-2">
              <div className="card"><div className="card-body">
                <h3>Próximos passeios ({data.upcoming_trips?.length || 0})</h3>
                <ul className="feat-list">
                  {(data.upcoming_trips || []).map(t => (
                    <li key={t.id}><strong>{t.title}</strong> <span className="muted small">· {t.date_label || t.date_iso}</span></li>
                  ))}
                  {(!data.upcoming_trips || data.upcoming_trips.length === 0) && <li className="muted">Sem passeios agendados.</li>}
                </ul>
              </div></div>
              <div className="card"><div className="card-body">
                <h3>Últimas inscrições ({data.recent_inscriptions?.length || 0})</h3>
                <ul className="feat-list">
                  {(data.recent_inscriptions || []).map(i => (
                    <li key={i.id}><strong>{i.reference}</strong> · {i.driver_name} <span className="muted small">· {i.status}</span></li>
                  ))}
                  {(!data.recent_inscriptions || data.recent_inscriptions.length === 0) && <li className="muted">Sem inscrições ainda.</li>}
                </ul>
              </div></div>
            </div>
            {data.app_stats && (
              <div className="card" data-testid="dashboard-app-stats" style={{ marginTop: 24 }}>
                <div className="card-body">
                  <h3>📱 Users App Tectur <span className="muted small" style={{ fontWeight: 400 }}>· app.tectur.pt</span></h3>
                  <div className="grid grid-5" style={{ marginTop: 12 }}>
                    <div className="card"><div className="card-body">
                      <h4 className="small muted" style={{ margin: 0 }}>Total</h4>
                      <p className="price" data-testid="app-stat-total" style={{ margin: 0 }}>{data.app_stats.total ?? 0}</p>
                    </div></div>
                    <div className="card"><div className="card-body">
                      <h4 className="small muted" style={{ margin: 0 }}>Trial</h4>
                      <p className="price" data-testid="app-stat-trial" style={{ margin: 0, color: '#f39c12' }}>{data.app_stats.trial ?? 0}</p>
                    </div></div>
                    <div className="card"><div className="card-body">
                      <h4 className="small muted" style={{ margin: 0 }}>Ativos</h4>
                      <p className="price" data-testid="app-stat-active" style={{ margin: 0, color: '#27ae60' }}>{data.app_stats.active ?? 0}</p>
                    </div></div>
                    <div className="card"><div className="card-body">
                      <h4 className="small muted" style={{ margin: 0 }}>Expirados</h4>
                      <p className="price" data-testid="app-stat-expired" style={{ margin: 0, color: '#7f8c8d' }}>{data.app_stats.expired ?? 0}</p>
                    </div></div>
                    <div className="card"><div className="card-body">
                      <h4 className="small muted" style={{ margin: 0 }}>Cancelados</h4>
                      <p className="price" data-testid="app-stat-cancelled" style={{ margin: 0, color: '#c0392b' }}>{data.app_stats.cancelled ?? 0}</p>
                    </div></div>
                  </div>
                  <p className="small muted" style={{ marginTop: 12, marginBottom: 0 }}>
                    Utilizadores registados via <code>app.tectur.pt</code>. Para gerir individualmente, abre o separador <strong>Users App</strong>.
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

        {tab === 'passeios' && <PasseiosManager />}
        {tab === 'inscricoes' && <InscricoesManager />}
        {tab === 'clientes' && <ClientesManager />}
        {tab === 'empresas' && <GruposManager type="teambuilding" />}
        {tab === 'municipios' && <GruposManager type="municipio" />}
        {tab === 'staff' && <StaffManager />}
        {tab === 'users-app' && <AppUsersManager />}
        {tab === 'testemunhos' && <TestemunhosManager />}
        {tab === 'leads' && <LeadsManager />}
        {tab === 'newsletter' && <NewsletterManager />}
        {tab === 'videos' && <VideosManager />}
        {tab === 'definicoes' && <DefinicoesManager />}

        <div style={{ marginTop: 24 }}>
          <button className="btn btn-outline" data-testid="logout-btn" onClick={onLogout}>Terminar sessão</button>
        </div>
      </div>
    </section>
  );
}

function Contactos() {
  const [form, setForm] = useState({ name: '', email: '', message: '' });
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  const submit = async (e) => {
    e.preventDefault();
    setStatus(''); setError('');
    try {
      await apiPost('/contact', form);
      setStatus('Mensagem enviada. Obrigado!');
      setForm({ name: '', email: '', message: '' });
    } catch (err) {
      setError(err.message || 'Erro ao enviar mensagem.');
    }
  };
  return (
    <section className="section">
      <div className="container">
        <div className="section-header">
          <h2>Contactos</h2>
          <div className="underline"></div>
        </div>
        <div className="grid grid-2">
          <div>
            <h3>Tectur</h3>
            <p className="muted">Empresa portuguesa de turismo off-road, apps de navegação e equipamento para expedições.</p>
            <p><strong>Email:</strong> tectur@tectur.pt</p>
            <p><strong>Loja:</strong> <a href="https://loja.tectur.pt">loja.tectur.pt</a></p>
            <p className="small muted">RNAAT nº 143/2022</p>
          </div>
          <form className="form-panel" onSubmit={submit} data-testid="contact-form">
            <div className="form-row"><label>Nome</label><input required data-testid="contact-name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} /></div>
            <div className="form-row"><label>Email</label><input type="email" required data-testid="contact-email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} /></div>
            <div className="form-row"><label>Mensagem</label><textarea rows="5" required data-testid="contact-message" value={form.message} onChange={e => setForm({ ...form, message: e.target.value })}></textarea></div>
            <button className="btn" type="submit" data-testid="contact-submit">Enviar</button>
            {status && <p className="small muted" style={{ marginTop: 12 }} data-testid="contact-status">{status}</p>}
            {error && <p className="small" style={{ color: '#ff6b1a', marginTop: 12 }} data-testid="contact-error">{error}</p>}
          </form>
        </div>
      </div>
    </section>
  );
}

export default function App() {
  const [page, setPage] = useState('home');
  const [passeioSel, setPasseioSel] = useState(null);
  const [passeios, setPasseios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [portalSession, setPortalSession] = useState(null);
  const [videos, setVideos] = useState(YT_VIDEOS_FALLBACK);
  const [realizados, setRealizados] = useState([]);

  // ---- URL ⇄ state routing (SPA path-based) ----
  const pageToPath = useCallback((p, data) => {
    switch (p) {
      case 'home': return '/';
      case 'passeios': return '/passeios';
      case 'inscricao': return `/passeios/${data?.slug || data?.id || ''}`.replace(/\/+$/, '') || '/passeios';
      case 'passeios-realizados': return '/passeios-realizados';
      case 'passeio-realizado': return `/passeios-realizados/${data?.slug || data?.id || ''}`.replace(/\/+$/, '') || '/passeios-realizados';
      case 'apps': return '/apps';
      case 'equipamento': return '/equipamento';
      case 'teambuilding': return '/team-building';
      case 'municipios': return '/municipios';
      case 'contactos': return '/contactos';
      case 'privacidade': return '/privacidade';
      case 'cookies': return '/cookies';
      case 'termos': return '/termos';
      case 'sobre': return '/sobre';
      case 'entrar': return '/entrar';
      case 'minha-area': return '/minha-area';
      case 'gestao': return '/gestao';
      case 'login': return '/gestao';
      case 'reset-password': return '/reset-password';
      default: return '/';
    }
  }, []);

  const pathToState = useCallback((pathname) => {
    // Retorna { page, slug } a partir do pathname
    const p = pathname.replace(/^\/+|\/+$/g, '');
    if (p === '' || p === 'index.html') return { page: 'home', slug: null };
    if (p === 'passeios') return { page: 'passeios', slug: null };
    if (p.startsWith('passeios/')) return { page: 'inscricao', slug: p.substring('passeios/'.length) };
    if (p === 'passeios-realizados') return { page: 'passeios-realizados', slug: null };
    if (p.startsWith('passeios-realizados/')) return { page: 'passeio-realizado', slug: p.substring('passeios-realizados/'.length) };
    if (p === 'team-building' || p === 'teambuilding') return { page: 'teambuilding', slug: null };
    if (p === 'reset-password') return { page: 'reset-password', slug: null };
    // rotas 1-para-1
    const direct = ['apps','equipamento','municipios','contactos','privacidade','cookies','termos','sobre','entrar','minha-area','gestao'];
    if (direct.includes(p)) return { page: p, slug: null };
    // fallback → home
    return { page: 'home', slug: null };
  }, []);

  const nav = useCallback((p, data) => {
    setPage(p);
    if (data) setPasseioSel(data);
    const path = pageToPath(p, data);
    if (typeof window !== 'undefined' && window.location.pathname !== path) {
      window.history.pushState({ page: p, slug: data?.slug || data?.id || null }, '', path);
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [pageToPath]);

  // Initial state from URL + popstate handler
  useEffect(() => {
    const applyPath = async () => {
      const st = pathToState(window.location.pathname);
      setPage(st.page);
      if (st.slug) {
        if (st.page === 'inscricao') {
          // fetch a trip by id/slug to hydrate passeioSel
          try {
            const t = await apiGet(`/trips/${encodeURIComponent(st.slug)}`);
            setPasseioSel(tripToCard(t));
          } catch {
            setPage('passeios');
          }
        } else if (st.page === 'passeio-realizado') {
          setPasseioSel({ slug: st.slug });
        }
      }
    };
    applyPath();
    const onPop = () => { applyPath(); };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [pathToState]);

  // Load trips from backend once
  useEffect(() => {
    apiGet('/trips/upcoming')
      .then(res => setPasseios((res.items || []).map(tripToCard)))
      .catch(() => setPasseios([]))
      .finally(() => setLoading(false));
  }, []);

  // Load latest YouTube videos from backend (RSS auto-synced)
  useEffect(() => {
    apiGet('/config/homepage-videos')
      .then(res => { if (res.items && res.items.length) setVideos(res.items); })
      .catch(() => {});
  }, []);

  // Load recent Passeios Realizados (homepage widget — 3 latest)
  useEffect(() => {
    apiGet('/passeios-realizados')
      .then(res => setRealizados((res?.items || []).slice(0, 3)))
      .catch(() => setRealizados([]));
  }, []);

  // Check if already logged in (admin)
  useEffect(() => {
    apiGet('/auth/me').then(setUser).catch(() => setUser(null));
  }, []);

  // Check portal session (skip if returning from Google Auth — the callback effect handles it)
  useEffect(() => {
    if (typeof window !== 'undefined' && window.location.hash && window.location.hash.includes('session_id=')) return;
    apiGet('/portal/me').then(d => setPortalSession({ type: d.type, name: d.entity?.name || d.entity?.email, must_change_pw: d.must_change_pw })).catch(() => setPortalSession(null));
  }, []);

  // Google OAuth callback — detect session_id fragment, exchange with backend, then clean URL
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const hash = window.location.hash || '';
    const m = hash.match(/session_id=([^&]+)/);
    if (!m) return;
    const sid = decodeURIComponent(m[1]);
    // Clear hash immediately to prevent replay
    window.history.replaceState({}, '', window.location.pathname);
    (async () => {
      try {
        const r = await apiPost('/portal/google-callback', { session_id: sid });
        setPortalSession(r);
        nav('minha-area');
      } catch (e) {
        alert(e.message || 'Não foi possível entrar com Google.');
        nav('entrar');
      }
    })();
  }, []);

  const handleLogin = useCallback((u) => { setUser(u); nav('gestao'); }, [nav]);
  const handleLogout = useCallback(async () => {
    try { await apiPost('/auth/logout', {}); } catch (e) { /* ignore */ }
    setUser(null);
    nav('home');
  }, [nav]);
  const handlePortalLogin = useCallback((r) => { setPortalSession(r); nav('minha-area'); }, [nav]);
  const handlePortalLogout = useCallback(() => { setPortalSession(null); nav('home'); }, [nav]);

  return (
    <div className="app">
      <Header current={page} onNav={nav} portalSession={portalSession} />
      <main>
        {page === 'home' && <Home onNav={nav} passeios={passeios} loading={loading} videos={videos} realizados={realizados} />}
        {page === 'passeios' && <Passeios onNav={nav} passeios={passeios} loading={loading} />}
        {page === 'passeios-realizados' && <PasseiosRealizados onNav={nav} />}
        {page === 'passeio-realizado' && <PasseioRealizadoDetail slug={passeioSel?.slug || passeioSel?.id} onNav={nav} />}
        {page === 'inscricao' && <Inscricao passeio={passeioSel} />}
        {page === 'apps' && <Apps />}
        {page === 'equipamento' && <Equipamento />}
        {page === 'teambuilding' && <TeamBuilding />}
        {page === 'municipios' && <Municipios />}
        {page === 'contactos' && <Contactos />}
        {page === 'privacidade' && <PoliticaPrivacidade />}
        {page === 'cookies' && <PoliticaCookies />}
        {page === 'termos' && <TermosCondicoes />}
        {page === 'sobre' && <SobreNos />}
        {page === 'reset-password' && <ResetPassword onNav={nav} />}
        {page === 'entrar' && (portalSession ? <PortalArea session={portalSession} onLogout={handlePortalLogout} /> : <PortalLogin onLoggedIn={handlePortalLogin} />)}
        {page === 'minha-area' && (portalSession ? <PortalArea session={portalSession} onLogout={handlePortalLogout} /> : <PortalLogin onLoggedIn={handlePortalLogin} />)}
        {page === 'login' && (user ? <GestaoDashboard user={user} onLogout={handleLogout} /> : <Login onLoggedIn={handleLogin} />)}
        {page === 'gestao' && (user ? <GestaoDashboard user={user} onLogout={handleLogout} /> : <Login onLoggedIn={handleLogin} />)}
      </main>
      <Footer onNav={nav} />
      {page === 'home' && <NewsletterPopup />}
      <CookieBanner onNav={nav} />
    </div>
  );
}
